/* eslint-disable complexity */
/* eslint-disable spaced-comment */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { cloneDeep, isEqual } from 'lodash';
import * as moment from 'moment';
import { BehaviorSubject, forkJoin, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { catchError, map, retry, take } from 'rxjs/operators';
import { GeneralConfirmationComponent } from '../../shared-components/controls/general-confirmation/general-confirmation.component';
import { CheckForErrorsGuard } from '../../shared-components/guards/check-for-errors/check-for-errors.guard';
import { Lookup } from '../../shared-components/lookup/lookup.model';
import { PagedContent } from '../../shared-components/models/pagedContent.model';
import { SecurityService } from '../../shared-components/security/security.service';
import { SharedService } from '../../shared-components/shared.service';
import { System } from '../../shared-components/System';
import { DataQueryPreferences } from '../../shared-components/user-preferences/data-query-preferences.model';
import { UserPreferencesService } from '../../shared-components/user-preferences/user-preferences.service';
import { UserPermission } from '../../shared-components/user/user-permission.model';
import { UserService } from '../../shared-components/user/user.service';
import { YearService } from '../../shared-components/year/year.service';
import { LoginService } from '../../shared-modules/login/login.service';
import { ClassroomSummaryService } from '../classroom-summary/classroom-summary.service';
import { ClassroomSummary } from '../classroom-summary/models/classroom-summary.model';
import { SelectCourseDialogComponent } from '../guards/select-course/select-course-dialog/select-course-dialog.component';
import { AddEditQueryDialogComponent } from './add-edit-query-dialog/add-edit-query.component';
import { ImportErrorDialogComponent } from './add-edit-query-dialog/import-error-dialog/import-error-dialog.component';
import { DataQueryCategory } from './models/data-query-category.model';
import { DataQueryCoreFile } from './models/data-query-core-file.model';
import { DataQueryFieldFilter } from './models/data-query-field-filter.model';
import { DataQueryField } from './models/data-query-field.model';
import { DataQueryFunction } from './models/data-query-function.model';
import { DataQueryInformation } from './models/data-query-information';
import { DataQueryObject } from './models/data-query-object.model';
import { DataQueryOperator } from './models/data-query-operator.model';
import { DataQueryParameter } from './models/data-query-parameter.model';
import { DataQueryPermissions } from './models/data-query-permissions';
import { DataQueryRelationship } from './models/data-query-relationship.model';
import { DataQueryResult } from './models/data-query-result.model';
import { PrintParameters } from './data-query-results/print-results-dialog/print-parameters.model';
import { InsertFieldDialogComponent } from './data-query-settings-for-export/insert-field-dialog/insert-field-dialog.component';
import { DataQueryTransformationField } from './models/data-query-transformation-field.model';
import { DataQueryUnsavedChangesDialogComponent } from './data-query-unsaved-changes-dialog/data-query-unsaved-changes-dialog.component';
import { DataQuery } from './models/data-query.model';
import { ErrorsExistDialogComponent } from './errors-exist-dialog/errors-exist-dialog.component';
import { NoRelationshipsWarningDialogComponent } from './no-relationships-warning-dialog/no-relationships-warning-dialog.component';
import { WarningDialogComponent } from './warning-dialog/warning-dialog.component';
import KeyDownEvent = JQuery.KeyDownEvent;
import ClickEvent = JQuery.ClickEvent;
import { DataQueryQuickStart } from './models/data-query-quick-start.model';
import { DataQueryCoreFileFilter } from './models/data-query-core-file-filter.model';
import { DataQueryCoreFileFilterOption } from './models/data-query-core-file-filter-option.model';

@Injectable()
export class DataQueryService {

    // Static Properties
    static filterFieldId = 0;

    // Local Properties
    private blocking = false;
    distinct = true;
    currentYearOnly = true;
    currentSchoolOnly = true;
    currentlyActiveStudentsOnly = true;
    private loadingQuery = false;
    private savedQueryModified: boolean;
    private FormulasTested: boolean;
    private _studentFilters: DataQuery[];
    private _courseFilters: DataQuery[];
    private _staffFilters: DataQuery[];
    private _savedQueries: DataQuery[];
    private _isStudentSearch = false;
    private _isCourseSearch = false;
    private _isStaffSearch = false;
    V10: boolean;
    studentSearchFilters: any = {};
    courseSearchFilters: any = {};
    importing = false;	// This is used to prevent double validations when on the results tab
    rosterQuery = false;
    obtainedRecentStudentQueries = false;  // This is used for student search because we don't want to initially get all info
    obtainedRecentCourseQueries = false;   // This is used for course search because we don't want to initially get all the info
    obtainedRecentStaffQueries = false;    // This is used for staff search because we don't want to initially get all the info
    nextPageSubscriptions: Subscription[] = [];

    // Result Properties
    queryResultTable = [];
    currentPage = 1;
    sqlErrors: any;
    disabledFieldInReport: boolean;
    resetPageIndex: boolean;
    recordCount: number;
    pageCount: number;
    pageSize: number;
    retrievedData: string[][];
    importQueryClick = false;
    cancelQuery = new Subject<void>();
    currentResultSubscriptions: Subscription[] = [];
    queryLoadingTimeout = null
    queryCanceled = false;
    resultsLoading = false;

    // Cached Server Properties
    private permissions: DataQueryPermissions;
    private legacyQueries: DataQuery[];
    private functions: DataQueryFunction[];
    private operators: DataQueryOperator[];
    private parameters: DataQueryParameter[];
    public userPermissions: UserPermission[];
    public disabledFields: { [fieldId: string]: boolean } = {};
    public editableFields: { [fieldId: string]: boolean } = {};
    public userCategories: number[];
    public studentSearchQuery: DataQuery;
    public courseSearchQuery: DataQuery;
    public cachedInformation = false;
    private cachedPreferences = false;
    private coreFiles: DataQueryCoreFile[] = [];
    private categoriesByCoreFile: { [coreFileId: number]: DataQueryCategory[] } = {};
    private quickStarts: { [coreFileId: number]: DataQueryQuickStart[] } = {};
    private coreFileFilters: { [coreFileId: number]: DataQueryCoreFileFilter[] } = {};

    // Permission Properties
    public enableMassUpdate = false;
    public unsavedChanges = false;
    public isHasEdited = false;

    // Sync Properties
    private _maximized = false;
    private observableMaximized = new BehaviorSubject(this._maximized);
    observableMaximizedStream$ = this.observableMaximized.asObservable();

    private _originalDataQueryQuery = new DataQuery();
    private _currentDataQueryQuery = new DataQuery();
    private _originalStudentSearchQuery = new DataQuery();
    private _currentStudentSearchQuery = new DataQuery();
    private observableCurrentQuery = new BehaviorSubject(this.currentQuery);
    observableCurrentQueryStream$ = this.observableCurrentQuery.asObservable();

    private _information = new DataQueryInformation();
    private observableInformation = new BehaviorSubject(this._information);
    observableInformationStream$ = this.observableInformation.asObservable();

    private _preferences = new DataQueryPreferences();
    private observablePreferences = new BehaviorSubject(this._preferences);
    observablePreferencesStream$ = this.observablePreferences.asObservable();

    private _results: DataQueryResult;
    private observableResults = new Subject<DataQueryResult>();
    observableResultsStream$ = this.observableResults.asObservable();

    private _nextPageResults: PagedContent<DataQueryResult>;
    private observableNextPageResults = new Subject<PagedContent<DataQueryResult>>();
    observableNextPageResultsStream$ = this.observableNextPageResults.asObservable();

    private _currentTab: string;
    private _previousTab: string;
    private observableCurrentTab = new BehaviorSubject<string>('Fields');
    observableCurrentTabStream$ = this.observableCurrentTab.asObservable();

    private observableSaveResult = new Subject<boolean>();
    observableSaveResultStream$ = this.observableSaveResult.asObservable();

    private _selectedCoreFile: DataQueryCoreFile;
    private observableSelectedCoreFile = new BehaviorSubject<DataQueryCoreFile>(null);
    observableSelectedCoreFileStream$ = this.observableSelectedCoreFile.asObservable();

    private _selectedQuickStart: DataQueryQuickStart;
    private observableSelectedQuickStart = new BehaviorSubject<DataQueryQuickStart>(null);
    observableSelectedQuickStartStream$ = this.observableSelectedQuickStart.asObservable();

    selectedCoreFileFilterOptionId: number;

    private _wizardOpen = false;
    public observableWizardOpen = new Subject<boolean>();
    observableWizardOpenStream$ = this.observableWizardOpen.asObservable();

    // Class Functions
    constructor(
        private http: HttpClient,
        private userService: UserService,
        private yearService: YearService,
        private loginService: LoginService,
        private classroomSummaryService: ClassroomSummaryService,
        private router: Router,
        private userPreferencesService: UserPreferencesService,
        private dialog: MatDialog,
        private sharedService: SharedService,
        private securityService: SecurityService,
        private guard: CheckForErrorsGuard
    ) {

        // Whenever we change the year, the cache is invalidated
        this.yearService.observableDefaultYearStream$.subscribe(() => {
            this.cachedInformation = false;
        });

        // Whenever we route away, reset current query
        router.events.subscribe(routerEvent => {
            if (routerEvent instanceof NavigationEnd && !routerEvent.urlAfterRedirects.includes('dataQuery')) {
                this.currentQuery = new DataQuery();
            }
        });

        // If we cancel the current query, we cancel any result call we're holding
        this.cancelQuery.subscribe(() => {
            this.currentResultSubscriptions.forEach(subscription => {
                if (!subscription.closed) {
                    subscription.unsubscribe();
                }
            });
            if (this.queryLoadingTimeout) {
                clearTimeout(this.queryLoadingTimeout);
            }
        });

        // Whenever data query preferences are saved, remove the cache
        this.userPreferencesService.dataQueryPreferenceChange.subscribe(() => {
            this.cachedPreferences = false;
            this.getPreferences().pipe(take(1)).subscribe();
        });

        // Setting our V10 flag
        this.V10 = this.loginService.LoginSettings.V10;
    }

    get isStudentSearch(): boolean {
        return this._isStudentSearch;
    }

    set isStudentSearch(value: boolean) {
        if (this._isStudentSearch != value) {
            this._isStudentSearch = value;
            if (value == true) {
                this._isCourseSearch = false;
                this._isStaffSearch = false;
            }
            this.currentQuery = new DataQuery();
            this.observableCurrentQuery.next(this.currentQuery);
        }
    }

    get isCourseSearch(): boolean {
        return this._isCourseSearch;
    }

    set isCourseSearch(value: boolean) {
        if (this._isCourseSearch != value) {
            this._isCourseSearch = value;
            if (value == true) {
                this._isStudentSearch = false;
                this._isStaffSearch = false;
            }
            this.currentQuery = new DataQuery();
            this.observableCurrentQuery.next(this.currentQuery);
        }
    }

    get isStaffSearch(): boolean {
        return this._isStaffSearch;
    }

    set isStaffSearch(value: boolean) {
        if (this._isStaffSearch != value) {
            this._isStaffSearch = value;
            if (value == true) {
                this._isCourseSearch = false;
                this._isStudentSearch = false;
            }
            this.currentQuery = new DataQuery();
            this.observableCurrentQuery.next(this.currentQuery);
        }
    }

    get studentFilters(): Observable<DataQuery[]> {
        if (this._studentFilters) {
            return of(this._studentFilters);
        } else {
            return this.getSavedQueries(true, false, false);
        }
    }

    get courseFilters(): Observable<DataQuery[]> {
        if (this._courseFilters) {
            return of(this._courseFilters);
        } else {
            return this.getSavedQueries(false, true, false);
        }
    }

    get staffFilters(): Observable<DataQuery[]> {
        if (this._staffFilters) {
            return of(this._staffFilters);
        } else {
            return this.getSavedQueries(false, false, true);
        }
    }

    get savedQueries(): Observable<DataQuery[]> {
        if (this._savedQueries) {
            return of(this._savedQueries);
        } else {
            return this.getSavedQueries(false, false, false);
        }
    }

    get savedQueriesInContext(): Observable<DataQuery[]> {
        if (this.isStudentSearch) {
            return this.studentFilters;
        } else if (this.isStaffSearch) {
            return this.staffFilters;
        } else if (this.isCourseSearch) {
            return this.courseFilters;
        } else {
            return this.savedQueries;
        }
    }

    get recentQueriesInContext(): DataQuery[] {
        if (this.isStudentSearch) {
            return this.information.recentStudentFilters;
        } else if (this.isStaffSearch) {
            return this.information.recentStaffFilters;
        } else if (this.isCourseSearch) {
            return this.information.recentCourseFilters;
        } else {
            return this.information.recentQueries;
        }
    }

    set recentQueriesInContext(value: DataQuery[]) {
        if (this.isStudentSearch) {
            this.information.recentStudentFilters = value;
        } else if (this.isStaffSearch) {
            this.information.recentStaffFilters = value;
        } else if (this.isCourseSearch) {
            this.information.recentCourseFilters = value;
        } else {
            this.information.recentQueries = value;
        }
    }

    get originalQuery(): DataQuery {
        if (this.isStudentSearch) {
            return this._originalStudentSearchQuery;
        } else {
            return this._originalDataQueryQuery;
        }
    }

    set originalQuery(value: DataQuery) {
        if (this.isStudentSearch) {
            this._originalStudentSearchQuery = value;
        } else {
            this._originalDataQueryQuery = value;
        }
    }

    get currentQuery(): DataQuery {
        if (this.isStudentSearch) {
            return this._currentStudentSearchQuery;
        } else {
            return this._currentDataQueryQuery;
        }
    }

    set currentQuery(value: DataQuery) {
        if (value == null) {
            value = new DataQuery();
        }
        if (value.QueryName == 'My Defaults') {
            value.QueryName = 'My Default';
        }
        value.organized = true;
        this.loadQuery(value);
    }

    get maximized(): boolean {
        return this._maximized;
    }

    set maximized(value: boolean) {
        this._maximized = value;
        this.observableMaximized.next(this._maximized);
    }

    get information(): DataQueryInformation {
        return this._information;
    }

    set information(value: DataQueryInformation) {
        this._information = value;
        this.observableInformation.next(this._information);
    }

    get preferences(): DataQueryPreferences {
        return this._preferences;
    }

    set preferences(value: DataQueryPreferences) {
        this._preferences = value;
        this.observablePreferences.next(this._preferences);
    }

    get results(): DataQueryResult {
        return this._results;
    }

    set results(value: DataQueryResult) {
        this._results = value;
        this.observableResults.next(this._results);
    }

    get currentTab(): string {
        return this._currentTab;
    }

    set currentTab(value: string) {
        this._previousTab = this._currentTab;
        this._currentTab = value;
        this.observableCurrentTab.next(this._currentTab);
    }

    get initialPageSize() {
        return 100;
    }

    get selectedCoreFile() {
        return this._selectedCoreFile;
    }

    set selectedCoreFile(value: DataQueryCoreFile) {
        this._selectedCoreFile = value;
        this.observableSelectedCoreFile.next(value);
    }

    get selectedQuickStart() {
        return this._selectedQuickStart;
    }

    set selectedQuickStart(value: DataQueryQuickStart) {
        this._selectedQuickStart = value;
        this.observableSelectedQuickStart.next(value);
    }

    get wizardOpen() {
        return this._wizardOpen;
    }

    set wizardOpen(value: boolean) {
        this._wizardOpen = value;
        // this.observableWizardOpen.next(value);
    }

    /////////// Method /////////////////
    isInformationCached() {
        // POST: returns whether or not information is cached
        return this.cachedInformation;
    }

    ////////// Server Calls //////////
    getSavedQueries(isStudentSearch = this.isStudentSearch, isCourseSearch = this.isCourseSearch, isStaffSearch = this.isStaffSearch): Observable<DataQuery[]> {
        // PRE: whether or not this is within the student search component or for student filters
        // POST: obtains the saved queries from the DB
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id +
            '&isStudentSearch=' + isStudentSearch +
            '&isCourseSearch=' + isCourseSearch +
            '&isStaffSearch=' + isStaffSearch;
        return this.http.get<DataQuery[]>(System.apiURL.Classroom + 'DataQueryGetSavedQueries' + queryString).pipe(
            take(1),
            map(queries => {
                if (!queries) {
                    queries = [];
                }
                for (let i = 0; i < queries.length; i++) {
                    queries[i] = System.convert<DataQuery>(queries[i], new DataQuery());

                    // Changing My Defaults => My Default...lovely
                    if (queries[i].QueryName == 'My Defaults') {
                        queries[i].QueryName = 'My Default';
                    }

                    // Setting up selected sites
                    if (queries[i].Scope == 'Selected Sites') {
                        queries[i].SelectedSiteNames = '';
                        const schoolIds = queries[i].SelectedSites.split(',');
                        schoolIds.forEach(schoolId => {
                            const schoolIdNumber = parseInt(schoolId.trim(), 10);
                            const school = this.yearService.schools.find(o => o.Id == schoolIdNumber);
                            if (school) {
                                if (queries[i].SelectedSiteNames.length != 0) {
                                    queries[i].SelectedSiteNames += ', ';
                                }
                                queries[i].SelectedSiteNames += school.SchoolName;
                            }
                        });
                    }
                }
                if (isStudentSearch) {
                    this._studentFilters = queries;
                } else if (isCourseSearch) {
                    this._courseFilters = queries;
                } else if (isStaffSearch) {
                    this._staffFilters = queries;
                } else {
                    this._savedQueries = queries;
                }
                return queries;
            })
        );
    }

    getRecentQueries(isStudentSearch = this.isStudentSearch, isCourseSearch = this.isCourseSearch, isStaffSearch = this.isStaffSearch): Observable<DataQuery[]> {
        // PRE: whether or not this is within the student search component
        // POST: obtains the recent recentQueries from the DB
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id +
            '&isStudentSearch=' + isStudentSearch +
            '&isCourseSearch=' + isCourseSearch +
            '&isStaffSearch=' + isStaffSearch;
        return this.http.get<DataQuery[]>(System.apiURL.Classroom + 'DataQueryGetRecentQueries' + queryString).pipe(
            map(queries => {
                // NOTE: we do this to overwrite 'My Defaults' with 'My Default'
                let returnedQueries = queries;
                if (!returnedQueries) {
                    returnedQueries = [];
                }
                for (let i = 0; i < returnedQueries.length; i++) {
                    returnedQueries[i] = System.convert(returnedQueries[i], new DataQuery());
                }
                returnedQueries.forEach(query => {
                    if (query.QueryName == 'My Defaults') {
                        query.QueryName = 'My Default';
                    }
                });
                return returnedQueries;
            })
        );
    }

    getObjects(): Observable<DataQueryObject[]> {
        // POST: obtains the data query objects from the DB
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id;
        return this.http.get<DataQueryObject[]>(System.apiURL.Classroom + 'GetDataQueryObjects' + queryString);
    }

    getCategories(): Observable<DataQueryCategory[]> {
        // POST: obtains the data query categories from the DB
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id;
        return this.http.get<DataQueryCategory[]>(System.apiURL.Classroom + 'GetDataQueryCategories' + queryString);
    }

    getFields(): Observable<DataQueryField[]> {
        // POST: obtains the data query fields from the DB
        let reorganize = false;
        if (this.information.organized) {
            this.information.disorganize();
            reorganize = true;
        }
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id;
        return this.http.get<DataQueryField[]>(System.apiURL.Classroom + 'GetDataQueryFields' + queryString).pipe(
            map(results => {
                if (reorganize) {
                    this.information.organize();
                }
                return results;
            })
        );
    }

    getUserCategories(): Observable<number[]> {
        // POST: obtains the user's category numbers in V10 or an empty array in V9

        // Checking the cache
        if (this.userCategories) {
            return of(this.userCategories);
        }

        // If not cached, feting from the server
        if (this.loginService.LoginSettings.V10) {
            return this.http.get<number[]>(System.apiURL.Classroom + 'GetV10UserCategoryIds').pipe(
                map(results => {
                    this.userCategories = results;
                    return this.userCategories;
                })
            );
        } else {
            return of([]);
        }
    }

    getDefaultQuery(): Observable<DataQuery> {
        // POST: obtains the default query from the server
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id;
        return this.http.get<DataQuery>(System.apiURL.Classroom + 'DataQueryGetMyDefaultsQuery' + queryString);
    }

    getInformation(): Observable<DataQueryInformation> {
        // POST: obtains all the relevant information from the server
        const serverCalls = forkJoin(
            this.getRecentQueries(false, false, false),
            this.getObjects(),
            this.getCategories(),
            this.getFields(),
            this.getUserCategories(),
            this.getDefaultQuery(),
            this.getRecentQueries(true, false, false),
            this.getRecentQueries(false, true, false),
            this.getRecentQueries(false, false, true),
            this.getOperators(),    // We call this to cache the result
            this.getRelationships([], false)
        );
        if (this.cachedInformation) {
            return of(this.information).pipe(take(1));
        } else {
            return serverCalls.pipe(
                retry(9),
                map(results => {
                    const information = new DataQueryInformation();

                    // Performing conversions
                    for (let i = 0; i < results[0].length; i++) {
                        results[0][i] = System.convert<DataQuery>(results[0][i], new DataQuery());
                    }
                    for (let i = 0; i < results[1].length; i++) {
                        results[1][i] = System.convert<DataQueryObject>(results[1][i], new DataQueryObject());
                    }
                    for (let i = 0; i < results[2].length; i++) {
                        results[2][i] = System.convert<DataQueryCategory>(results[2][i], new DataQueryCategory());
                    }
                    for (let i = 0; i < results[3].length; i++) {
                        results[3][i] = System.convert<DataQueryField>(results[3][i], new DataQueryField());
                    }
                    results[5] = System.convert<DataQuery>(results[5], new DataQuery());
                    const defaultQuery = System.convert<DataQuery>(results[5], new DataQuery());
                    for (let i = 0; i < results[6].length; i++) {
                        results[6][i] = System.convert<DataQuery>(results[6][i], new DataQuery());
                    }
                    for (let i = 0; i < results[7].length; i++) {
                        results[7][i] = System.convert<DataQuery>(results[7][i], new DataQuery());
                    }
                    for (let i = 0; i < results[8].length; i++) {
                        results[8][i] = System.convert<DataQuery>(results[8][i], new DataQuery());
                    }

                    // Setting information
                    information.recentQueries = results[0];
                    information.objects = results[1];
                    information.categories = results[2];
                    information.fields = results[3];
                    if (this.V10 == false) {
                        information.fields.forEach(field => {
                            if (field.DisplayName.indexOf('.') > -1) {
                                field.DisplayName = field.DisplayName.replace('.', '');
                            }
                        });
                    }

                    information.userCategories = results[4];
                    information.recentStudentFilters = results[6];
                    information.recentCourseFilters = results[7];
                    information.recentStaffFilters = results[8];
                    const recentDefault = information.recentQueries.find(query => query.ID == defaultQuery.ID);
                    if (recentDefault) {
                        information.defaultQuery = recentDefault;
                    } else {
                        information.defaultQuery = defaultQuery;
                        this.information.recentQueries.push(defaultQuery);
                    }
                    information.relationships = results[10];
                    information.organize();

                    // Returning the information
                    this.information = information;
                    this.cachedInformation = true;

                    // Returning the information
                    return this.information;
                }),
                take(1)
            );
        }
    }

    getPermissions(): Observable<DataQueryPermissions> {
        // POST: sets the permissions of the data query

        // If we've already set the permissions, simply return that
        if (this.permissions) {
            return of(this.permissions);
        }

        // Otherwise, get the permissions from the server and set it
        this.userPermissions = this.securityService.userPermissions;
        this.permissions = new DataQueryPermissions(this.userPermissions, this.loginService.LoginSettings.V10);
        return of(this.permissions);
    }

    getPreferences(): Observable<DataQueryPreferences> {
        // POST: obtains the preferences from the server
        // NOTE: this is implemented this way so that it only needs to be called from DataQueryComponent
        if (this.cachedPreferences) {
            return of(this.preferences);
        } else {
            return this.userPreferencesService.getUserPreferences(0,
                '/OneSISDataQuery',
                0,
                '',
                false
            ).pipe(
                map(results => {
                    this.preferences = JSON.parse(results[0]);
                    this.userPreferencesService.dataQueryPreferences = this.preferences;
                    this.cachedPreferences = true;
                    return this.preferences;
                })
            );
        }
    }

    getLegacyQueries(): Observable<DataQuery[]> {
        // POST: obtains the legacy queries from the server

        // If we already have the legacy queries, just use that
        if (this.legacyQueries) {
            return of(this.legacyQueries);
        }

        // Otherwise, get the queries from the server
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id;
        return this.http.get<DataQuery[]>(System.apiURL.Classroom + 'GetSavedQueriesFromLegacy' + queryString).pipe(
            map(results => {
                this.legacyQueries = results;
                for (let i = 0; i < this.legacyQueries.length; i++) {
                    System.convert<DataQuery>(this.legacyQueries[i], new DataQuery());
                }
                return this.legacyQueries;
            })
        );
    }

    getFunctions(): Observable<DataQueryFunction[]> {
        // POST: obtains the functions from the server
        if (this.functions) {
            return of(this.functions);
        } else {
            return this.http.get<DataQueryFunction[]>(System.apiURL.Classroom + 'GetDataQueryFunctions').pipe(
                map(functions => {
                    this.functions = functions;
                    for (let i = 0; i < this.functions.length; i++) {
                        this.functions[i] = System.convert<DataQueryFunction>(this.functions[i], new DataQueryFunction());
                    }
                    return this.functions;
                })
            );
        }
    }

    getOperators(): Observable<DataQueryOperator[]> {
        // POST: obtains the operators from the server
        if (this.operators) {
            return of(this.operators);
        } else {
            return this.http.get<DataQueryOperator[]>(System.apiURL.Classroom + 'GetDataQueryOperators').pipe(
                map(operators => {
                    this.operators = operators;
                    for (let i = 0; i < this.operators.length; i++) {
                        this.operators[i] = System.convert<DataQueryOperator>(this.operators[i], new DataQueryOperator());
                    }
                    return this.operators;
                })
            );
        }
    }

    getParameters(): Observable<DataQueryParameter[]> {
        // POST: obtain the parameters from the server
        if (this.parameters) {
            return of(this.parameters);
        } else {
            return this.http.get<any[]>(System.apiURL.Classroom + 'GetDataQueryParameters').pipe(
                map(parameters => {
                    this.parameters = parameters;
                    for (let i = 0; i < this.parameters.length; i++) {
                        this.parameters[i] = System.convert<DataQueryParameter>(this.parameters[i], new DataQueryParameter());
                    }
                    return this.parameters;
                })
            );
        }
    }

    getRelationships(fields?: DataQueryField[], forCurrentQuery = true): Observable<DataQueryRelationship[]> {
        // PRE: a list of fields to get relationships for
        // POST: returns a list of data query relationships
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id;
        if (!fields) {
            if (forCurrentQuery) {
                fields = this.getAllFields(this.currentQuery);
            } else {
                fields = [];
            }
        } else {
            fields.forEach(field => {
                field.object = null;
                field.category = null;
            });
        }

        // We have to preserve the filters because the following can eliminate them
        const filters = [];
        if (forCurrentQuery) {
            for (let i = 0; i < this.currentQuery.filterFields.length; i++) {
                filters.push(this.currentQuery.filterFields[i].Filters);
            }
        }

        // We have to remove the transformation fields to avoid a null error on the DTO
        const transformationFields = [];
        for (let i = 0; i < fields.length; i++) {
            transformationFields[i] = fields[i].TransformationFields;
            delete fields[i].TransformationFields;
        }

        // Returning the request
        return this.postRequest<DataQueryRelationship[]>('GetDataQueryRelationships', queryString, fields).pipe(
            map(relationships => {
                // NOTE: we do this processing to prevent overwriting whatever relationships are present

                // We only do this if we're manipulating the query
                if (forCurrentQuery) {

                    // Resetting the transformation fields
                    for (let i = 0; i < fields.length; i++) {
                        fields[i].TransformationFields = transformationFields[i];
                    }

                    // Using the right relationship array
                    let retRelationships = [];
                    if (forCurrentQuery) {
                        retRelationships = this.currentQuery.relationships;
                        for (let i = 0; i < this.currentQuery.filterFields.length; i++) {
                            this.currentQuery.filterFields[i].Filters = filters[i];
                        }
                    }

                    // Removing erroneous relationships from the current query
                    let l = retRelationships.length;
                    while (l) {
                        l -= 1;
                        if (!relationships.find(rel => rel.ID == this.currentQuery.relationships[l].ID)) {
                            retRelationships.splice(l, 1);
                        }
                    }

                    // Adding new relationships
                    relationships.forEach(relationship => {
                        if (!this.currentQuery.relationships.find(rel =>
                            (rel.Category1ID == relationship.Category1ID && rel.Category2ID == relationship.Category2ID)
                            || (rel.Category2ID == relationship.Category1ID && rel.Category1ID == relationship.Category2ID))
                        ) {
                            retRelationships.push(relationship);
                        }
                    });

                    // Returning the relationships
                    this.currentQuery.relationships = retRelationships;
                    return retRelationships;
                } else {
                    return relationships;
                }
            }),
            catchError(err => {

                // Resetting the transformation fields
                for (let i = 0; i < fields.length; i++) {
                    fields[i].TransformationFields = transformationFields[i];
                }

                // Throwing the error to higher handlers
                console.log('Error');
                console.log(err);
                throw (err);
            })
        );
    }

    saveQuery(query: DataQuery, _callback: Function, _error?: Function, includeValidation = true, forImport = false): void {
        // PRE: a query to save
        // POST: performs the validation (if includeValidation is true) then saves the query to the server

        // The service call as a utility function
        const serverCall = () => {
            const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
                '&yearId=' + this.yearService.defaultYear.Id +
                '&siteSectionId=' + query.siteSectionId;

            // Setting the XML for saving
            let exportSettings = query.QueryXML.substring(query.QueryXML.indexOf('<ExportSettings>'), query.QueryXML.indexOf('</ExportSettings>'));
            const reportXML = query.QueryXML.substring(query.QueryXML.indexOf('<reportTemplate>'), query.QueryXML.indexOf('</reportTemplate'));

            query.QueryXML = this.getQueryXML(this.getAllFields(query, false, true));
            if (exportSettings != undefined && exportSettings != '') {
                // ONE -10727
                const exportTypeXML = exportSettings.substring(exportSettings.indexOf('<ExportType>'), exportSettings.indexOf('</ExportType>'));
                exportSettings = exportSettings.replace(exportTypeXML + '</ExportType>', '');
                exportSettings += '<ExportType>' + query.ExportType + '</ExportType>';
                query.QueryXML = query.QueryXML.replace('</root>', exportSettings + '</ExportSettings>' + '</root>');
            }
            query.QueryXML = query.QueryXML;

            if (reportXML != undefined && reportXML != '') {
                const rtXML = this.getReportTemplateXMLData(reportXML);
                query.QueryXML = query.QueryXML + rtXML;
            }

            // The default query is "my default"
            // but the DTO expects my defaults for some reason
            if (query.QueryName == 'My Default') {
                query.QueryName = 'My Defaults';
            }
            if (query.ID <= 0) {
                query.OwnerName = this.userService.currentUser.LastName + ', ' + this.userService.currentUser.FirstName;
            }

            // Sending the save request
            const saveSubscription = this.postRequest<any>('DataQuerySaveQuery', queryString, query).subscribe(results => {
                if (results[0] == 'Success') {

                    // The default query is "my default"
                    // but the DTO expects my defaults for some reason
                    if (query.QueryName == 'My Defaults') {
                        query.QueryName = 'My Default';
                    }

                    // Setting the query's data
                    query.ID = parseInt(results[1], 10);
                    this.savedQueryModified = false;
                    query.queryChanged = false;

                    // Setting the query as a recent query (to preserve data) (context-sensitive)
                    if (!query.Deleted) {
                        let queryFound = false;
                        this.recentQueriesInContext.forEach((recentQuery: DataQuery) => {
                            if (recentQuery.ID == query.ID) {
                                Object.assign(recentQuery, query);
                                queryFound = true;
                            }
                        });
                        if (!queryFound) {
                            this.recentQueriesInContext.unshift(query);
                        }
                    }

                    // If the current query is the deleted query, set it's ID to -1
                    if (this.currentQuery.Deleted && this.currentQuery == query) {
                        this.currentQuery.ID = -1;
                    }

                    if (this._savedQueries == undefined || this._savedQueries == null) {
                        this._savedQueries = [];
                    }

                    // Performing manipulations of saved queries (context-sensitive)
                    this.savedQueriesInContext.pipe(take(1)).subscribe(queries => {

                        // Adding or removing it to the saved queries
                        if (!query.Deleted) {

                            // When Overwrite query, delete it and then readd it
                            const indexOfSavedQuery = queries.findIndex(savedQuery => savedQuery.ID == query.ID);
                            if (indexOfSavedQuery >= 0) {
                                queries.splice(indexOfSavedQuery, 1);
                            }

                            // Adding it if we cannot find it
                            if (queries && !queries.find(savedQuery => savedQuery.ID == query.ID)) {
                                queries.push(query);
                            }
                        } else {
                            if (queries) {
                                const indexOfSavedQuery = queries.findIndex(savedQuery => savedQuery.ID == query.ID);
                                if (indexOfSavedQuery >= 0) {
                                    queries.splice(indexOfSavedQuery, 1);
                                }
                            }
                        }
                    });
                    // Showing confirmation
                    this.sharedService.showGoodSnackbar();
                } else {

                    // Showing error notice
                    this.sharedService.showBadSnackbar();
                }
                if (_callback) {
                    _callback(results);
                }
                saveSubscription.unsubscribe();
            });
        };

        // Performing validation if we need to
        if (includeValidation) {
            this.validateQuery(
                () => {
                    serverCall();
                },
                () => {
                    // this.sharedService.showBadSnackbar();
                    if (_error) {
                        _error();
                    }
                },
                forImport,
                query
            );
        } else {
            serverCall();
        }
    }

    deleteQuery(query: DataQuery, _callback: Function, _error?: Function): void {
        // PRE: a query to delete
        // POST: flags the query for deletion and saves it
        query.Deleted = true;
        this.saveQuery(query, _callback, _error, false);
    }

    updateRecentQueries(query: DataQuery): Observable<DataQuery[]> {
        // PRE: a queryId to set as recent
        // POST: sets the query as a recent query
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id +
            '&queryId=' + query.ID +
            '&isStudentSearch=' + this.isStudentSearch +
            '&isCourseSearch=' + this.isCourseSearch +
            '&isStaffSearch=' + this.isStaffSearch;
        return this.http.get<DataQuery[]>(System.apiURL.Classroom + 'DataQueryUpdateRecentQueries' + queryString).pipe(
            map(results => {
                for (let i = 0; i < results.length; i++) {
                    if (results[i].QueryName == 'My Defaults') {
                        results[i].QueryName = 'My Default';
                    }
                    if (results[i].ID == query.ID) {
                        results[i] = query;
                    } else {
                        results[i] = System.convert<DataQuery>(results[i], new DataQuery());
                    }
                }
                this.recentQueriesInContext = results;
                if (!this.recentQueriesInContext.find(recentFilter => recentFilter.ID == this.currentQuery.ID)) {
                    this.recentQueriesInContext.push(this.currentQuery);
                }
                return results;
            })
        );
    }

    importQueryFromLegacy(importedQuery): Observable<DataQuery> {
        // PRE: a query object that is used for persistence of the server
        // POST: returns the legacy query while maintaining some persistence with this query
        // NOTE: this does not perform the object assignment for you, in case you want to maintain some of your data
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id;
        return this.postRequest<DataQuery>('ImportQueryFromLegacy', queryString, importedQuery);
    }

    getFilterOptions(filter: DataQueryFieldFilter, categoryId = 0, eForm = false): Observable<Lookup[]> {
        // PRE: a filter to obtain the filter options for. The filter must have a field or the categoryId must be set.
        // POST: returns a list of lookups and automatically sets the filter options of the filter
        // NOTE: this will just return the filter options of the filter if its set
        if (filter.FilterOptions && filter.FilterOptions.length > 0) {
            return of(filter.FilterOptions);
        }
        let queryString = '';
        // TODO: this shouldn't exist. Ever. We shouldn't add context dependent variables to the service.
        if (eForm == true) {
            queryString = '?dataQueryFieldId=' + (filter ? filter.ID : filter.FilterID) +
                '&dataQueryCategoryId=' + (filter ? filter.CategoryID : categoryId) +
                '&userId=' + this.userService.currentUser.Id;
        } else {
            queryString = '?dataQueryFieldId=' + (filter.field ? filter.field.ID : filter.FilterID) +
                '&dataQueryCategoryId=' + (filter.field ? filter.field.CategoryID : categoryId) +
                '&userId=' + this.userService.currentUser.Id;
        }

        return this.http.get<Lookup[]>(System.apiURL.Classroom + 'GetDataQueryFieldLookupReadByFieldId' + queryString).pipe(
            map(results => {
                results.sort((a: Lookup, b: Lookup) => {
                    return a.Sequence - b.Sequence;
                });
                filter.FilterOptions = results;
                return results;
            })
        );
    }

    getFilterOptionsByField(field: DataQueryField): Observable<Lookup[]> {
        if (field.FilterOptions && field.FilterOptions.length > 0) {
            return of(field.FilterOptions);
        }
        let queryString = '';
        queryString = '?dataQueryFieldId=' + (field.ID) +
            '&dataQueryCategoryId=' + (field.CategoryID) +
            '&userId=' + this.userService.currentUser.Id;
        return this.http.get<Lookup[]>(System.apiURL.Classroom + 'GetDataQueryFieldLookupReadByFieldId' + queryString).pipe(
            map(results => {
                results.sort((a: Lookup, b: Lookup) => {
                    return a.Sequence - b.Sequence;
                });
                field.FilterOptions = results;
                return results;
            })
        );
    }

    validateTransformationFunctions(xml, index): Observable<string> {
        // PRE: XML to be validates
        // POST: validates the transformation functions found within the supplied XML
        xml = xml.replace(/Age /g, 'Age&nbsp;');
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id +
            '&index=' + index +
            '&siteSectionId=' + this.currentQuery.siteSectionId;
        return this.http.post<string>(System.apiURL.Classroom + 'ValidateTransformationFunctions' + queryString, [xml]);
    }

    getPreviewResults(): Observable<string[]> {
        // POST: obtains a single set of results for preview
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id +
            '&index=' + 0 +
            '&siteSectionId=' + this.currentQuery.siteSectionId;
        let xml = this.getQueryXML(this.getAllFields());
        xml = xml.replace(/Age /g, 'Age&nbsp;');
        return this.http.post<string[]>(System.apiURL.Classroom + 'DataQueryPreviewResults' + queryString, [xml]).pipe(
            map(results => {
                for (let i = 0; i < results.length; i++) {
                    if (this.currentQuery.reportFields.length > i) {
                        this.currentQuery.reportFields[i].PreviewData = results[i];
                    }
                }
                return results;
            })
        );
    }

    getResults(getChunkedResults: boolean, _callback?: Function, _error?: Function, getRelationshipsBeforeResults = false, callFromStudentSearch = false, siteIds = '') {
        // POST: obtains a page of results from the server or composes a page based on the data we have
        // Getting relationships before we try to get results

        // Removing subscriptions we've closed
        this.currentResultSubscriptions = this.currentResultSubscriptions.filter(subscription => !subscription.closed);

        // Obtaining results if we need to
        if (this.currentQuery.queryChangedSinceLastResult || (!this.loginService.LoginSettings.V10 && this.isHasEdited == true)) {
            let pageToGet = 0;
            let resultsPerPage = 0;
            this.nextPageSubscriptions.forEach(sub => {
                sub.unsubscribe();
            });
            let observable = of(this.currentQuery.relationships);
            if (getRelationshipsBeforeResults) {
                observable = this.getRelationships();
            }
            this.currentResultSubscriptions.push(
                observable.pipe(take(1)).subscribe(() => {

                    // Enforcing transformation fields (in case we haven't until this point)
                    this.currentQuery.reportFields.forEach(field => {
                        if (!field.TransformationFields || field.TransformationFields.length == 0) {
                            this.transFunctionChanged(field);
                        }
                    });

                    // Starting from the query XML
                    let xml = this.getQueryXML(this.getAllFields(), true);
                    xml = xml.replace(/Age /g, 'Age&nbsp;');

                    // Resetting result properties if we've changed the query since the last results
                    if (this.currentQuery.queryChangedSinceLastResult) {
                        this.resetResultProperties();
                        pageToGet = 1;
                    }

                    // Creating helper functions
                    const updateResultsPerPage = () => {
                        this.pageCount = Math.ceil(this.recordCount / resultsPerPage);
                    };

                    // Initializing the calls
                    let errorCount = 0;

                    let queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
                        '&yearId=' + this.yearService.defaultYear.Id +
                        '&page=' + pageToGet +
                        '&recordsPerPage=' + resultsPerPage +
                        '&siteSectionId=' + this.currentQuery.siteSectionId +
                        '&callFromStudentSearch=' + callFromStudentSearch +
                        '&siteIds=' + siteIds;

                    // Checking for errors
                    const dataQueryFormError: any = {};
                    if (!this.loginService.LoginSettings.V10) {
                        for (let i = 0; i < this.currentQuery.reportFields.length; i++) {
                            if (this.currentQuery.reportFields[i].Alias == '') {
                                dataQueryFormError.blankAlias = true;
                            }
                            const upperAlias = (this.currentQuery.reportFields[i].Alias == null ? this.currentQuery.reportFields[i].DisplayName : this.currentQuery.reportFields[i].Alias);
                            for (let j = i + 1; j < this.currentQuery.reportFields.length; j++) {
                                const innerAlias = (this.currentQuery.reportFields[j].Alias == null ? this.currentQuery.reportFields[j].DisplayName : this.currentQuery.reportFields[j].Alias);
                                if (upperAlias.toLowerCase() == innerAlias.toLowerCase()) {
                                    errorCount = 1;
                                    dataQueryFormError.duplicateAlias = upperAlias;
                                }
                            }
                        }
                        if (dataQueryFormError.blankAlias == '') {
                            errorCount += 1;
                        }
                    }

                    // If there are no errors, get the results
                    if (errorCount == 0) {
                        // const xml = this.getQueryXML(this.currentQuery.reportFields);
                        let isGetResultsFinished = false;
                        const isGetCountFinished = false;

                        // ONE-6403: If more than 10,000 records, warn the user
                        this.currentResultSubscriptions.push(
                            this.http.post<number>(System.apiURL.Classroom + 'GetDataQueryResultCount' + queryString, [xml]).pipe(take(1)).subscribe((count: number) => {
                                let dialogChoice = of(true);

                                // Showing a dialog if we got a negative number (indicating an error)
                                if (count < 0) {
                                    const dialogBox = this.dialog.open(GeneralConfirmationComponent, {
                                        disableClose: true,
                                        data: {
                                            headerLabel: 'Too Many Records',
                                            informational: true,
                                            confirmMessage: 'Your request will pull too many records. Update your filters to return less records.'
                                        }
                                    });
                                    dialogChoice = dialogBox.afterClosed().pipe(map(() => { return false; }));
                                }

                                // Showing a dialog if there are more than 10,000 records
                                if (count > 10000) {
                                    const dialogBox = this.dialog.open(GeneralConfirmationComponent, {
                                        disableClose: true,
                                        data: {
                                            headerLabel: 'WARNING',
                                            warning: true,
                                            confirmMessage: 'Your request will pull ' + count.toLocaleString() + ' records. This process may take several minutes.',
                                            secondMessage: 'Do you want to proceed?',
                                            okButtonLabel: 'Continue'
                                        }
                                    });
                                    dialogChoice = dialogBox.afterClosed();
                                }
                                dialogChoice.subscribe(choice => {

                                    // If the user chooses to continue (or no dialog was shown), obtain the results
                                    if (choice) {

                                        // Updating the record count
                                        this.recordCount = count;
                                        updateResultsPerPage();

                                        // Setting the timeout for the notification
                                        if (this.queryLoadingTimeout) {
                                            clearTimeout(this.queryLoadingTimeout);
                                        }
                                        this.queryLoadingTimeout = setTimeout(() => {
                                            this.sharedService.showCustomSnackbar(
                                                'The query is still loading. Adding filters can speed up the query.',
                                                'notification'
                                            );
                                        }, 10000);

                                        // Obtaining the results by page
                                        if (this.recordCount > 10000 && getChunkedResults) {
                                            pageToGet = 1;
                                            resultsPerPage = 100000;
                                            queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
                                                '&yearId=' + this.yearService.defaultYear.Id +
                                                '&page=' + pageToGet +
                                                '&recordsPerPage=' + resultsPerPage +
                                                '&siteSectionId=' + this.currentQuery.siteSectionId +
                                                '&callFromStudentSearch=' + callFromStudentSearch +
                                                '&siteIds=' + siteIds;
                                        }
                                        this.currentResultSubscriptions.push(
                                            this.postRequest<DataQueryResult>('GetDataQueryResults', queryString, [xml]).subscribe(
                                                (results) => {
                                                    clearTimeout(this.queryLoadingTimeout);

                                                    // Ensuring results exist
                                                    if (!results) {
                                                        return;
                                                    }

                                                    // Converting the results
                                                    results = System.convert(results, new DataQueryResult());

                                                    // Setting query SQL based on result SQL
                                                    this.sqlErrors = results.Errors;
                                                    this.currentQuery.SQLScript = results.QueryString;
                                                    this.currentQuery.SQLScript.replace('\r\n', '<br />');
                                                    this.currentQuery.SQLScript.replace('\t', '&nbsp;&nbsp;&nbsp;&nbsp;');

                                                    // Checking for disable fields
                                                    this.disabledFieldInReport = false;
                                                    if (results.Data && results.Data.length > 0) {
                                                        for (let i = 0; i < results.Data[0].length; i++) {
                                                            if (results.Data[0][i] == '[-[-**Disabled**-]-]') {
                                                                this.disabledFieldInReport = true;
                                                            }
                                                        }
                                                    }

                                                    // Ensuring the headers have the right semantic data type
                                                    // TODO: this change is for ONE-3481, where a column that only has numbers is VARCHAR. Ultimately, we should change the data types to be semantically correct.
                                                    if (this.V10) {
                                                        for (let i = 0; i < results.Columns.length; i++) {
                                                            if (results.Columns[i].DataType == 'VARCHAR' || results.Columns[i].DataType == 'NVARCHAR') {
                                                                let onlyNumbers = true;
                                                                let allBlank = true;
                                                                for (let j = 0; j < results.Data.length; j++) {
                                                                    if (results.Data[j][i] && results.Data[j][i] != '') {
                                                                        allBlank = false;
                                                                    }
                                                                    if (results.Data[j][i] && Number.isNaN(Number(results.Data[j][i]))) {
                                                                        onlyNumbers = false;
                                                                        break;
                                                                    }
                                                                }
                                                                if (onlyNumbers && allBlank == false) {
                                                                    results.Columns[i].DataType = 'NUMERIC';
                                                                }
                                                            }
                                                        }
                                                    }

                                                    if (!this.loginService.LoginSettings.V10) {
                                                        results.Data.forEach(datum => {
                                                            for (let i = 0; i < datum.length; i++) {
                                                                if (datum[i]) {
                                                                    datum[i] = datum[i].trim();
                                                                }
                                                            }
                                                        });
                                                    }

                                                    // Converting the result data to the right data time
                                                    this.fixDataTypes(results);

                                                    this.results = results;
                                                    if (getChunkedResults && resultsPerPage > 0) {
                                                        let totalPages = Math.floor(this.recordCount / resultsPerPage);
                                                        if (this.recordCount % resultsPerPage > 0) {
                                                            totalPages++;
                                                        }
                                                        if (totalPages > 1) {
                                                            this.getRemainingPages(2, resultsPerPage, totalPages, xml);
                                                        }
                                                    }

                                                    // Resetting query data
                                                    if (this.currentQuery.queryChangedSinceLastResult) {
                                                        this.resetPageIndex = true;
                                                    }
                                                    updateResultsPerPage();
                                                    this.currentQuery.queryChangedSinceLastResult = false;
                                                    if (_callback != null) {
                                                        _callback(results);
                                                    }
                                                },
                                                () => {
                                                    if (_error != null) {
                                                        _error();
                                                    }
                                                },
                                                () => {
                                                    isGetResultsFinished = true;
                                                }
                                            )
                                        );
                                    }

                                    // If the user decides to cancel, take them to the filters tab
                                    else {
                                        this.currentTab = 'Filters';
                                    }
                                });
                            })
                        );
                    } else {
                        if (_error != null) {
                            _error(dataQueryFormError);
                        }
                    }
                })
            );
        }

        // If we don't need to get new results, reassert the existing ones
        else {
            this.results = cloneDeep(this.results);
            _callback(this.results);
        }
    }

    saveDataQueryResult(data: DataQueryResult): Observable<boolean> {
        let queryString = '';
        queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id;
        return this.postRequest<boolean>('SaveDataQueryResults', queryString, data);
    }

    fixDataTypes(results: DataQueryResult) {
        // Converting the result data to the right data time
        results.Data.forEach(datum => {
            for (let i = 0; i < datum.length; i++) {
                if (results.Columns[i].Visible) {
                    switch (results.Columns[i].DataType) {

                        // Booleans
                        case 'BIT':
                            datum[i] = datum[i] == '1';
                            break;

                        // PHONE
                        case 'PHONE':
                            if (datum[i] && datum[i].trim().length >= 10) {
                                datum[i] = '(' + datum[i].substring(0, 3) + ') ' + datum[i].substring(3, 6) + '-' + datum[i].substring(6, datum[i].length);
                            } else {
                                datum[i] = datum[i];
                            }
                            break;

                        // Decimals
                        case 'DECIMAL':
                        case 'NUMERIC':
                        case 'FLOAT':
                        case 'REAL':
                            if (datum[i] && datum[i].trim() != '') {
                                datum[i] = Number.parseFloat(datum[i] as string);
                            }
                            break;

                        // Integers
                        case 'INT':
                        case 'BIGINT':
                        case 'TINYINT':
                            if (datum[i]) {
                                datum[i] = Number.parseInt(datum[i] as string, 10);
                            }
                            break;

                        // Dates (WE DON'T CONVERT BECAUSE YOU CAN CHANGE HOW THEY DISPLAY)
                        case 'SMALLDATETIME':
                        case 'DATETIME':
                        case 'DATE':
                        case 'DATETIME2':
                        case 'DATETIMEOFFSET':
                            break;

                        // They are natively strings, so do nothing
                        case 'IMAGE':
                        case 'VARCHAR':
                        case 'CHAR':
                        case 'NVARCHAR':
                        case 'NCHAR':
                        case 'NTEXT':
                        case 'BINARY':
                        case 'VARBINARY':
                        default:
                            break;
                    }
                }
            }
        });
    }

    getRemainingPages(pageToGet: number, recordsPerPage: number, totalPages: number, xml: string) {
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id +
            '&page=' + pageToGet +
            '&recordsPerPage=' + recordsPerPage +
            '&siteSectionId=' + this.currentQuery.siteSectionId;
        const subscription = this.postRequest<DataQueryResult>('GetDataQueryNextPage', queryString, [xml])
            .subscribe(results => {
                results = System.convert(results, new DataQueryResult());
                this.fixDataTypes(results);
                this.results.assertOrder(results);
                const page = new PagedContent<DataQueryResult>(pageToGet, results);
                this.observableNextPageResults.next(page);
                if (pageToGet < totalPages) {
                    this.getRemainingPages(pageToGet + 1, recordsPerPage, totalPages, xml);
                }
            });
        this.currentResultSubscriptions.push(subscription);
        this.nextPageSubscriptions.push(
            subscription
        );
    }

    getResultsAsString(): Observable<string> {
        // POST: obtains the results of the query as a string

        // Setting the query XML
        this.currentQuery.QueryXML = this.getQueryXML(this.getAllFields(), true);
        const currentQuery = cloneDeep(this.currentQuery);
        currentQuery.QueryXML = currentQuery.QueryXML.replace(/Age /g, 'Age&nbsp;');

        // ONE-7785  CouldFlare blocks the request which contains SQL string.
        if (this.loginService.LoginSettings.V10 == false) {
            currentQuery.SQLScript = '';

            // Remove circular structure to JSON
            if (currentQuery.reportFields != undefined && currentQuery.reportFields.length > 0) {
                currentQuery.reportFields.forEach(rField => {
                    if (rField.object != undefined) {
                        rField.object = null;
                    }
                    if (rField.category != undefined) {
                        rField.category = null;
                    }
                });
            }
            if (currentQuery.sortFields != undefined && currentQuery.sortFields.length > 0) {
                currentQuery.sortFields.forEach(sField => {
                    // Remove circular structure to JSON
                    if (sField.object != undefined) {
                        sField.object = null;
                    }
                    if (sField.category != undefined) {
                        sField.category = null;
                    }
                });
            }
            if (currentQuery.filterFields != undefined && currentQuery.filterFields.length > 0) {
                currentQuery.filterFields.forEach(fField => {
                    // Remove circular structure to JSON
                    if (fField.object != undefined) {
                        fField.object = null;
                    }
                    if (fField.category != undefined) {
                        fField.category = null;
                    }
                });
            }
        }

        // Composing the service call
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id +
            '&siteSectionId=' + this.currentQuery.siteSectionId;
        return this.postRequest<string>('DataQueryGetResultsAsString', queryString, currentQuery);
    }

    getResultPDF(printParameters: PrintParameters): Observable<string> {
        // POST: obtains a string representing a result pdf

        // Setting the query XML
        this.currentQuery.QueryXML = this.getQueryXML(this.getAllFields(), true);
        let xml = this.currentQuery.QueryXML;
        xml = xml.replace(/Age /g, 'Age&nbsp;');

        // Composing the service call
        this.information.disorganize(); // We have to manually disorganize because we have to stringify it beforehand
        this.currentQuery.disorganize();
        const postParams = [
            xml,
            JSON.stringify(printParameters),
            JSON.stringify(this.currentQuery.reportFields),
            this.yearService.defaultYear.SchoolName,
            this.yearService.defaultYear.Description
        ];
        this.information.organize(); // We have to manually reorganize because we have to stringify it beforehand
        this.currentQuery.organize(this.information, this.V10);
        const queryString = '?schoolId=' + this.yearService.defaultYear.SchoolID +
            '&yearId=' + this.yearService.defaultYear.Id +
            '&page=1' +
            '&recordsPerPage=0' +
            '&siteSectionId=' + this.currentQuery.siteSectionId;
        return this.http.post<string>(System.apiURL.Classroom + 'DQGeneratePDF' + queryString, postParams).pipe(
            map(results => {
                return results;
            })
        );
    }

    getReportTemplateXML(queryId: number): Observable<string> {
        let xml = '';
        if (queryId) {
            const queryString = '?queryId=' + queryId;
            return this.http.get<any>(System.apiURL.Classroom + 'DQGetReportTemplate' + queryString).pipe(
                map(report => {
                    if (report.Id > 0) {
                        xml += '<reportTemplate>';
                        xml += '<Name>' + report.Name + '</Name>';
                        xml += '<Template>' + report.Template + '</Template>';
                        xml += '</reportTemplate>';
                    }
                    return xml;
                })
            );
        }
    }

    getCoreFiles(): Observable<DataQueryCoreFile[]> {
        return this.http.get<DataQueryCoreFile[]>(System.apiURL.Classroom + 'GetDataQueryCoreFiles').pipe(take(1));
    }

    getCategoriesByCoreFile(coreFileId: number): Observable<DataQueryCategory[]> {
        if (this.categoriesByCoreFile[coreFileId]) {
            return of(this.categoriesByCoreFile[coreFileId]);
        } else {
            const queryString = '?siteId=' + this.yearService.defaultYear.SchoolID
                + '&yearId=' + this.yearService.defaultYear.Id
                + '&coreFileId=' + coreFileId;
            return this.http.get<DataQueryCategory[]>(System.apiURL.Classroom + 'GetDataQueryCategoriesByCoreFile' + queryString)
                .pipe(
                    take(1),
                    map(categories => {
                        this.categoriesByCoreFile[coreFileId] = categories;
                        return this.categoriesByCoreFile[coreFileId];
                    })
                );
        }
    }

    getQuickStarts(coreFileId: number): Observable<DataQueryQuickStart[]> {
        if (this.quickStarts[coreFileId]) {
            return of(this.quickStarts[coreFileId]);
        } else {
            const queryString = '?coreFileId=' + coreFileId;
            return this.http.get<DataQueryQuickStart[]>(System.apiURL.Classroom + 'GetDataQueryQuickStarts' + queryString)
                .pipe(
                    take(1),
                    map(quickStarts => {
                        this.quickStarts[coreFileId] = quickStarts;
                        return this.quickStarts[coreFileId];
                    })
                );
        }
    }

    getCoreFileFilters(coreFileId: number): Observable<DataQueryCoreFileFilter[]> {
        if (this.coreFileFilters[coreFileId]) {
            return of(this.coreFileFilters[coreFileId]);
        } else {
            const queryString = '?coreFileId=' + coreFileId;
            return this.http.get<DataQueryCoreFileFilter[]>(System.apiURL.Classroom + 'GetDataQueryCoreFileFilters' + queryString)
                .pipe(
                    take(1),
                    map(coreFileFilters => {
                        this.coreFileFilters[coreFileId] = coreFileFilters;
                        return this.coreFileFilters[coreFileId];
                    })
                );
        }
    }

    getCategoriesByRelatedCategory(categoryId: number): Observable<DataQueryCategory[]> {
        const queryString = '?siteId=' + this.yearService.defaultYear.SchoolID
            + '&yearId=' + this.yearService.defaultYear.Id
            + '&categoryId=' + categoryId;
        return this.http.get<DataQueryCategory[]>(System.apiURL.Classroom + 'GetDataQueryCategoriesByRelatedCategory' + queryString)
            .pipe(take(1));
    }

    getFieldsByRelatedCategory(categoryId: number): Observable<DataQueryField[]> {
        const queryString = '?siteId=' + this.yearService.defaultYear.SchoolID
            + '&yearId=' + this.yearService.defaultYear.Id
            + '&categoryId=' + categoryId;
        return this.http.get<DataQueryField[]>(System.apiURL.Classroom + 'GetDataQueryFieldsByRelatedCategory' + queryString)
            .pipe(
                take(1),
                map(fields => {
                    for (let i = 0; i < fields.length; i++) {
                        fields[i] = System.convert(fields[i], new DataQueryField());

                        // We set these properties because they need to exist on the field
                        fields[i].Filters = [];
                        fields[i].FunctionText = '';
                        fields[i].TransformationFields = [];
                    }
                    return fields;
                })
            );
    }

    ////////// Utility Methods //////////
    postRequest<T>(urlExtension, queryString, data): Observable<T> {
        // PRE: a url extension to send the request to, the query string, and the data to send
        // POST: performs the request and ensures there there is no circular data
        // NOTE: this is intended to be used on any post request where you send fields or queries
        // 	 	 This was decided between the trade-off of bi-directional references or manual searching

        // Reorganize utility function
        const organize = () => {
            this.information.organize();
            this.currentQuery.organize(this.information, this.loginService.LoginSettings.V10);
            if (data instanceof DataQuery) {
                data.organize(this.information, this.loginService.LoginSettings.V10);
            }
        };

        // Checking for organization
        let reorganize = false;
        if (this.cachedInformation) {
            if (this.information.organized) {
                this.information.disorganize();
                reorganize = true;
            }
            if (this.currentQuery.organized) {
                this.currentQuery.disorganize();
                reorganize = true;
            }
            if (data instanceof DataQuery && data.organized) {
                data.disorganize();
                reorganize = true;
            }
        }
        return this.http.post<T>(System.apiURL.Classroom + urlExtension + queryString, data).pipe(
            map(results => {
                if (reorganize) {
                    organize();
                }
                return results;
            }),
            catchError(err => {
                if (reorganize) {
                    organize();
                }
                console.log('Error');
                console.log(err);
                throw (err);
            })
        );
    }

    checkDeactivationAndChange(value: DataQuery): Observable<boolean> {
        // POST: checks if the query has unsaved changes, then changes if the dialog allows
        // NOTE: this should be the only way the current query changes outside of the service and synchronization
        // NOTE: this also doesn't do ANYTHING if another instance of this hasn't resolved
        const queryChanged = new ReplaySubject<boolean>(1);
        if (value != this.currentQuery && !this.blocking) {
            this.blocking = true;
            if (value == null) {
                value = new DataQuery();
            }
            const canDeactivate = this.showWarning();
            if (canDeactivate instanceof Observable) {
                const subscription = canDeactivate.subscribe(result => {
                    if (subscription) { // Sometimes, this is undefined ???
                        subscription.unsubscribe();
                    }
                    if (result) {
                        this.currentQuery = value;
                    }
                    queryChanged.next(result);
                    this.blocking = false;
                });
            } else {
                if (canDeactivate) {
                    this.currentQuery = value;
                    queryChanged.next(true);
                } else {
                    queryChanged.next(false);
                }
                this.blocking = false;
            }
        }
        return queryChanged.asObservable();
    }

    showWarning(): Observable<boolean> | Promise<boolean> | boolean {
        // PRE: the user tries to perform an action which may need to be blocked
        // POST: Shows the warning dialog. Then, performs the following functions:
        // 		If 2, saves the query and continues and returns true
        // 		If 1, saves the query as a new query and returns true
        // 		If 0, continues without saving the query and returns true
        // 		If -1, returns false
        if (
            this.currentQuery.queryChanged
            && this.unsavedChanges == false
            && (
                this.permissions.canUserCreateDistrictQuery
                || this.permissions.canUserCreateSchoolQuery
                || this.permissions.canUserCreatePrivateQuery
            )
        ) {
            const dialogBox = this.dialog.open(DataQueryUnsavedChangesDialogComponent, {
                disableClose: true,
                data: {
                    userCanOverwrite: this.checkUserCanOverwriteQuery(),
                    currentQuery: this.currentQuery,
                    unsavedChanges: false
                }
            });
            return dialogBox.afterClosed().pipe(
                map(result => {
                    switch (result) {

                        // Save query
                        case 2:
                            if (this.currentQuery.QueryName != undefined) {
                                this.currentQuery.QueryXML = this.getQueryXML(this.getAllFields());
                                this.saveQuery(
                                    this.currentQuery,
                                    (results) => {
                                        const returnMessage = results[0];
                                        if (returnMessage == 'Success') {
                                            this.currentQuery.ID = parseInt(results[1], 10);
                                            this.currentQuery.queryChanged = false;
                                            this.currentQuery.queryChangedSinceLastResult = false;
                                        } else {
                                            this.dialog.open(WarningDialogComponent, {
                                                disableClose: true,
                                                data: {
                                                    query: this.currentQuery,
                                                    warningMessage: results[0],
                                                    allowOverwrite: false
                                                }
                                            });
                                        }
                                    }
                                );
                            } else {

                                // If it was an existing query, open it for editing
                                if (this.currentQuery.ID > 0) {
                                    this.openAddEditQuery(
                                        this.currentQuery,
                                        true,
                                        false
                                    );
                                }

                                // If it was not an existing query, open it for adding
                                else {
                                    this.openAddEditQuery(
                                        this.currentQuery,
                                        false,
                                        false,
                                        (returnedQuery: DataQuery) => {
                                            if (returnedQuery) {
                                                Object.assign(this.currentQuery, returnedQuery);
                                                this.updateRecentQueries(this.currentQuery).pipe(take(1)).subscribe();
                                            }
                                        }
                                    );
                                }
                            }
                            return true;

                        // Save query as new
                        case 1:
                            const savedQuery = cloneDeep(this.currentQuery);
                            this.openAddEditQuery(
                                savedQuery,
                                false,
                                false,
                                (returnedQuery: DataQuery) => {
                                    if (returnedQuery) {

                                        // If it was a new query, set that to the returned query and update the recents with the current
                                        if (this.currentQuery.ID <= 0) {
                                            Object.assign(this.currentQuery, returnedQuery);
                                            this.updateRecentQueries(this.currentQuery).pipe(take(1)).subscribe();
                                        }

                                        // If it was an existing query, set the current to the new query and update the recents with the new
                                        else {
                                            this.currentQuery = returnedQuery;
                                        }
                                    }
                                }
                            );
                            return true;

                        // Continue
                        case 0:
                            return true;

                        // Cancel
                        case -1:
                            return false;
                        default:
                            return false;
                    }
                })
            );
        } else if (this.loginService.LoginSettings.V10 == false && this.unsavedChanges == true) {

            const dialogBox = this.dialog.open(DataQueryUnsavedChangesDialogComponent, {
                disableClose: true,
                data: {
                    userCanOverwrite: false,
                    currentQuery: this.currentQuery,
                    unsavedChanges: true
                }
            });
            return dialogBox.afterClosed().pipe(
                map(result => {
                    switch (result) {

                        // Save query
                        case 2:
                            this.observableSaveResult.next(true);
                            this.unsavedChanges = false;
                            return true;

                        // Save query as new
                        case 1:
                            this.unsavedChanges = false;
                            return true;

                        // Continue
                        case 0:
                            this.unsavedChanges = false;
                            return true;

                        // Cancel
                        case -1:
                            return false;
                        default:
                            return false;
                    }
                })
            );
        } else {
            return true;
        }
    }

    openAddEditQuery(query = new DataQuery(), forEdit = false, forImport = false, callback?: Function) {
        // POST: opens the add-edit query dialog with the right data
        const dialogBox = this.dialog.open(AddEditQueryDialogComponent, {
            disableClose: true,
            data: {
                query: query,
                forEdit: forEdit,
                forImport: forImport,
                dataQueryService: this
            }
        });
        const subscription = dialogBox.afterClosed().subscribe(returnedQuery => {
            subscription.unsubscribe();
            if (callback) {
                callback(returnedQuery);
            }
        });
    }

    checkUserCanOverwriteQuery() {
        // POST: returns true if the user can overwrite this query
        if (!this.currentQuery) {
            return false;
        } else {
            if (this.currentQuery.OwnerID == this.userService.currentUser.Id) {
                if (this.currentQuery.Scope == 'Private') {
                    return this.permissions.canUserCreatePrivateQuery;
                } else if (this.currentQuery.Scope == 'Selected Sites') {
                    return this.permissions.canUserCreateSchoolQuery;
                } else if (this.currentQuery.Scope == 'District') {
                    return this.permissions.canUserCreateDistrictQuery;
                } else {
                    return false;
                }
            } else {
                return false;
            }
        }
    }

    loadQuery(query: DataQuery) {
        // POST: loads a query
        if (this.isStudentSearch) {
            this._currentStudentSearchQuery = query;
        } else {
            this._currentDataQueryQuery = query;
        }

        // Mark as loading
        this.loadingQuery = true;

        // Setting the initial settings
        if (query.ID >= 0) {
            if (this.isStaffSearch) {
                query.StaffSearch = true;
            }
            if (this.isCourseSearch) {
                query.CourseSearch = true;
            }
            if (this.isStudentSearch) {
                query.StudentSearch = true;
            }
            const updateSubscription = this.updateRecentQueries(query).subscribe(() => {
                updateSubscription.unsubscribe();
            });
        }

        // Loading the query
        if (query && this.permissions) {
            this.loadDataFromXML(query, this.importQueryClick);
            this.importQueryClick = false;
        }

        // Unmark as loading
        this.loadingQuery = false;
        query.xmlLoaded = true;

        // Setting the current query to its original state if it has unsaved changes
        if (this.currentQuery.queryChanged && this.originalQuery.ID == this.currentQuery.ID) {
            Object.assign(this.currentQuery, this.originalQuery);
        }

        // Maintaining the original data
        Object.assign(this.originalQuery, this.currentQuery);

        // Setting the current query with the now-loaded data
        this.observableCurrentQuery.next(this.currentQuery);
    }

    loadDataFromXML(query: DataQuery, importQueryClick?: boolean) {
        // POST: obtains the query object from the query's XML

        // Checking the XML exists
        if (!query.QueryXML) {
            return;
        }
        const xml = query.QueryXML;
        query.xmlLoaded = true;

        // Loading the query
        this.getObjectFromXML(xml, query, importQueryClick);
    }

    getObjectFromXML(xml, query?: DataQuery, importQueryClick?: boolean): DataQuery {
        // POST: returns a query object from an xml string

        // Creating query object
        if (!query) {
            query = new DataQuery();
        } else {
            query.reportFields = [];
            query.filterFields = [];
            query.sortFields = [];
            query.relationships = [];
        }

        // Loading the query XML
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xml, 'text/xml');
        const distinctNode = xmlDoc.getElementsByTagName('Distinct')[0];
        if (distinctNode && distinctNode.childNodes.length > 0) {
            query.distinct = distinctNode.childNodes[0].nodeValue == 'true';
        } else {
            query.distinct = false;
        }
        const currentYearOnlyNode = xmlDoc.getElementsByTagName('CurrentYearOnly')[0];
        if (currentYearOnlyNode && currentYearOnlyNode.childNodes.length > 0) {
            query.currentYearOnly = currentYearOnlyNode.childNodes[0].nodeValue == 'true';
        } else {
            query.currentYearOnly = true;
        }
        if (xmlDoc.getElementsByTagName('CurrentSchoolOnly').length > 0 && xmlDoc.getElementsByTagName('CurrentSchoolOnly')[0].childNodes.length > 0) {
            query.currentSchoolOnly = xmlDoc.getElementsByTagName('CurrentSchoolOnly')[0].childNodes[0].nodeValue != 'false';
        } else {
            query.currentSchoolOnly = true;
        }
        if (xmlDoc.getElementsByTagName('CurrentlyActiveStudentsOnly') != undefined && xmlDoc.getElementsByTagName('CurrentlyActiveStudentsOnly').length > 0 && xmlDoc.getElementsByTagName('CurrentlyActiveStudentsOnly')[0].childNodes.length > 0) {
            query.currentlyActiveStudentsOnly = xmlDoc.getElementsByTagName('CurrentlyActiveStudentsOnly')[0].childNodes[0].nodeValue != 'false';
        } else {
            query.currentlyActiveStudentsOnly = true;
        }

        if (this.V10 == false) {
            query.currentSchoolOnly = true;
            query.currentYearOnly = true;
        }

        this.distinct = query.distinct;
        this.currentYearOnly = query.currentYearOnly;
        this.currentSchoolOnly = query.currentSchoolOnly;
        this.currentlyActiveStudentsOnly = query.currentlyActiveStudentsOnly;


        // Getting query header information
        if (xmlDoc.getElementsByTagName('CourseName').length > 0 && xmlDoc.getElementsByTagName('CourseName')[0].childNodes.length > 0 && xmlDoc.getElementsByTagName('SiteSectionID').length > 0 && xmlDoc.getElementsByTagName('SiteSectionID')[0].childNodes.length > 0) {
            const courseNameNode = xmlDoc.getElementsByTagName('CourseName')[0];
            if (courseNameNode && courseNameNode.childNodes.length > 0) {
                query.courseName = System.xmlToText(courseNameNode.childNodes[0].nodeValue);
            } else {
                query.courseName = '';
            }
            // this.siteSectionId = parseInt(xmlDoc.getElementsByTagName("SiteSectionID")[0].childNodes[0].nodeValue, 10);
            const siteSectionNode = xmlDoc.getElementsByTagName('SiteSectionID')[0];
            if (siteSectionNode && siteSectionNode.childNodes.length > 0) {
                query.siteSectionId = System.xmlToText(siteSectionNode.childNodes[0].nodeValue);
            } else {
                query.siteSectionId = 0;
            }
            if (typeof query.siteSectionId == 'string' && query.siteSectionId.length == 0) {
                query.siteSectionId = 0;
            }
        } else {
            query.courseName = '';
            query.siteSectionId = 0;
        }
        if (!this.permissions || !this.permissions.canUserQueryAllYears) {
            query.currentYearOnly = true;
        }

        // Obtaining the objects from the xml, starting at the fields
        const fieldNodes = xmlDoc.getElementsByTagName('Field');
        for (let i = 0; i < fieldNodes.length; i++) {

            // Getting base values
            let fieldName = '';
            let categoryName = '';
            let objectName = '';
            let object: DataQueryObject = null;
            let category: DataQueryCategory = null;
            let field: DataQueryField = null;

            // Helper Function
            const getNodeValue = (nodeName: string, dataType?: string): (string | number | boolean) => {
                const nodes = fieldNodes[i].getElementsByTagName(nodeName);
                if (nodes.length > 0 && nodes[0].childNodes.length > 0) {
                    let value = '';
                    if (nodes[0].childNodes[0].nodeName.includes('text')) {
                        value = System.xmlToText(nodes[0].childNodes[0].nodeValue);
                    } else {
                        value = System.xmlToText(nodes[0].childNodes[0]['outerHTML']) as string;
                    }
                    switch (dataType) {
                        case 'number':
                            return parseInt(value, 10);
                        case 'boolean':
                            return value == 'true';
                        default:
                            return value && value != 'undefined' && value != 'null' ? value : '';
                    }
                } else {
                    return null;
                }
            };
            const processNode = (nodeName: string, dataType?: string) => {
                field[nodeName] = getNodeValue(nodeName, dataType);
            };

            // Setting values
            const fieldID = getNodeValue('FieldID', 'number') as number;
            const catID = getNodeValue('CategoryID', 'number') as number;
            const fieldCategory = this.information.getField(f => f.ID == fieldID && f.CategoryID == catID);
            fieldName = getNodeValue('FieldName') as string;
            if (!fieldName) {
                fieldName = '';
            }
            categoryName = getNodeValue('CategoryName') as string;
            if (!this.loginService.LoginSettings.V10 && fieldCategory != null && categoryName != null && (categoryName == 'null' || categoryName == 'undefined' || categoryName.trim() == '')) {
                categoryName = fieldCategory.CategoryName;
            }
            if (!categoryName) {
                categoryName = '';
            }
            objectName = getNodeValue('ObjectName') as string;
            if (!objectName) {
                objectName = '';
            }

            // Finding the appropriate field and setting it's names
            object = this.information.objects.find(o => o.ObjectName == objectName);
            if (object != null) {
                category = this.information.categories.find(c => c.CategoryName == categoryName && c.ObjectID == object.ID);
                if (category == null) {
                    category = this.information.categories.find(c =>
                        c.ID == catID
                        && c.ObjectID == object.ID
                        && c.fields
                        && c.fields.find(f =>
                            f.ID == fieldID
                        )
                    );
                }
            }
            if (category != null) {
                field = this.information.getField(f => f.FieldName == fieldName && f.CategoryID == category.ID);
            }
            if (field != null) {
                field.CategoryName = category.CategoryName;
                field.ObjectName = object.ObjectName;
            } else {

                // If we haven't found a field yet, try from the bottom up
                field = this.information.getField(f => f.ID == fieldID && f.CategoryID == catID);
                category = this.information.categories.find(c => c.ID == catID);
                if (!this.loginService.LoginSettings.V10 && field != null) {
                    category = this.information.categories.find(c => c.ID == catID && c.CategoryName == field.CategoryName);
                }

                // If we have a category and a field, set up the object
                if (field != null && category != null) {
                    field.CategoryName = category.CategoryName;
                    const obj = this.information.objects.find(o => o.ID == category.ObjectID);
                    if (obj != null) {
                        field.ObjectName = obj.ObjectName;
                    }
                }

                // If the names don't match, create a new data query field with the right properties
                if (field == null || (
                    fieldName != null && fieldName != '' &&
                    field.FieldName != fieldName
                ) || (
                        category != null &&
                        categoryName != null && categoryName != '' &&
                        category.CategoryName != categoryName
                    )) {

                    // Already set data
                    field = new DataQueryField();
                    field.FieldName = fieldName;
                    field.CategoryName = categoryName;
                    field.ObjectName = objectName;
                    field.CategoryID = catID;
                    field.ID = fieldID;
                    if (importQueryClick && fieldName != '') {
                        field.IsCalculated = true;
                    }
                }
            }

            // Getting data from the XML
            if (field.DisplayName) {
                field.Alias = field.DisplayName;
            }
            processNode('SortDirection');
            processNode('SortOrder', 'number');
            processNode('Break');
            processNode('Alias');
            processNode('ShowOnReport', 'boolean');
            processNode('IsConstant', 'boolean');
            processNode('ConstantValue');
            if (importQueryClick != true || field.IsCalculated == undefined || field.IsCalculated == null) {
                processNode('IsCalculated', 'boolean');
            }
            processNode('DataType');
            processNode('IsDefaultFilterField', 'boolean');
            processNode('IsDuplicate', 'boolean');
            processNode('TransformationFunction');

            // We perform old operations if the transformation function isn't an XML
            const transFunctionNodes = fieldNodes[i].getElementsByTagName('TransformationFunction');
            let transFunctionIsXML = transFunctionNodes.length > 0 && transFunctionNodes[0].children.length > 0;

            // If we don't have a transformation function, just create a default one
            if (
                !field.TransformationFunction
                && field
                && category
            ) {
                const fieldNode = document.createElement('Field');
                fieldNode.setAttribute('Id', field.ID.toString());
                fieldNode.setAttribute('FieldName', field.FieldName.toString());
                fieldNode.setAttribute('CategoryId', field.CategoryID.toString());  // We have to supply this because V9 has id collisions...
                fieldNode.setAttribute('CategoryName', category.CategoryName.toString());  // We have to supply this because V9 has id collisions...
                fieldNode.setAttribute('ObjectId', category.ObjectID.toString());
                fieldNode.setAttribute('ObjectName', field.ObjectName.toString());
                field.TransformationFunction = fieldNode.outerHTML;
                transFunctionIsXML = true;
            }

            // Setting up the initial function text
            if (transFunctionIsXML) {
                field.FunctionText = this.transFunctionInterpreter(field.TransformationFunction);
            } else {
                field.FunctionText = field.TransformationFunction;
            }
            if (field.FunctionText == null || field.FunctionText == 'undefined') {
                field.FunctionText = '';
            }
            if (field.FunctionText.match(/\[.+\]/)) {
                field.IsCalculated = false; // We do this because some fields are erroneously "IsCalculated" and they can't be if they have a field
            }
            // Below code is not doing anything
            // if (field.DisplayName == DataQueryFieldFilter.courseFilterDisplayName && typeof query.siteSectionId == 'string' && query.siteSectionId.toString().length > 0) {
            //     const sSIds = query.siteSectionId.toString().split(',');
            //     for (let x = 0; x < sSIds.length; x++) {
            //         const tmpCourse = this.classroomSummaryService.myClasses.find(c => c.Id == parseInt(sSIds[x], 10));
            //         if (tmpCourse == null) {
            //             // TODO: tmpCourse = _.findWhere(this.classroomSummaryService.allTermClasses, { Id: parseInt(sSIds[x]) });
            //         }
            //         if (tmpCourse != null) {
            //             // TODO: this.classroomSummaryService.rosterFilteredClasses.push(tmpCourse);
            //         }
            //     }
            // }

            // Setting the fields transformation function
            field.TransformationFields = [];
            if (field.TransformationFunction) {

                // ONE-10973: We now use XML, so we only use the old process if we find no XML children
                if (!transFunctionIsXML) {

                    // Getting the trans fields
                    const transFields = [];
                    const transNodes = fieldNodes[i].getElementsByTagName('TransformationField');
                    for (let t = 0; t < transNodes.length; t++) {
                        const transField = {
                            fieldId: -1,
                            categoryId: -1,
                            position: 0,
                            sql: ''
                        };
                        let tf: DataQueryField = null;
                        let transFieldName = '';
                        let transCategoryName = '';
                        let transObjectName = '';
                        let categoryFromName: DataQueryCategory = null;
                        let objectFromName: DataQueryObject = null;
                        if (transNodes[t].getElementsByTagName('FieldName').length > 0 && transNodes[t].getElementsByTagName('FieldName')[0].childNodes.length > 0) {
                            transFieldName = System.xmlToText(transNodes[t].getElementsByTagName('FieldName')[0].childNodes[0].nodeValue);
                        }
                        if (transNodes[t].getElementsByTagName('CategoryName').length > 0 && transNodes[t].getElementsByTagName('CategoryName')[0].childNodes.length > 0) {
                            transCategoryName = System.xmlToText(transNodes[t].getElementsByTagName('CategoryName')[0].childNodes[0].nodeValue);
                        }
                        if (transNodes[t].getElementsByTagName('ObjectName').length > 0 && transNodes[t].getElementsByTagName('ObjectName')[0].childNodes.length > 0) {
                            transObjectName = System.xmlToText(transNodes[t].getElementsByTagName('ObjectName')[0].childNodes[0].nodeValue);
                        }
                        objectFromName = this.information.objects.find(o => o.ObjectName.toLowerCase() == transObjectName.toLowerCase());
                        if (objectFromName != null) {
                            categoryFromName = objectFromName.categories.find(c => c.CategoryName.toLowerCase() == transCategoryName.toLowerCase());
                        }
                        if (categoryFromName != null) {
                            tf = categoryFromName.fields.find(f => f.FieldName.toLowerCase() == transFieldName.toLowerCase());
                        }

                        // If the tf is null, we ought to do some processing to check for previous versions
                        if (tf == null) {
                            let transFieldId = -1;
                            let transCategoryId = -1;
                            if (transNodes[t].getElementsByTagName('FieldID')[0].childNodes.length > 0) {
                                transFieldId = (parseInt(transNodes[t].getElementsByTagName('FieldID')[0].childNodes[0].nodeValue, 10));
                                if (transNodes[t].getElementsByTagName('CategoryID').length > 0) {
                                    if (transNodes[t].getElementsByTagName('CategoryID')[0].childNodes.length > 0) {
                                        transCategoryId = (parseInt(transNodes[t].getElementsByTagName('CategoryID')[0].childNodes[0].nodeValue, 10));

                                        tf = this.information.getField(f => f.ID == transFieldId && f.CategoryID == transCategoryId);
                                        let tc = this.information.categories.find(c => c.ID == transCategoryId);
                                        if (!this.loginService.LoginSettings.V10 && tf != null) {
                                            tc = this.information.categories.find(c => c.ID == transCategoryId && c.CategoryName == tf.CategoryName);
                                        }
                                        if ((tf != null && transFieldName != '' && tf.FieldName != transFieldName) || (tc != null && transCategoryName != '' && transCategoryName != 'null' && tc.CategoryName != transCategoryName)) {
                                            tf = null;
                                        }
                                        if (tf != null && tc != null && (objectFromName == null || objectFromName == undefined || categoryFromName == null || categoryFromName == undefined)) {
                                            categoryFromName = tc;
                                            const transObject = this.information.objects.find(o => o.ID == categoryFromName.ObjectID);
                                            if (transObject != null) {
                                                objectFromName = transObject;
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        // If the transformation field isn't null, set up it's properties and add it to the list
                        // NOTE: we set the function text here because, otherwise, it won't actually match a field
                        if (tf != null) {
                            transField.fieldId = tf.ID;
                            transField.categoryId = tf.CategoryID;
                            const tfCategory = this.information.categories.find(c => c.ID == tf.CategoryID);
                            let viewName = '';
                            if (tfCategory != null) {
                                viewName = tfCategory.ViewName;
                            } else {
                                console.warn('No tfCategory for transField sql');
                            }
                            if (field.FunctionText) {
                                if (this.loginService.LoginSettings.V10) {
                                    // ONE-5786 - we shouldn't be converting the user entered text to lowercase.  Use a case-insensitive regEx instead
                                    // field.FunctionText = field.FunctionText.toLowerCase().replace('vw' + viewName.toLowerCase() + '.[' + tf.FieldName.toLowerCase() + ']', this.getTransFunctionText(tf));
                                    let searchString = 'vw' + viewName + '.[' + tf.FieldName + ']';
                                    // escape regex special characters
                                    searchString = searchString.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
                                    const regEx = new RegExp(searchString, 'ig');
                                    const replaceString = this.getTransFunctionText(tf);
                                    field.FunctionText = field.FunctionText.replace(regEx, replaceString);
                                    field.FunctionText = field.FunctionText.replace(searchString, replaceString);
                                } else {
                                    field.FunctionText = field.FunctionText.replace('vw' + viewName + '.[' + tf.FieldName + ']', '[' + tf.ObjectName + '.' + tf.CategoryName + '.' + tf.DisplayName + ']');
                                }
                            } else {
                                field.FunctionText = this.getTransFunctionText(tf);
                            }
                            transField.sql = 'vw' + viewName + '.[' + tf.FieldName + ']';
                            if (transNodes[t].getElementsByTagName('Position')[0].childNodes.length > 0) {
                                transField.position = (parseInt(transNodes[t].getElementsByTagName('Position')[0].childNodes[0].nodeValue, 10));
                            }
                            transFields.push(transField);
                        }

                        // If, after all this processing, TF is null, then we're in error
                        if (tf == null) {
                            field.FunctionText = this.getTransFunctionText(field);
                        }
                    }

                    // Adding the transformation fields to the data query field
                    for (let x = 0; x < transFields.length; x++) {
                        const tr = transFields.find(f => f.position == x);
                        if (tr != null) {
                            field.TransformationFields.push(tr);
                        }
                    }
                }
            }

            // Setting up the field's function text if not transformation function is present
            else {
                field.FunctionText = this.getTransFunctionText(field);
                if (category == null) {
                    console.warn('No category for field.TransformationField sql');
                }
                field.TransformationFields.push({
                    fieldId: field.ID,
                    categoryId: field.CategoryID,
                    position: 0,
                    sql: null
                });
            }

            // ONE-12636: Always parse the function text to get the XML
            const parseResult = this.transFunctionParser(field.FunctionText);
            field.TransformationFunction = parseResult.transFunction;

            // ONE-14123: The field must always match a field within the transformation function
            if (!parseResult.fields.find(transField => transField.ID == field.ID)) {
                if (parseResult.fields.length > 0) {
                    field.ID = parseResult.fields[0].ID;
                    field.CategoryID = parseResult.fields[0].CategoryID;
                    field.CategoryName = parseResult.fields[0].CategoryName;
                    field.ObjectName = parseResult.fields[0].category.ObjectName;
                } else {
                    field.ID = -1;
                    field.CategoryID = -1;
                    field.CategoryName = '';
                    field.ObjectName = '';
                }
            }

            // Putting the field in the reports and sorts if it should be there
            if (field.ShowOnReport) {
                query.reportFields.push(cloneDeep(field));
                if (!field.IsDuplicate) {
                    query.sortFields.push(field);
                }
            }

            // Obtaining its filters
            const filters: DataQueryFieldFilter[] = [];
            const filterNodes = fieldNodes[i].getElementsByTagName('Filter');
            for (let f = 0; f < filterNodes.length; f++) {
                const filter = new DataQueryFieldFilter();
                if (filterNodes[f].getElementsByTagName('FilterID')[0].childNodes.length > 0) {
                    filter.FilterID = parseInt(filterNodes[f].getElementsByTagName('FilterID')[0].childNodes[0].nodeValue, 10);
                    DataQueryService.filterFieldId = Math.max(filter.FilterID + 1, DataQueryService.filterFieldId);
                }
                if (filterNodes[f].getElementsByTagName('Operator')[0].childNodes.length > 0) {
                    filter.Operator = System.xmlToText(filterNodes[f].getElementsByTagName('Operator')[0].childNodes[0].nodeValue);
                }
                if (filterNodes[f].getElementsByTagName('Value')[0].childNodes.length > 0) {
                    filter.Value = System.xmlToText(filterNodes[f].getElementsByTagName('Value')[0].childNodes[0].nodeValue);
                }
                if (filterNodes[f].getElementsByTagName('Value2')[0] && filterNodes[f].getElementsByTagName('Value2')[0].childNodes.length > 0) {
                    filter.Value2 = System.xmlToText(filterNodes[f].getElementsByTagName('Value2')[0].childNodes[0].nodeValue);
                }
                if (filterNodes[f].getElementsByTagName('Group')[0].childNodes.length > 0) {
                    filter.Group = System.xmlToText(filterNodes[f].getElementsByTagName('Group')[0].childNodes[0].nodeValue);
                }
                if (filterNodes[f].getElementsByTagName('UseCurrentDate1')[0].childNodes.length > 0) {
                    filter.UseCurrentDate1 = filterNodes[f].getElementsByTagName('UseCurrentDate1')[0].childNodes[0].nodeValue == 'true';
                }
                if (filterNodes[f].getElementsByTagName('UseCurrentDate2')[0].childNodes.length > 0) {
                    filter.UseCurrentDate2 = filterNodes[f].getElementsByTagName('UseCurrentDate2')[0].childNodes[0].nodeValue == 'true';
                }
                if (filterNodes[f].getElementsByTagName('UseFilterForStudentSearch').length > 0 && filterNodes[f].getElementsByTagName('UseFilterForStudentSearch')[0].childNodes.length > 0) {
                    filter.UseFilterForStudentSearch = filterNodes[f].getElementsByTagName('UseFilterForStudentSearch')[0].childNodes[0].nodeValue == 'true';
                }
                if (filterNodes[f].getElementsByTagName('IsStudentFilter').length > 0 && filterNodes[f].getElementsByTagName('IsStudentFilter')[0].childNodes.length > 0) {
                    filter.IsStudentFilter = filterNodes[f].getElementsByTagName('IsStudentFilter')[0].childNodes[0].nodeValue == 'true';
                }
                if (filterNodes[f].getElementsByTagName('StudentFilterId').length > 0 && filterNodes[f].getElementsByTagName('StudentFilterId')[0].childNodes.length > 0) {
                    filter.StudentFilterId = parseInt(filterNodes[f].getElementsByTagName('StudentFilterId')[0].childNodes[0].nodeValue, 10);
                }
                if (filterNodes[f].getElementsByTagName('UseFilterForCourseSearch').length > 0 && filterNodes[f].getElementsByTagName('UseFilterForCourseSearch')[0].childNodes.length > 0) {
                    filter.UseFilterForCourseSearch = filterNodes[f].getElementsByTagName('UseFilterForCourseSearch')[0].childNodes[0].nodeValue == 'true';
                }
                if (filterNodes[f].getElementsByTagName('UseFilterForStaffSearch').length > 0 && filterNodes[f].getElementsByTagName('UseFilterForStaffSearch')[0].childNodes.length > 0) {
                    filter.UseFilterForStaffSearch = filterNodes[f].getElementsByTagName('UseFilterForStaffSearch')[0].childNodes[0].nodeValue == 'true';
                }
                if (filterNodes[f].getElementsByTagName('IsCourseFilter').length > 0 && filterNodes[f].getElementsByTagName('IsCourseFilter')[0].childNodes.length > 0) {
                    filter.IsCourseFilter = filterNodes[f].getElementsByTagName('IsCourseFilter')[0].childNodes[0].nodeValue == 'true';
                }
                if (filterNodes[f].getElementsByTagName('CourseFilterId').length > 0 && filterNodes[f].getElementsByTagName('CourseFilterId')[0].childNodes.length > 0) {
                    filter.CourseFilterId = parseInt(filterNodes[f].getElementsByTagName('CourseFilterId')[0].childNodes[0].nodeValue, 10);
                }

                if (filterNodes[f].getElementsByTagName('ApplyToAll').length > 0 && filterNodes[f].getElementsByTagName('ApplyToAll')[0].childNodes.length > 0) {
                    filter.ApplyToAll = filterNodes[f].getElementsByTagName('ApplyToAll')[0].childNodes[0].nodeValue == 'true';
                }
                const filterOptions = [];
                const filterOptionNodes = filterNodes[f].getElementsByTagName('FilterOptions');
                for (let o = 0; o < filterOptionNodes.length; o++) {
                    const filterOption = {
                        Id: '',
                        Code: '',
                        CodeDescription: '',
                        Sequence: ''
                    };
                    if (
                        filterOptionNodes[o].getElementsByTagName('Id')[0]
                        && filterOptionNodes[o].getElementsByTagName('Id')[0].childNodes.length > 0
                    ) {
                        filterOption.Id = System.xmlToText(filterOptionNodes[o].getElementsByTagName('Id')[0].childNodes[0].nodeValue);
                    }
                    if (
                        filterOptionNodes[o].getElementsByTagName('Code')[0]
                        && filterOptionNodes[o].getElementsByTagName('Code')[0].childNodes.length > 0
                    ) {
                        filterOption.Code = System.xmlToText(filterOptionNodes[o].getElementsByTagName('Code')[0].childNodes[0].nodeValue);
                    }
                    if (
                        filterOptionNodes[o].getElementsByTagName('CodeDescription')[0]
                        && filterOptionNodes[o].getElementsByTagName('CodeDescription')[0].childNodes.length > 0
                    ) {
                        filterOption.CodeDescription = System.xmlToText(filterOptionNodes[o].getElementsByTagName('CodeDescription')[0].childNodes[0].nodeValue);
                    }
                    if (
                        filterOptionNodes[o].getElementsByTagName('Sequence')[0]
                        && filterOptionNodes[o].getElementsByTagName('Sequence')[0].childNodes.length > 0
                    ) {
                        filterOption.Sequence = System.xmlToText(filterOptionNodes[o].getElementsByTagName('Sequence')[0].childNodes[0].nodeValue);
                    }
                    filterOptions.push(filterOption);
                }
                filter.FilterOptions = filterOptions;

                // date filter for v9
                if (!this.loginService.LoginSettings.V10 && field.FilterType != undefined && field.FilterType != null && field.FilterType == 1) {
                    if (filter.Value != undefined && filter.Value != null && filter.Value != '') {
                        filter.dateValue = new Date(filter.Value.toString());
                    }
                    if (filter.Value2 != undefined && filter.Value2 != null && filter.Value2 != '') {
                        filter.dateValue2 = new Date(filter.Value2.toString());
                    }
                }

                if (
                    this.loginService.LoginSettings.V10
                    || this.isStudentSearch || filter.UseFilterForStudentSearch
                    || this.isCourseSearch || filter.UseFilterForCourseSearch
                    || this.isStaffSearch || filter.UseFilterForStaffSearch
                ) {
                    filters.push(filter);
                }

                // If the filter is using current dates, set the date values accordingly
                if (filter.UseCurrentDate1) {
                    filter.dateValue = new Date();
                    filter.Value = filter.dateValue.toLocaleDateString();
                }
                if (filter.UseCurrentDate2) {
                    filter.dateValue2 = new Date();
                    filter.Value2 = filter.dateValue2.toLocaleDateString();
                }
            }

            // Setting the filters to the field, now that it's complete
            for (let j = 0; j < filters.length; j++) {
                if (j == 0) {
                    field.Filters = [filters[0]];
                    query.filterFields.push(field);
                    filters[0].field = field;
                } else {
                    const newFilterField = cloneDeep(field);
                    newFilterField.Alias = field.Alias + ' (' + (j) + ')';
                    newFilterField.Filters = [filters[j]];
                    query.filterFields.push(newFilterField);
                    filters[j].field = newFilterField;
                }
            }
        }

        // Setting up the relationships in the query
        const relationshipNodes = xmlDoc.getElementsByTagName('Relationship');
        query.relationships = [];
        for (let r = 0; r < relationshipNodes.length; r++) {
            const relationship = {
                ID: parseInt(relationshipNodes[r].getElementsByTagName('RelationshipID')[0].childNodes[0].nodeValue, 10),
                Category1ID: parseInt(relationshipNodes[r].getElementsByTagName('Category1ID')[0].childNodes[0].nodeValue, 10),
                Category1Name: '',
                Category2ID: parseInt(relationshipNodes[r].getElementsByTagName('Category2ID')[0].childNodes[0].nodeValue, 10),
                Category2Name: '',
                Type: System.xmlToText(relationshipNodes[r].getElementsByTagName('JoinType')[0].childNodes[0].nodeValue),
                Deleted: false,
                SwapPrimaryObject: false,
                Object1Name: '',
                Object2Name: '',
                Visiable: true,
                Field1ID: undefined,	// TODO: This should be something
                Field2ID: undefined		// TODO: This should be something
            };
            const category1 = this.information.categories.find(c => c.ID == relationship.Category1ID);
            const category2 = this.information.categories.find(c => c.ID == relationship.Category2ID);
            if (category1 != undefined) {
                const object = this.information.objects.find(o => o.ID == category1.ObjectID);
                if (object != null) {
                    if (!this.loginService.LoginSettings.V10 && (object.ObjectName == 'Student User-Defined' || object.ObjectName == 'Programs and Services' || object.ObjectName == 'Assessments' || object.ObjectName == 'Assessments Best')) {
                        relationship.Category1Name = object.ObjectName;
                        relationship.Object1Name = object.ObjectName;
                    } else {
                        if (this.loginService.LoginSettings.V10) {
                            relationship.Category1Name = category1.CategoryName;
                        } else {
                            relationship.Category1Name = category1.CategoryName;
                            relationship.Object1Name = object.ObjectName;
                        }

                    }
                }
            }
            if (category2 != undefined) {
                const object = this.information.objects.find(o => o.ID == category2.ObjectID);
                if (object != null) {
                    if (!this.loginService.LoginSettings.V10 && (object.ObjectName == 'Student User-Defined' || object.ObjectName == 'Programs and Services' || object.ObjectName == 'Assessments' || object.ObjectName == 'Assessments Best')) {
                        relationship.Category2Name = object.ObjectName;
                        relationship.Object2Name = object.ObjectName;
                    } else {
                        if (this.loginService.LoginSettings.V10) {
                            relationship.Category2Name = category2.CategoryName;
                        } else {
                            relationship.Object2Name = object.ObjectName;
                            relationship.Category2Name = category2.CategoryName;
                        }
                    }
                }
            }

            //  use for display .. ONE-6879
            if ((relationship.Object1Name == relationship.Object2Name) || (query.relationships.filter(o => (o.Object1Name == relationship.Object1Name && o.Object2Name == relationship.Object2Name) || (o.Object1Name == relationship.Object2Name && o.Object2Name == relationship.Object1Name)).length > 0)) {
                relationship.Visiable = false;
            } else {
                relationship.Visiable = true;
            }

            query.relationships.push(relationship);
        }

        // Returning the query
        return query;
    }

    transFunctionParser(functionText: string): { transFunction: string, fields: DataQueryField[], errors: string[] } {
        // PRE: function text, which is pseudo-SQL
        // POST: parses the function text to create an XML string representing the transformation function
        const xml = document.createElement('TransformationFunction');
        const fields: DataQueryField[] = [];
        const errors: string[] = [];

        // If we don't have any function text, return nothing
        if (!functionText) {
            return {
                transFunction: '',
                fields: [],
                errors: []
            };
        }

        // Helper functions
        const findParenthetical = (start: number): { start: number, end: number, argEndIndexes: number[] } => {
            // PRE: the start index in our current function text
            // POST: returns the first set of matching parenthesis, as well as the indexes for its commas

            // Finding the outermost parentheses
            const parenthesisIndexPairs: { [currentParenthesis: number]: { start: number, end: number, argEndIndexes: number[] } } = {};
            let parenthesisDepth = 0;
            let isQuote = false;
            let isField = false;
            for (let i = start; i < functionText.length; i++) {
                if (!parenthesisIndexPairs[parenthesisDepth]) {
                    parenthesisIndexPairs[parenthesisDepth] = { start: -1, end: -1, argEndIndexes: [] };
                }

                // We need to maintain a escaped state
                if (functionText[i] == '"') {
                    isQuote = !isQuote;
                }
                if (!isQuote) {
                    if (functionText[i] == '[') {
                        isField = true;
                    }
                    if (functionText[i] == ']' && isField) {
                        isField = false;
                    }
                }
                if (!isField && !isQuote) {

                    // Start of parenthesis means we need to set that as a start and increment our depth
                    if (functionText[i] == '(') {
                        parenthesisIndexPairs[parenthesisDepth].start = i;
                        parenthesisDepth += 1;
                    }

                    // End of parenthesis means we need to set that as the end and decrement our depth
                    else if (functionText[i] == ')') {
                        parenthesisDepth -= 1;
                        parenthesisIndexPairs[parenthesisDepth].end = i;

                        // If our current parenthsis is 0, we've found a complete node, so it's ready for parsing
                        if (parenthesisDepth == 0) {

                            // The last parenthesis also forms a function arg
                            parenthesisIndexPairs[0].argEndIndexes.push(i);

                            // Returning the current parenthesis pair
                            return parenthesisIndexPairs[0];
                        }
                    }

                    // This parses function args for the current parenthesis
                    else if (functionText[i] == ',' && parenthesisDepth > 0) {
                        parenthesisIndexPairs[parenthesisDepth - 1].argEndIndexes.push(i);
                    }
                }
            }

            // If we made it out here, we didn't find a matching parenthesis, so log an error
            errors.push('No matching parenthesis');
            return null;
        };
        const parseParenthetical = (start: number): { documentFragment: DocumentFragment, endIndex: number } => {

            // Finding the parenthetical
            const parenthetical = findParenthetical(start);

            // parsing the parenthetical
            if (parenthetical) {
                const parseResult = this.transFunctionParser(
                    functionText.substring(
                        parenthetical.start + 1,
                        parenthetical.end
                    )
                );
                fields.push(...parseResult.fields);
                errors.push(...parseResult.errors);
                return {
                    documentFragment: document.createRange().createContextualFragment(
                        parseResult.transFunction
                    ),
                    endIndex: parenthetical.end
                };
            }

            // If no parenthetical was found, return nothing
            else {
                return null;
            }
        };
        const parseFunctionArgs = (start: number): { nodes: ChildNode[], endIndex: number } => {

            // Finding the parenthetical
            const parenthetical = findParenthetical(start);

            // Finding the distinct args, separated by columns
            if (parenthetical) {

                // Extracting the arguments
                const args: string[] = [];
                if (parenthetical.argEndIndexes) {
                    for (let i = 0; i < parenthetical.argEndIndexes.length; i++) {
                        let argStart = parenthetical.start + 1;
                        if (i > 0) {
                            argStart = parenthetical.argEndIndexes[i - 1] + 1;
                        }
                        if (argStart < parenthetical.argEndIndexes[i]) {    // Only substring valid indexes (prevents reading () as having a "" argument)
                            args.push(functionText.substring(argStart, parenthetical.argEndIndexes[i]));
                        }
                    }
                }

                // Parsing each arg text
                const argXML: ChildNode[] = [];
                args.forEach(arg => {
                    const parseResult = this.transFunctionParser(arg);
                    const documentFragment = document.createRange().createContextualFragment(parseResult.transFunction);
                    argXML.push(documentFragment.firstChild);   // We should only ever have one child
                    fields.push(...parseResult.fields);
                    errors.push(...parseResult.errors);
                });

                // Returning the elements and the end of the parenthetical
                return { nodes: argXML, endIndex: parenthetical.end };
            }

            // If no parenthetical was found, return nothing
            else {
                return null;
            }
        };

        // Reading until we find [, ], (, ), ", a number, or an operator
        let currentRead = '';
        let openBracket = false;
        let openQuote = false;
        let openNumber = false;
        for (let i = 0; i < functionText.length; i++) {

            // If we encountered a parenthesis first, our current read should be a function
            if (functionText[i] == '(' && !openBracket && !openQuote) {

                // If this isn't a function, then we should parse it as a unit
                if (currentRead == '') {
                    const parsedParenthetical = parseParenthetical(i);
                    if (parsedParenthetical) {
                        i = parsedParenthetical.endIndex;
                        parsedParenthetical.documentFragment.childNodes.forEach(node => {
                            xml.appendChild(node);
                        });
                    }

                    // If no parse could be made, then end this loop
                    else {
                        break;
                    }
                }

                // If we have a read, then this is a function
                else {
                    let functionFound = false;
                    for (let j = 0; j < this.functions.length; j++) {
                        if (this.functions[j].Name.trim().toLowerCase() == currentRead.trim().toLowerCase()) {
                            functionFound = true;

                            // Creating and appending a node for our function
                            const functionNode = document.createElement('Function');
                            functionNode.setAttribute('Id', this.functions[j].Id.toString());

                            // Parsing the args
                            const parsedFunctionArgs = parseFunctionArgs(i);
                            if (parsedFunctionArgs) {
                                i = parsedFunctionArgs.endIndex;
                                parsedFunctionArgs.nodes.forEach(node => {
                                    if (node) {
                                        functionNode.appendChild(node);
                                    } else {
                                        errors.push('Function ' + this.functions[j].DisplayName + ' has invalid arguments.');
                                    }
                                });

                                // Appending the function node to our document
                                xml.appendChild(functionNode);
                            }

                            // If no parse could be made, then end this loop
                            else {
                                break;
                            }
                        }
                    }

                    // If we haven't found a function, then we have a syntax error
                    if (!functionFound) {
                        errors.push('Function not found: ' + currentRead.trim() + '.');
                    }

                    // Resetting the current read for future elements
                    currentRead = '';
                }
            }

            // If we find a ) and we're not parsing, then that's a syntax error
            else if (functionText[i] == ')' && !openBracket && !openQuote) {
                errors.push('No matching parenthesis.');
            }

            // Quotes become string literals
            else if (functionText[i] == '"') {

                // If we just hit a quotation, we're starting a string literal
                if (!openQuote) {

                    // If we have a current read before this, then we've hit a syntax error
                    if (currentRead.trim() != '') {
                        errors.push('Unknown element: ' + currentRead + '.');
                    }

                    // Resetting the current read to get its value
                    currentRead = '';
                    openQuote = true;
                }

                // If we're in a quote, the current read becomes a string literal
                else {
                    const stringElement = document.createElement('string');
                    stringElement.innerHTML = currentRead;
                    stringElement.setAttribute('value', currentRead);
                    xml.appendChild(stringElement);

                    // Resetting the current read to get future elements
                    currentRead = '';
                    openQuote = false;
                }
            }

            // If we've encountered a [, we're looking for a field
            else if (functionText[i] == '[' && !openQuote) {

                // If we have a current read, we've hit a syntax error
                if (currentRead.trim() != '') {
                    errors.push('Unknown element: ' + currentRead + '.');
                }

                // Resetting the current read to find the field
                currentRead = '';
                openBracket = true;
            }

            // If we've encountered a ], we can begin parsing a field
            else if (functionText[i] == ']' && !openQuote) {

                // If we're not looking for a field already, then we hav a syntax error
                if (!openBracket) {
                    errors.push('No matching bracket.');
                }

                // If we are looking for a field, parse it and add that to the document
                else {

                    // Looking for the field
                    const components = currentRead.split('.');
                    if (components.length == 3) {
                        const object = this.information.objects.find(o => o.ObjectName.toLowerCase() == components[0].toLowerCase());
                        let category: DataQueryCategory;
                        let field: DataQueryField;
                        if (object) {
                            category = object.categories.find(c => c.CategoryName.toLowerCase() == components[1].toLowerCase());
                        }
                        if (category) {
                            field = category.fields.find(f => f.DisplayName == components[2]);
                            if (field == null) {
                                field = category.fields.find(f => f.DisplayName.toLowerCase() == components[2].toLowerCase());
                            }
                        }

                        // If we find a field, create a node for the field and appending that
                        if (field) {
                            const fieldNode = document.createElement('Field');
                            fieldNode.setAttribute('Id', field.ID.toString());
                            fieldNode.setAttribute('FieldName', field.FieldName.toString());
                            fieldNode.setAttribute('CategoryId', field.CategoryID.toString());  // We have to supply this because V9 has id collisions...
                            fieldNode.setAttribute('CategoryName', category.CategoryName.toString());  // We have to supply this because V9 has id collisions...
                            fieldNode.setAttribute('ObjectId', object.ID.toString());
                            fieldNode.setAttribute('ObjectName', field.ObjectName.toString());
                            xml.appendChild(fieldNode);
                            fields.push(field);
                        }

                        // If we haven't found a node, then we have a syntax error
                        else {
                            errors.push(currentRead + ' field must be chosen from the dialog.');
                        }
                    }

                    // Resetting the current read
                    currentRead = '';
                    openBracket = false;
                }
            }

            // If we encounter a number and we're not in another context, this is a number literal
            else if (
                !isNaN(Number.parseInt(functionText[i], 10))
                && !openQuote
                && !openBracket
                && !openNumber
            ) {
                openNumber = true;
                if (currentRead.trim() != '') {
                    errors.push('Unknown element: ' + currentRead + '.');
                }
                currentRead = functionText[i];
            }

            // Adding to the current read since we haven't encountered a special character
            else {
                currentRead += functionText[i];
            }

            // If we're in a number context and the next element is not a number, try to parse out a number
            if (
                openNumber
                && (
                    functionText.length - 1 == i
                    || (
                        isNaN(Number.parseInt(functionText[i + 1], 10))
                        && functionText[i + 1] != '.'
                    )
                )
            ) {

                // If the current read is not a number, this is a syntax error
                if (Number.parseFloat(currentRead) != NaN) {

                    // Adding the number element
                    const numberElement = document.createElement('number');
                    numberElement.innerHTML = currentRead;
                    xml.appendChild(numberElement);
                } else {
                    errors.push('Number literal not completed: ' + currentRead + '.');
                }

                // Resetting the number context
                openNumber = false;
                currentRead = '';
            }

            // If we've encountered an operator, everything here becomes the left and everything after becomes the right
            const matchingOperator = this.operators != null ? this.operators.find(operator => operator.Display.trim().toLowerCase() == currentRead.trim().toLowerCase()) : null;
            if (matchingOperator && !openBracket && !openQuote) {

                // Make sure we capture the largest operator, as some operators are composites (>= composed of > and =)
                let potentialComposite: DataQueryOperator = null;
                let checkText = currentRead;
                let jumpAhead = 0;
                if (i < functionText.length - 1) {
                    for (let j = i + 1; j < functionText.length; j++) {
                        if (functionText[j] != ' ') {
                            checkText += functionText[j];
                            potentialComposite = this.operators.find(operator => operator.Display == checkText.trim().toLowerCase());
                            if (potentialComposite != null) {
                                jumpAhead = j - i;
                                break;
                            }
                        } else {
                            break;
                        }
                    }
                }

                // Appending a node for operator. ALl current nodes become LHS.
                const operatorNode = document.createElement('Operator');
                operatorNode.setAttribute('Id', matchingOperator.ID.toString());
                xml.childNodes.forEach(childNode => {
                    operatorNode.appendChild(childNode);
                });
                xml.appendChild(operatorNode);

                // Appending the rest of the document as the right side
                const rightHandString = functionText.substring(i + 1 + jumpAhead, functionText.length);
                const rightHandParseResult = this.transFunctionParser(rightHandString);
                fields.push(...rightHandParseResult.fields);
                errors.push(...rightHandParseResult.errors);
                const rightSideFragment = document.createRange().createContextualFragment(rightHandParseResult.transFunction);
                rightSideFragment.childNodes.forEach(node => {
                    operatorNode.appendChild(node);
                });

                // We're done the function text now, so we can break the loop
                currentRead = '';
                break;
            }
        }

        // If we've got here with a current read, then there's a syntax error. Something wasn't parsed.
        if (currentRead.trim() != '') {
            errors.push('Unknown element: ' + currentRead + '.');
        }

        // If we have any open contexts, we know we haven't matched something
        if (openBracket) {
            errors.push('No matching bracket.');
        }
        if (openQuote) {
            errors.push('No matching quote.');
        }

        // If we hit a syntax error, throw an error in the console
        if (errors.length > 0) {
            console.warn('Syntax errors found: ' + errors.toString());
        }

        // Returning the XML, distinct fields, and distinct errors
        return {
            transFunction: xml.innerHTML,
            fields: fields.filter((field, index, self) => self.findIndex(subField => subField.ID == field.ID) == index),
            errors: errors.filter((error, index, self) => self.findIndex(subError => subError == error) == index)
        };
    }

    transFunctionInterpreter(transFunctionXML: string) {
        // PRE: XML that is formatted as a transformation function according to our functions, operators, and parameters
        //      There must be a top level element, like "root" or "transformation function" if withRoot is true
        // POST: parses the XML to create pseudo-SQL function text
        let transFunction = '';
        let invalidXML = false;

        // Creating XML doc
        const parser = new DOMParser();
        transFunctionXML = transFunctionXML.replace(/&/g, '&amp;');
        const xml = parser.parseFromString(transFunctionXML, 'text/xml');

        // Creating a helper function to parse the node
        const parseNode = (xmlNode: Element): string => {
            const childElements = Array.from(xmlNode.children);
            const nodeName = xmlNode.nodeName.toLowerCase();
            const idNode = xmlNode.attributes.getNamedItem('id');
            const fieldNameNode = xmlNode.attributes.getNamedItem('fieldname');
            const categoryIdNode = xmlNode.attributes.getNamedItem('categoryid');
            const categoryNameNode = xmlNode.attributes.getNamedItem('categoryname');
            const objectIdNode = xmlNode.attributes.getNamedItem('objectid');
            const objectNameNode = xmlNode.attributes.getNamedItem('objectname');
            let id = -1;
            let categoryId = -1;
            let objectId = -1;
            if (idNode != null && Number.parseInt(idNode.value, 10) != NaN) {
                id = Number.parseInt(idNode.value, 10);
            }
            if (categoryIdNode != null && Number.parseInt(categoryIdNode.value, 10) != NaN) {
                categoryId = Number.parseInt(categoryIdNode.value, 10);
            }
            if (objectIdNode != null && Number.parseInt(objectIdNode.value, 10) != NaN) {
                objectId = Number.parseInt(objectIdNode.value, 10);
            }

            // Operator
            if (nodeName == 'operator') {

                // Find the operator
                const operator = this.operators.find(o => o.ID == id);

                // Adding the operator and its arguments on either side
                if (operator && childElements.length == 2) {
                    return parseNode(childElements[0]) + ' ' + operator.Display + ' ' + parseNode(childElements[1]);
                } else {
                    invalidXML = true;
                }
            }

            // Function
            if (nodeName == 'function') {

                // Find the function
                const funct = this.functions.find(o => o.Id == id);

                // Adding the function and it's args
                if (funct) {
                    let result = funct.Name + '(';
                    for (let i = 0; i < childElements.length; i++) {
                        result += parseNode(childElements[i]);
                        if (i != childElements.length - 1) {
                            result += ', ';
                        }
                    }
                    result += ')';
                    return result;
                } else {
                    invalidXML = true;
                }
            }

            // Field
            if (nodeName == 'field') {

                // Finding the field
                const field = this.information.fields.find(o =>
                    (this.V10 || !fieldNameNode ? o.ID == id : o.FieldName == fieldNameNode.value)
                    && (
                        this.V10 || !categoryNameNode ?
                            (categoryId == -1 || o.CategoryID == categoryId) :
                            (o.CategoryName == categoryNameNode.value)
                    )
                    && (
                        this.V10 || !objectNameNode ?
                            true :
                            (o.ObjectName == objectNameNode.value)
                    )
                );

                // Adding the field in brackets
                if (field) {
                    if (field.category) {
                        return '['
                            + field.category.ObjectName + '.'
                            + field.category.CategoryName + '.'
                            + field.DisplayName
                            + ']';
                    }
                    else {
                        return '['
                            + field.ObjectName + '.'
                            + field.CategoryName + '.'
                            + field.DisplayName
                            + ']';
                    }
                } else {
                    invalidXML = true;
                }
            }

            // Literals
            if (nodeName == 'string') {
                let value = xmlNode.getAttribute('value');
                if (!value) {
                    value = xmlNode.innerHTML;
                }
                return '"' + (value ? value : '') + '"';
            }
            if (nodeName == 'number') {
                return xmlNode.innerHTML;
            }
        };

        // Iterating through the node's children
        // NOTE: this will probably just be the one node usually
        for (let i = 0; i < xml.children.length; i++) {
            transFunction += parseNode(xml.children.item(i));
            if (i != xml.children.length - 1) {
                transFunction += ' ';
            }
        }

        // Returning the transformation function
        return transFunction;
    }

    transFunctionChanged(field: DataQueryField, resetErrors = true, markAsChanged = true, force = true) {
        // PRE: the field's transformation function is changed
        // POST: sets the fields errors and related data
        if (!this.loadingQuery) {

            // Setting the field and query data for processing
            this.savedQueryModified = true;
            if (markAsChanged) {
                this.currentQuery.queryChangedSinceLastResult = true;
            }
            if (resetErrors) {
                field.FunctionError = [];
            }
            field.PreviewData = '';
            this.FormulasTested = false;
            if (field.FunctionText == null) {
                field.FunctionText = '';
            }
            field.TransformationFields = [];
            field.TransformationFunction = '';

            // Setting the transformation function and errors
            if (!field.FunctionText && !field.FunctionError.includes('Value is required.')) {
                field.addFunctionError('Value is required.');
            } else {
                const parseResult = this.transFunctionParser(field.FunctionText);
                field.TransformationFunction = parseResult.transFunction;
                for (let i = 0; i < parseResult.fields.length; i++) {
                    const transField = new DataQueryTransformationField(parseResult.fields[i]);
                    transField.position = i;
                    field.TransformationFields.push(transField);
                    if (i == 0) {
                        field.category = this.information.categories.find(category => category.ID == transField.categoryId);
                        if (field.category) {
                            field.ID = transField.fieldId;
                            field.CategoryID = transField.categoryId;
                            field.ObjectName = field.category.ObjectName;
                        }
                    }
                }
                for (let i = 0; i < parseResult.errors.length; i++) {
                    field.addFunctionError(parseResult.errors[i]);
                }
                field.IsCalculated = parseResult.fields.length == 0;
            }
        }
    }

    transFunctionKeydown(field: DataQueryField, event: KeyDownEvent) {
        // PRE: a field's function is input into
        // POST: opens a selection dialog if [ is input
        // 		 prevents ] from being input
        // 		 allows all others

        // Extracting important information
        const key = event.key;
        const element = event.target;
        const fnText = element.value;
        const selectedText = fnText.substring(element.selectionStart, element.selectionEnd);

        // Unsetting the preview data
        field.PreviewData = '';

        // Opening dialogs for bracket
        if (key == '[') {
            event.preventDefault();
            const dialogBox = this.dialog.open(InsertFieldDialogComponent, {
                disableClose: true,
                data: {
                    information: this.information,
                    preferences: this.preferences,
                    currentQuery: this.currentQuery
                }
            });
            const subscription = dialogBox.afterClosed().subscribe(selectedField => {
                this.insertTransField(selectedField, field, element);
                this.transFunctionChanged(field);
                subscription.unsubscribe();
            });
        } else if (key == ']') {
            event.preventDefault();
        }

        // Erasing the previous field on a backspace
        else if (key == 'Backspace') {
            if (selectedText == '') {
                let fieldArrayPosition = 0;
                if (this.isCursorOnField(field, element.selectionStart, -1)) {
                    element.selectionStart = element.selectionStart - 1;
                    if (fnText[element.selectionStart] == '[') {
                        const nextText = fnText.substring(element.selectionStart);
                        element.selectionEnd = element.selectionStart + nextText.indexOf(']') + 1;
                    } else if (fnText[element.selectionStart] == ']') {
                        const prevText = fnText.substring(0, element.selectionStart);
                        element.selectionStart = prevText.lastIndexOf('[');
                    } else {
                        const nextText = fnText.substring(element.selectionStart);
                        element.selectionEnd = element.selectionStart + nextText.indexOf(']') + 1;
                        const prevText = fnText.substring(0, element.selectionStart);
                        element.selectionStart = prevText.lastIndexOf('[');
                    }
                    for (let i = 0; i < element.selectionStart - 1; i++) {
                        if (fnText[i] == ']') {
                            fieldArrayPosition += 1;
                        }
                    }
                    field.TransformationFields.splice(fieldArrayPosition, 1);
                }
            } else {
                let fieldPosition = field.TransformationFields.length;
                for (let i = fnText.length - 1; i > element.selectionStart; i--) {
                    if (fnText[i] == ']') {
                        fieldPosition -= 1;
                        if (i <= element.selectionEnd) {
                            field.TransformationFields.splice(fieldPosition, 1);
                        }
                    }
                }
            }
        }

        // Erasing the next field on a delete
        else if (key == 'Delete') {
            if (selectedText == '') {
                let fieldArrayPosition = 0;
                if (this.isCursorOnField(field, element.selectionStart, 0)) {
                    if (fnText[element.selectionStart] == '[') {
                        const nextText = fnText.substring(element.selectionStart);
                        element.selectionEnd = element.selectionStart + nextText.indexOf(']') + 1;
                    } else if (fnText[element.selectionStart] == ']') {
                        const prevText = fnText.substring(0, element.selectionStart);
                        element.selectionStart = prevText.lastIndexOf('[');
                    } else {
                        const nextText = fnText.substring(element.selectionStart);
                        element.selectionEnd = element.selectionStart + nextText.indexOf(']') + 1;
                        const prevText = fnText.substring(0, element.selectionStart);
                        element.selectionStart = prevText.lastIndexOf('[');
                    }
                    for (let i = 0; i < element.selectionStart - 1; i++) {
                        if (fnText[i] == ']') {
                            fieldArrayPosition += 1;
                        }
                    }
                    field.TransformationFields.splice(fieldArrayPosition, 1);
                }
            } else {
                let fieldPosition = field.TransformationFields.length;
                for (let i = fnText.length - 1; i > element.selectionStart; i--) {
                    if (fnText[i] == ']') {
                        fieldPosition -= 1;
                        if (i <= element.selectionEnd) {
                            field.TransformationFields.splice(fieldPosition, 1);
                        }
                    }
                }
            }
        }

        // Special keys
        else if (!Number.isNaN(Number.parseInt(key, 10)) ||
            key == ' ' ||
            /[a-z]/.test(key) ||
            key == ';' ||
            key == '=' ||
            key == ',' ||
            key == '-' ||
            key == '.' ||
            key == '/' ||
            key == '\\' ||
            key == '\'') {

            if (selectedText == '') {
                let fieldArrayPosition = 0;
                if (fnText[element.selectionStart] == '[') {

                } else if (this.isCursorOnField(field, element.selectionStart, 0)) {
                    if (fnText[element.selectionStart] == '[') {
                        const nextText = fnText.substring(element.selectionStart);
                        element.selectionEnd = element.selectionStart + nextText.indexOf(']') + 1;
                    } else if (fnText[element.selectionStart] == ']') {
                        const prevText = fnText.substring(0, element.selectionStart);
                        element.selectionStart = prevText.lastIndexOf('[');
                    } else {
                        const nextText = fnText.substring(element.selectionStart);
                        element.selectionEnd = element.selectionStart + nextText.indexOf(']') + 1;
                        const prevText = fnText.substring(0, element.selectionStart);
                        element.selectionStart = prevText.lastIndexOf('[');
                    }
                    for (let i = 0; i < element.selectionStart - 1; i++) {
                        if (fnText[i] == ']') {
                            fieldArrayPosition += 1;
                        }
                    }
                    field.TransformationFields.splice(fieldArrayPosition, 1);
                }
            } else {
                let fieldPosition = field.TransformationFields.length;
                for (let i = fnText.length - 1; i > element.selectionStart; i--) {
                    if (fnText[i] == ']') {
                        fieldPosition -= 1;
                        if (i <= element.selectionEnd) {
                            field.TransformationFields.splice(fieldPosition, 1);
                        }
                    }
                }
            }
        } else if (key == 'ArrowLeft') {
            if (this.isCursorOnField(field, element.selectionStart, -1)) {
                if (fnText[element.selectionStart - 1] == '[') {
                    const nextText = fnText.substring(element.selectionStart - 1);
                    element.selectionEnd = element.selectionStart - 1 + nextText.indexOf(']') + 1;
                } else if (fnText[element.selectionStart - 1] == ']') {
                    const prevText = fnText.substring(0, element.selectionStart - 1);
                    element.selectionStart = prevText.lastIndexOf('[');
                } else {
                    const nextText = fnText.substring(element.selectionStart - 1);
                    element.selectionEnd = element.selectionStart - 1 + nextText.indexOf(']') + 1;
                    const prevText = fnText.substring(0, element.selectionStart - 1);
                    element.selectionStart = prevText.lastIndexOf('[');
                }
            }
        } else if (key == 'ArrowRight') {
            if (this.isCursorOnField(field, element.selectionStart, 1)) {
                if (fnText[element.selectionStart + 1] == '[') {
                    const nextText = fnText.substring(element.selectionStart + 1);
                    element.selectionEnd = element.selectionStart + 1 + nextText.indexOf(']') + 1;
                } else if (fnText[element.selectionStart + 1] == ']') {
                    const prevText = fnText.substring(0, element.selectionStart + 1);
                    element.selectionStart = prevText.lastIndexOf('[');
                } else {
                    const nextText = fnText.substring(element.selectionStart + 1);
                    element.selectionEnd = element.selectionStart + 1 + nextText.indexOf(']') + 1;
                    const prevText = fnText.substring(0, element.selectionStart + 1);
                    element.selectionStart = prevText.lastIndexOf('[');
                }
            }
        }
        return true;
    }

    transFunctionClicked(field: DataQueryField, event: ClickEvent) {
        // PRE: a transformation function input field is clicked into
        // POST: highlights a field is one is clicked on
        field.IsTransErrorOpen = false;
        event.stopPropagation();
        const elem = event.target;
        if (elem != null) {
            event.preventDefault();
            const fnText = elem.value;
            let previousText = fnText.substring(0, elem.selectionStart);
            if (previousText.indexOf('[') >= 0) {
                previousText = previousText.substring(previousText.lastIndexOf('['));
                if (previousText.indexOf(']') < 0) {
                    elem.selectionStart = elem.selectionStart - previousText.length;
                }
            }
            let nextText = fnText.substring(elem.selectionEnd);
            if (nextText.indexOf(']') >= 0) {
                nextText = nextText.substring(0, nextText.indexOf(']'));
                if (nextText.indexOf('[') < 0) {
                    elem.selectionEnd = elem.selectionEnd + nextText.length + 1;
                }
            }
        }
    }

    isCursorOnField(field: DataQueryField, cursorPosition: number, modifier: number): boolean {
        // PRE: the field, a cursor position, and modifier for the cursor position
        // POST: returns true if the cursor is on the specified field
        const functionText = field.FunctionText == null ? '' : field.FunctionText;
        let isField = false;
        for (let i = 0; i < functionText.length; i++) {
            if (functionText[i] == '[') {
                isField = true;
            } else if (i > 0) {
                if (functionText[i - 1] == ']') {
                    isField = false;
                }
            }
            if (cursorPosition + modifier == i) {
                return isField;
            }
        }
        return false;
    }

    getTransFunctionText(field: DataQueryField): string {
        // PRE: the field must be organized
        // POST: obtains the text to be put in the function text
        if (field.DisplayName && field.ObjectName && field.CategoryName) {
            return '[' + field.ObjectName + '.' + field.CategoryName + '.' + field.DisplayName + ']';
        } else {
            return '';
        }
    }

    insertTransField(selectedField: DataQueryField, fieldToUpdate: DataQueryField, element?: HTMLInputElement, insertIndex?: number) {
        // PRE: a selected field from the insert field dialog, a field to update, and (optionally) the element of that field
        // POST: updates the field's function text. If an element is provided, inserts it at the cursor. Otherwise, at 0.
        let viewName = '';
        if (selectedField != null) {

            // Updating the field
            let category = selectedField.category;
            if (category == null) {
                category = this.information.categories.find(c => c.ID == selectedField.CategoryID);
            }
            if (category != null) {
                viewName = category.ViewName;
            } else {
                console.warn('No category for insertTransField');
            }
            const textToInsert = this.getTransFunctionText(selectedField);
            const sqlToInsert = 'vw' + viewName + '.[' + selectedField.FieldName + ']';
            // ONE-13145
            if (this.V10 == false) {
                fieldToUpdate.category = category;
                fieldToUpdate.ID = selectedField.ID;
                fieldToUpdate.ObjectName = selectedField.ObjectName;
                fieldToUpdate.CategoryID = selectedField.CategoryID;
            }

            const updateField = fieldToUpdate;
            const selectionStart = element ? element.selectionStart : (insertIndex ? insertIndex : 0);
            const selectionEnd = element ? element.selectionEnd : (insertIndex ? insertIndex : 0);
            if (updateField.TransformationFields == null) {
                updateField.TransformationFields = [];
            }
            let transFieldArrayPosition = cloneDeep(updateField.TransformationFields.length);
            if (selectionStart == 0) {
                transFieldArrayPosition = 0;
            }
            const trans = {
                fieldId: selectedField.ID,
                categoryId: selectedField.CategoryID,
                position: 0,
                sql: sqlToInsert
            };
            updateField.TransformationFields.splice(transFieldArrayPosition, 0, trans);
            updateField.FunctionText = updateField.FunctionText.substring(0, selectionStart) + textToInsert + updateField.FunctionText.substring(selectionEnd);

            // Updating the element
            if (element) {
                let i = 0;
                if (selectionStart != selectionEnd) {
                    let fieldPosition = updateField.TransformationFields.length;
                    for (i = updateField.FunctionText.length - 1; i > element.selectionStart; i--) {
                        if (updateField.FunctionText[i] == ']') {
                            fieldPosition -= 1;
                            if (i <= element.selectionEnd) {
                                updateField.TransformationFields.splice(fieldPosition, 1);
                            }
                        }
                    }
                }
                i = this.loginService.LoginSettings.V10 ? 0 : -1;
                while (i++ < element.selectionStart) {
                    if (updateField.FunctionText[i] == '[') {
                        transFieldArrayPosition += 1;
                    }
                }
                setTimeout(() => {
                    element.selectionStart = selectionStart + textToInsert.length;
                    element.selectionEnd = selectionStart + textToInsert.length;
                    element.focus();
                }, 0);

                if (this.V10 == false) {
                    if (updateField.Alias == null || updateField.Alias == '') {
                        updateField.Alias = selectedField.FieldName;
                    }
                    this.transFunctionChanged(updateField);
                }
            }
        }
    }

    getQueryXML(fields: DataQueryField[], forResults = false): string {
        // PRE: the fields for the query
        // POST: gets the xml of the current query based on the fields
        let xml = '';
        xml += '<root>';

        // XML Fields
        for (let i = 0; i < fields.length; i++) {
            xml += '<Field>';
            xml += '<FieldID>' + fields[i].ID + '</FieldID>';
            xml += '<FieldName>' + System.textToXML(fields[i].FieldName) + '</FieldName>';
            xml += '<CategoryID>' + fields[i].CategoryID + '</CategoryID>';
            xml += '<CategoryName>' + System.textToXML(fields[i].CategoryName) + '</CategoryName>';
            xml += '<ObjectName>' + System.textToXML(fields[i].ObjectName) + '</ObjectName>';
            xml += '<SortDirection>' + fields[i].SortDirection + '</SortDirection>';
            xml += '<SortOrder>' + fields[i].SortOrder + '</SortOrder>';
            xml += '<Break>' + (fields[i].Break ? fields[i].Break : '') + '</Break>';
            xml += '<Alias>' + System.textToXML(fields[i].Alias) + '</Alias>';
            xml += '<ShowOnReport>' + !!fields[i].ShowOnReport + '</ShowOnReport>';
            xml += '<IsConstant>' + !!fields[i].IsConstant + '</IsConstant>';
            xml += '<ConstantValue>' + System.textToXML(fields[i].ConstantValue) + '</ConstantValue>';
            xml += '<IsCalculated>' + !!fields[i].IsCalculated + '</IsCalculated>';
            xml += '<DataType>' + System.textToXML(fields[i].DataType) + '</DataType>';
            xml += '<IsDefaultFilterField>' + !!fields[i].IsDefaultFilterField + '</IsDefaultFilterField>';
            xml += '<IsDuplicate>' + !!fields[i].IsDuplicate + '</IsDuplicate>';

            // XML Filters
            if (fields[i].Filters != undefined && fields[i].Filters != null) {
                for (let x = 0; x < fields[i].Filters.length; x++) {
                    let setupFilter = false;
                    if (this.isStudentSearch) {
                        if (!forResults || (!this.V10 && fields[i].IsDefaultFilterField) || (this.V10 && !fields[i].IsDefaultFilterField) || fields[i].Filters[x].UseFilterForStudentSearch) {
                            setupFilter = true;
                        }
                    } else if (this.isCourseSearch) {
                        if (!forResults || !fields[i].IsDefaultFilterField || fields[i].Filters[x].UseFilterForCourseSearch) {
                            setupFilter = true;
                        }
                    } else if (this.isStaffSearch) {
                        if (!forResults || !fields[i].IsDefaultFilterField || fields[i].Filters[x].UseFilterForStaffSearch) {
                            setupFilter = true;
                        }
                    } else {
                        if (!forResults || !fields[i].IsDefaultFilterField || !this.V10) {
                            setupFilter = true;
                        }
                    }
                    if (setupFilter) {
                        xml += '<Filter>';
                        xml += '<FilterID>' + fields[i].Filters[x].FilterID + '</FilterID>';
                        xml += '<Operator>' + System.textToXML(fields[i].Filters[x].Operator) + '</Operator>';
                        const value: any = (fields[i].Filters[x].Value == '""' || fields[i].Filters[x].Value == '\'\'') ? '' : fields[i].Filters[x].Value;
                        const value2: any = (fields[i].Filters[x].Value2 == '""' || fields[i].Filters[x].Value2 == '\'\'') ? '' : fields[i].Filters[x].Value2;
                        if (fields[i].DataType != undefined && (fields[i].DataType.toLowerCase() == 'datetime' || fields[i].DataType.toLowerCase() == 'smalldatetime')) {
                            // if (value) {
                            //     value = System.convertDateToUTC(value);
                            //     value = (value.getMonth() + 1) + '/' + value.getDate() + '/' + value.getFullYear();
                            // }
                            // if (value2) {
                            //     value2 = System.convertDateToUTC(value2);
                            //     value2 = (value2.getMonth() + 1) + '/' + value2.getDate() + '/' + value2.getFullYear();
                            // }
                        }
                        xml += '<Value>' + System.textToXML(value) + '</Value>';
                        xml += '<Value2>' + System.textToXML(value2) + '</Value2>';
                        xml += '<Group>' + fields[i].Filters[x].Group + '</Group>';
                        xml += '<UseCurrentDate1>' + !!fields[i].Filters[x].UseCurrentDate1 + '</UseCurrentDate1>';
                        xml += '<UseCurrentDate2>' + !!fields[i].Filters[x].UseCurrentDate2 + '</UseCurrentDate2>';
                        for (let y = 0; y < fields[i].Filters[x].FilterOptions.length; y++) {
                            xml += '<FilterOptions>';
                            xml += '<Id>' + fields[i].Filters[x].FilterOptions[y].Id + '</Id>';
                            xml += '<Code>' + fields[i].Filters[x].FilterOptions[y].Code + '</Code>';
                            xml += '<CodeDescription>' + System.textToXML(fields[i].Filters[x].FilterOptions[y].CodeDescription) + '</CodeDescription>';
                            xml += '<Sequence>' + fields[i].Filters[x].FilterOptions[y].Sequence + '</Sequence>';
                            xml += '</FilterOptions>';
                        }
                        xml += '<UseFilterForStudentSearch>' + !!fields[i].Filters[x].UseFilterForStudentSearch + '</UseFilterForStudentSearch>';
                        xml += '<IsStudentFilter>' + !!fields[i].Filters[x].IsStudentFilter + '</IsStudentFilter>';
                        xml += '<StudentFilterId>' + fields[i].Filters[x].StudentFilterId + '</StudentFilterId>';
                        xml += '<UseFilterForCourseSearch>' + !!fields[i].Filters[x].UseFilterForCourseSearch + '</UseFilterForCourseSearch>';
                        xml += '<UseFilterForStaffSearch>' + !!fields[i].Filters[x].UseFilterForStaffSearch + '</UseFilterForStaffSearch>';
                        xml += '<IsCourseFilter>' + !!fields[i].Filters[x].IsCourseFilter + '</IsCourseFilter>';
                        xml += '<CourseFilterId>' + !!fields[i].Filters[x].CourseFilterId + '</CourseFilterId>';
                        xml += '<ApplyToAll>' + fields[i].Filters[x].ApplyToAll + '</ApplyToAll>';
                        xml += '</Filter>';
                    }
                }
            }
            if (!fields[i].TransformationFunction && fields[i].FunctionText) {
                this.transFunctionChanged(fields[i], false, false);
            }
            if (fields[i].TransformationFunction) {
                xml += '<TransformationFunction>' + fields[i].TransformationFunction + '</TransformationFunction>'; // Note: we deliberately don't use textToXml here because the transformation function is xml
            } else {
                xml += '<TransformationFunction></TransformationFunction>';
            }
            if (fields[i].TransformationFields) {
                for (let t = 0; t < fields[i].TransformationFields.length; t++) {
                    xml += '<TransformationField>';
                    const field = this.information.getField(f => f.ID == fields[i].TransformationFields[t].fieldId && f.CategoryID == fields[i].TransformationFields[t].categoryId);
                    const category = this.information.categories.find(c => c.ID == fields[i].TransformationFields[t].categoryId);
                    const object = this.information.objects.find(o => o.ID == category.ObjectID);
                    xml += '<FieldID>' + fields[i].TransformationFields[t].fieldId + '</FieldID>';
                    xml += '<FieldName>' + (field != undefined ? System.textToXML(field.FieldName) : System.textToXML(fields[i].FieldName)) + '</FieldName>';
                    xml += '<CategoryID>' + fields[i].TransformationFields[t].categoryId + '</CategoryID>';
                    xml += '<CategoryName>' + (this.loginService.LoginSettings.V10 && category != undefined ? category.CategoryName : (field != undefined ? System.textToXML(field.CategoryName) : System.textToXML(fields[i].CategoryName))) + '</CategoryName>';
                    xml += '<ObjectName>' + (object != undefined ? System.textToXML(object.ObjectName) : System.textToXML(fields[i].ObjectName)) + '</ObjectName>';
                    xml += '<Position>' + t + '</Position>';
                    xml += '</TransformationField>';
                }
            }
            xml += '</Field>';
        }

        // XML Relationships
        for (let r = 0; r < this.currentQuery.relationships.length; r++) {
            xml += '<Relationship>';
            xml += '<RelationshipID>' + this.currentQuery.relationships[r].ID + '</RelationshipID>';
            xml += '<Category1ID>' + this.currentQuery.relationships[r].Category1ID + '</Category1ID>';
            xml += '<Category2ID>' + this.currentQuery.relationships[r].Category2ID + '</Category2ID>';
            xml += '<JoinType>' + this.currentQuery.relationships[r].Type + '</JoinType>';
            xml += '</Relationship>';
        }

        // XML Query
        xml += '<Distinct>' + this.distinct + '</Distinct>';
        xml += '<CurrentYearOnly>' + this.currentYearOnly + '</CurrentYearOnly>';
        xml += '<CurrentSchoolOnly>' + this.currentSchoolOnly + '</CurrentSchoolOnly>';
        xml += '<CurrentlyActiveStudentsOnly>' + this.currentlyActiveStudentsOnly + '</CurrentlyActiveStudentsOnly>';
        xml += '<SiteSectionID>' + System.textToXML(this.currentQuery.siteSectionId) + '</SiteSectionID>';
        xml += '<CourseName>' + System.textToXML(this.currentQuery.courseName) + '</CourseName>';
        xml += '</root>';
        return xml;
    }

    getReportTemplateXMLData(rtXML) {
        if (rtXML == undefined || rtXML == null || rtXML.trim() == '') {
            return '';
        }
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(rtXML, 'text/xml');
        let xml = '';
        xml += '<reportTemplate>';
        xml += '<Name>' + xmlDoc.getElementsByTagName('Name')[0].childNodes[0].nodeValue + '</Name>';
        xml += '<Template>' + xmlDoc.getElementsByTagName('Template')[0].childNodes[0].nodeValue + '</Template>';
        xml += '</reportTemplate>';
        return xml;
    }

    checkDisabledField(field: DataQueryField): boolean {
        // PRE: a field to check for disabled
        // POST: returns if the field should be disabled
        const fieldKey = field.ObjectName + '-' + field.CategoryID + '-' + field.ID;

        // Checking cache
        if (this.disabledFields[fieldKey] != undefined) {
            return this.disabledFields[fieldKey];
        }

        // Creating helper functions
        const checkDisabledByCode = (...args): boolean => {
            for (let i = 0; i < args.length; i++) {
                if (this.userPermissions.find(permission => permission.PermissionCode == args[i])) {
                    return false;
                }
            }
            return true;
        };
        const checkDisabledByName = (...args): boolean => {
            for (let i = 0; i < args.length; i++) {
                if (this.userPermissions.find(permission => permission.PermissionName == args[i])) {
                    return false;
                }
            }
            return true;
        };

        // The information must be organized for any of this to work
        if (!this.information.organized) {
            this.information.organize();
        }

        // If this is V10, we merely see if the category is fine
        if (this.loginService.LoginSettings.V10) {
            if (this.userCategories) {
                let hasCategory = false;
                for (let i = 0; i < this.userCategories.length; i++) {
                    if (this.userCategories[i] == field.CategoryID) {
                        hasCategory = true;
                        break;
                    }
                }
                this.disabledFields[fieldKey] = !field.IsConstant && !field.IsCalculated && !hasCategory;
                return this.disabledFields[fieldKey];
            } else {
                return true;
            }
        }

        // If this is V9, we have to check a bunch of permissions
        else {

            // If we have permission metadata turned on, use that
            if (this.loginService.LoginSettings.EnableDataQueryPermissionMetadata) {

                // Checking dynamic permissions
                if (field.Permission) {
                    this.disabledFields[fieldKey] = checkDisabledByCode('SD_M_' + field.Permission, 'SD_V_' + field.Permission, 'SD_VASDDS', 'SD_MASDDS');
                }
                if (this.disabledFields[fieldKey]) {
                    return this.disabledFields[fieldKey];   // If we're disabled here, the metadata checks won't matter
                }

                // Checking static permissions
                if (field.ViewPermissionsFromMetadata) {

                    // Extracting permissions into permission groups
                    const permissions = field.ViewPermissionsFromMetadata.split(',').map(permission => permission.trim());
                    const permissionGroups: { [permissionGroup: string]: string[] } = {};
                    permissions.forEach(permission => {
                        let group = 'NOGROUP';
                        let permissionToAdd = permission;
                        if (permission.includes('>')) {
                            const permissionWithGrouping = permission.split('>');
                            group = permissionWithGrouping[0];
                            permissionToAdd = permissionWithGrouping[1];
                        }
                        else if (permission.includes('&gt;')) {
                            const permissionWithGrouping = permission.split('&gt;');
                            group = permissionWithGrouping[0];
                            permissionToAdd = permissionWithGrouping[1];
                        }
                        if (!permissionGroups[group]) {
                            permissionGroups[group] = [];
                        }
                        permissionGroups[group].push(permissionToAdd);
                    });

                    // Checking each permission group
                    // We need one from each group
                    Object.keys(permissionGroups).forEach(group => {
                        if (!this.disabledFields[fieldKey]) {
                            this.disabledFields[fieldKey] = checkDisabledByCode(...permissionGroups[group]);
                        }
                    });
                }
            }

            // Otherwise, use static checks
            else {

                // Performing special case object checks
                switch (field.ObjectName) {
                    case 'Staff':
                    case 'Staff User-Defined':
                        this.disabledFields[fieldKey] = checkDisabledByCode('SUI_VDSD', 'SUI_MDSD');
                        break;
                    case 'Student Data for Reporting':
                        this.disabledFields[fieldKey] = checkDisabledByName('Maintain Student Fields', 'View Student Fields');
                        break;
                    case 'Special Education':
                        this.disabledFields[fieldKey] = checkDisabledByName('View Student IEP Alert Icon');
                }
                if (this.disabledFields[fieldKey] == true) {
                    return true;
                }

                // If this isn't a staff field, perform a bunch of checks
                if (field.ObjectName != 'Staff' && field.ObjectName != 'Staff User-Defined') {

                    // Performing field checks
                    if (field.IsConstant || field.IsCalculated || field.FieldName == null || field.FieldName == undefined) {
                        this.disabledFields[fieldKey] = false;
                        return this.disabledFields[fieldKey];
                    }
                    switch (field.FieldName) {
                        case 'Student Portal ID':
                        case 'Student Portal Password':
                            this.disabledFields[fieldKey] = checkDisabledByCode('SD_VSPLIAP', 'SD_MSPLIAP');
                            break;
                        case 'LunchBalance':
                            this.disabledFields[fieldKey] = checkDisabledByCode('LA_VSLAH', 'LA_MSLAH');
                            break;
                        case 'Attd Changes':
                            this.disabledFields[fieldKey] = checkDisabledByCode('A_VAH', 'A_CAH');
                            break;
                        case '#Medical Alerts':
                            this.disabledFields[fieldKey] = checkDisabledByCode('MR_VMC', 'MR_VAMD', 'MR_CAMD');
                            break;
                        case '#Release Alerts':
                            this.disabledFields[fieldKey] = checkDisabledByCode('SD_VSRA', 'SD_ASRA');
                            break;
                        case 'SSN':
                        case 'Former SIS Id':
                            this.disabledFields[fieldKey] = checkDisabledByCode('GO_VSSSN');
                            break;
                    }
                    if (field.FieldName.includes('LunchStatus') || field.FieldName.includes('LunchEffectiveDate')) {
                        this.disabledFields[fieldKey] = checkDisabledByCode('GO_VSFRS', 'LA_MSLSP');
                    } else if (field.FieldName.includes('LunchPIN')) {
                        this.disabledFields[fieldKey] = checkDisabledByCode('LA_VSLSP', 'LA_MSLSP');
                    } else if (field.FieldName.includes('IEPStatus')) {
                        this.disabledFields[fieldKey] = checkDisabledByCode('SEIEP_VSISISH');
                    } else if (field.FieldName.includes('Locker')) {
                        this.disabledFields[fieldKey] = checkDisabledByCode('LM_VLD');
                    }
                    if (this.disabledFields[fieldKey] == true) {
                        return true;
                    }

                    // Performing category checks
                    switch (field.CategoryName) {
                        case 'Courses':
                            this.disabledFields[fieldKey] = checkDisabledByCode('SD_VSCAAS', 'SD_MSCAAS');
                            break;
                        case 'Family':
                            this.disabledFields[fieldKey] = checkDisabledByCode('SD_VFD', 'SD_MFD');
                            break;
                        case 'GPA':
                            this.disabledFields[fieldKey] = checkDisabledByCode('GR_VG', 'GR_MGHNFT');
                            break;
                        case 'Graduation':
                            this.disabledFields[fieldKey] = checkDisabledByCode('SD_VS4YP', 'SD_MS4YP');
                            break;
                        case 'Fields':
                            switch (field.ObjectName) {
                                case 'Student Schedules':
                                    this.disabledFields[fieldKey] = checkDisabledByCode('SD_VSCAAS', 'SD_MSCAAS');
                                    break;
                                case 'Teacher Schedules':
                                case 'Sections':
                                    this.disabledFields[fieldKey] = checkDisabledByCode('CD_VSCSAR', 'CD_MSSFS');
                                    break;
                            }
                            break;
                    }
                    if (this.disabledFields[fieldKey] == true) {
                        return true;
                    }

                    // Performing object checks
                    switch (field.ObjectName) {
                        case 'Assessments':
                        case 'Assessments Best':
                            this.disabledFields[fieldKey] = checkDisabledByCode('AS_VAS', 'AS_MSFIS', 'AS_DSTS') || field.Permission == 'No';
                            break;
                        case 'Courses':
                            this.disabledFields[fieldKey] = checkDisabledByCode('CD_VDSL', 'CD_MDSL');
                            break;
                        case 'Behavior':
                            if (field.CategoryName == 'Incidents' || field.CategoryName == 'Events' || field.CategoryName == 'Actions') {
                                this.disabledFields[fieldKey] = checkDisabledByCode('PD_VDSU', 'PD_VDAU');
                            }
                            break;
                        default:
                            if (field.Permission) {
                                this.disabledFields[fieldKey] = checkDisabledByCode('SD_M_' + field.Permission, 'SD_V_' + field.Permission, 'SD_VASDDS', 'SD_MASDDS');
                                break;
                            }
                    }
                    if (field.ObjectName == 'Programs and Services') {
                        this.disabledFields[fieldKey] = checkDisabledByCode('PS_VASPAS', 'PS_MASPAS', 'PS_VPAS', 'PS_MPAS');
                    }

                    if (field.ObjectName == 'Sections') {
                        if (field.FieldName != 'Teacher SSN' && field.FieldName != 'Substitute Teacher SSN') {
                            this.disabledFields[fieldKey] = checkDisabledByCode('CD_VSCSAR', 'CD_MSSFS');
                        }
                        else {
                            this.disabledFields[fieldKey] = checkDisabledByCode('GO_VFSSSN');
                        }
                    }
                    if (this.disabledFields[fieldKey] == true) {
                        return true;
                    }
                } else {
                    if (field.FieldName == 'SSN' && checkDisabledByCode('GO_VFSSSN')) {
                        this.disabledFields[fieldKey] = true;
                    }
                }
            }
        }

        // If it hasn't been set yet, set it to false
        if (this.disabledFields[fieldKey] == undefined) {
            this.disabledFields[fieldKey] = false;
        }

        // Returns that disabled field
        return this.disabledFields[fieldKey];
    }

    checkEditableField(field: DataQueryField): boolean {
        // PRE: a field to check for editability
        // POST: returns if the field should be editable
        const fieldKey = field.ObjectName + '-' + field.CategoryID + '-' + field.ID;

        // Checking cache
        if (this.editableFields[fieldKey] != undefined) {
            return this.editableFields[fieldKey];
        }

        // Creating helper functions
        const hasPermissionsByCode = (...args): boolean => {
            let ret = true;
            for (let i = 0; i < args.length; i++) {
                if (this.userPermissions.find(permission => permission.PermissionCode == args[i])) {
                    ret = false;
                }
            }
            return ret;
        };

        // The information must be organized for any of this to work
        if (!this.information.organized) {
            this.information.organize();
        }

        // V10 is never editable, so always return false for v10
        if (this.loginService.LoginSettings.V10) {
            this.editableFields[fieldKey] = false;
        }

        // If this is V9, we check the fields and possibly the permissions
        else {

            // Fields are marked as editable on the database.
            this.editableFields[fieldKey] = field.Editable;

            // If we have permission metadata turned on, also use that
            if (
                field.Editable
                && this.loginService.LoginSettings.EnableDataQueryPermissionMetadata
                && field.UpdatePermissionsFromMetadata
            ) {
                const permissions = field.UpdatePermissionsFromMetadata.split(',').map(permission => permission.trim());
                this.editableFields[fieldKey] = hasPermissionsByCode(...permissions);
            }
        }

        // If it hasn't been set yet, set it to false
        if (this.editableFields[fieldKey] == undefined) {
            this.editableFields[fieldKey] = false;
        }

        // Returns that disabled field
        return this.editableFields[fieldKey];
    }

    updateFilter(filter = null, field = null) {
        // POST: updates the filter translation of the data query

        // Checking that we have a field somewhere
        if (filter != null && field == null) {
            field = filter.field;
        }

        // Setting the filter translation
        const groups = [];
        const applyToAllGroup = [];
        this.currentQuery.filterTranslation = '';
        let groupCount = 0;
        for (let x = 0; x < this.currentQuery.filterFields.length; x++) {
            const filterField = this.currentQuery.filterFields[x];
            for (let n = 0; n < filterField.Filters.length; n++) {
                if (filterField.IsDefaultStudentSearchFilter == false
                    || filterField.Filters[n].UseFilterForStudentSearch == true
                    || filterField.Filters[n].UseFilterForCourseSearch == true
                    || filterField.Filters[n].UseFilterForStaffSearch == true
                ) {
                    if (groups[filterField.Filters[n].Group] == null && (filterField.Filters[n].ApplyToAll == null || filterField.Filters[n].ApplyToAll == false)) {
                        groups[filterField.Filters[n].Group] = [];
                    }
                    let value = (filterField.Filters[n].Value == '' ? '""' : filterField.Filters[n].Value);
                    const value2 = (filterField.Filters[n].Value2 == '' ? '""' : filterField.Filters[n].Value2);

                    if ((value != '') && (filterField.FieldName == 'StudentsListfilter' || filterField.FieldName == 'StudentsTeamfilter')) {
                        const teamListValue = filterField.Filters[n].FilterOptions.find(w => w.Id == value).Code;
                        if (teamListValue != undefined) {
                            value = teamListValue;
                        }
                    }

                    if (filterField.Filters[n].Operator != 'Between' && filterField.DisplayName != DataQueryFieldFilter.courseFilterDisplayName) {
                        filterField.Filters[n].Value2 = '';
                        filterField.Filters[n].dateValue2 = null;
                    }
                    if (filterField.Filters[n].Operator == 'Is Blank' || filterField.Filters[n].Operator == 'Is Not Blank') {
                        filterField.Filters[n].Value = '';
                        filterField.Filters[n].dateValue = null;
                    }

                    // We only perform updates to the filter translation for filters that are included
                    if (
                        (!this.isStudentSearch && !this.isCourseSearch && !this.isStaffSearch)
                        || (!this.V10 && !this.isStudentSearch && !this.isStaffSearch && !filterField.IsDefaultFilterField)
                        || (this.V10 && !filterField.IsDefaultFilterField)
                        || filterField.Filters[n].UseFilterForStudentSearch
                        || filterField.Filters[n].UseFilterForCourseSearch
                        || filterField.Filters[n].UseFilterForStaffSearch
                    ) {
                        if (filterField.Filters[n].ApplyToAll == null || filterField.Filters[n].ApplyToAll == false) {
                            groups[filterField.Filters[n].Group].push({
                                DisplayName: filterField.DisplayName,
                                Operator: filterField.Filters[n].Operator,
                                Value: value,
                                Value2: value2,
                                DataType: filterField.DataType
                            });
                        } else {
                            applyToAllGroup.push({
                                DisplayName: filterField.DisplayName,
                                Operator: filterField.Filters[n].Operator,
                                Value: value,
                                Value2: value2,
                                DataType: filterField.DataType
                            });
                        }
                    }
                }
            }
        }
        const groupIds = Object.keys(groups);
        for (let i = 0; i < groupIds.length; i++) {
            const g = groupIds[i];
            groupCount += 1;
            if (groupCount > 1) {
                this.currentQuery.filterTranslation += ' OR ';
            }
            this.currentQuery.filterTranslation += '(';
            for (let j = 0; j < groups[g].length; j++) {
                if (j > 0) {
                    this.currentQuery.filterTranslation += ' AND ';
                }

                let value = groups[g][j].Value;
                let value2 = groups[g][j].Value2;

                if (groups[g][j].DataType.toLowerCase() == 'char' || groups[g][j].DataType.toLowerCase() == 'varchar' || groups[g][j].DataType.toLowerCase() == 'datetime' || groups[g][j].DataType.toLowerCase() == 'smalldatetime') {
                    value = '\'' + groups[g][j].Value + '\'';
                    value2 = '\'' + groups[g][j].Value2 + '\'';
                }

                if (groups[g][j].Operator == 'Between') {
                    this.currentQuery.filterTranslation += groups[g][j].DisplayName + ' is between ' + value + ' and ' + value2;
                } else if (groups[g][j].Operator == 'Is Blank' || groups[g][j].Operator == 'Is Not Blank') {
                    this.currentQuery.filterTranslation += groups[g][j].DisplayName + ' ' + groups[g][j].Operator;
                } else if (groups[g][j].Operator == 'In List' || groups[g][j].Operator == 'Not In List') {
                    this.currentQuery.filterTranslation += groups[g][j].DisplayName + ' ' + groups[g][j].Operator + '(' + value + ')';
                } else {
                    this.currentQuery.filterTranslation += groups[g][j].DisplayName + ' ' + groups[g][j].Operator + ' ' + value;
                }
            }
            this.currentQuery.filterTranslation += ')';
        }
        if (applyToAllGroup.length > 0) {
            this.currentQuery.filterTranslation = '(' + this.currentQuery.filterTranslation + ')';
            if (this.currentQuery.filterTranslation.length > 0) {
                this.currentQuery.filterTranslation += ' AND ';
            }
            this.currentQuery.filterTranslation += '(';
            for (let a = 0; a < applyToAllGroup.length; a++) {
                if (a > 0) {
                    this.currentQuery.filterTranslation += ' OR ';
                }

                let value = applyToAllGroup[a].Value;
                let value2 = applyToAllGroup[a].Value2;

                if (applyToAllGroup[a].DataType.toLowerCase() == 'char' || applyToAllGroup[a].DataType.toLowerCase() == 'varchar' || applyToAllGroup[a].DataType.toLowerCase() == 'datetime' || applyToAllGroup[a].DataType.toLowerCase() == 'smalldatetime') {
                    value = '\'' + applyToAllGroup[a].Value + '\'';
                    value2 = '\'' + applyToAllGroup[a].Value2 + '\'';
                }

                if (applyToAllGroup[a].Operator == 'Between') {
                    this.currentQuery.filterTranslation += applyToAllGroup[a].DisplayName + ' is between ' + value + ' and ' + value2;
                } else if (applyToAllGroup[a].Operator == 'Is Blank' || applyToAllGroup[a].Operator == 'Is Not Blank') {
                    this.currentQuery.filterTranslation += applyToAllGroup[a].DisplayName + ' ' + applyToAllGroup[a].Operator;
                } else if (applyToAllGroup[a].Operator == 'In List' || applyToAllGroup[a].Operator == 'Not In List') {
                    this.currentQuery.filterTranslation += applyToAllGroup[a].DisplayName + ' ' + applyToAllGroup[a].Operator + '(' + value + ')';
                } else {
                    this.currentQuery.filterTranslation += applyToAllGroup[a].DisplayName + ' ' + applyToAllGroup[a].Operator + ' ' + value;
                }
            }
            this.currentQuery.filterTranslation += ')';
        }

        // Setting the errors on the filter
        if (field != null && filter != null) {
            if (field.DataType.toLowerCase() == 'int' || field.DataType.toLowerCase() == 'decimal' || field.DataType.toLowerCase() == 'float'
                || field.DataType.toLowerCase() == 'money' || field.DataType.toLowerCase() == 'bigint' || field.DataType.toLowerCase() == 'smallint'
                || field.DataType.toLowerCase() == 'tinyint' || field.DataType.toLowerCase() == 'numeric'
                || field.DataType.toLowerCase() == 'smalldatetime') {
                filter.showFilterError1 = false;
                filter.showFilterError2 = false;

                if (field.DataType.toLowerCase() == 'smalldatetime') {
                    if (this.isValidDate(filter.Value) == false) {
                        filter.showFilterError1 = true;
                    }
                    if (this.isValidDate(filter.Value2) == false && filter.Operator == 'Between') {
                        filter.showFilterError2 = true;
                    }
                } else {
                    if (isNaN(filter.Value)) {
                        filter.showFilterError1 = true;
                    }
                    if (isNaN(filter.Value2) && filter.Operator == 'Between') {
                        filter.showFilterError2 = true;
                    }
                }
            }
        }
    }

    isValidDate(d) {
        if (d != undefined && d != '') {
            if (moment(d).isValid() == false) {
                return false;
            }
            return true;
        }
        return true;
    }

    splitFunctionString(fnText) {
        const operatorList = this.operators.map(operator => operator.Display);
        let parenCount = 0;
        const result = [];
        let currentString = '';
        let isStringLiteral = false;
        let isFieldName = false;
        for (let i = 0; i < fnText.length; i++) {
            currentString += fnText[i];

            // Catching string literals
            if (fnText[i] == '"') {
                if (isStringLiteral == true && parenCount == 0) {
                    result.push(currentString.trim());
                    currentString = '';
                }
                isStringLiteral = !isStringLiteral;
            }
            if (isStringLiteral == false) {

                // Catching field names
                if (parenCount == 0) {
                    if (fnText[i] == '[') {
                        isFieldName = true;
                        if (currentString.substring(0, currentString.length - 1).trim() != '') {
                            result.push(currentString.substring(0, currentString.length - 1));
                        }
                        currentString = fnText[i];
                    } else if (fnText[i] == ']') {
                        isFieldName = false;
                        result.push(currentString);
                        currentString = '';
                    }
                }
                if (isFieldName == false) {

                    // Catching parenthesis
                    if (fnText[i] == '(') {
                        parenCount++;
                    } else if (parenCount > 0) {
                        if (fnText[i] == ')') {
                            parenCount--;
                            if (parenCount == 0) {
                                if (currentString.trim() != '') {
                                    result.push(currentString.trim());
                                }
                                currentString = '';
                            }
                        }
                    }

                    // Catching operands
                    else if (currentString.trim() != '' && this.operators.find(op => op.Display == currentString)) {
                        let operatorText = currentString;
                        let operator: DataQueryOperator;

                        // Looking ahead as well
                        let j = i;
                        let moveIndex = i;
                        while (this.operators.find(op => op.Display.includes(operatorText))) {
                            const lookAheadOperator = this.operators.find(op => op.Display == operatorText);
                            if (lookAheadOperator) {
                                operator = lookAheadOperator;
                                moveIndex = j;
                            }
                            j += 1;
                            operatorText += fnText[j];
                        }

                        // Adding the operator and moving ahead
                        result.push(operator.Display);
                        currentString = '';
                        i = moveIndex;
                    }

                    // Catching other literals
                    else if (
                        fnText[i] == ' '
                        && currentString.trim() != ''
                    ) {
                        result.push(currentString.trim());
                        currentString = '';
                    }
                }
            }

            // Catches the remaining contents of the string
            if (i + 1 == fnText.length) {
                if (currentString.trim() != '') {
                    result.push(currentString.trim());
                }
                currentString = '';
            }
        }
        return result;
    }

    addRosterFilter(rosterFilterCourses?: ClassroomSummary[], _callback?: Function, query?: DataQuery) {
        // PRE: optionally, a list of courses or a callback function
        // POST: if courses are provided, uses those to add roster filters
        // 		If no courses are provided, opens a dialog to select a number of courses.
        // 			Then, adds those courses as a filter and calls the callback.

        // If a query is not supplied, use the current query
        if (!query) {
            query = this.currentQuery;
        }

        // Utility function for handling courses
        const handleCourses = (courses: ClassroomSummary[]) => {

            // We only do anything if we have some courses
            if (courses && courses.length > 0) {

                // Obtaining the right object and category
                const studentObject = this.information.objects.find(object => object.ObjectName.toLowerCase() == 'student');
                if (studentObject) {
                    const demographicsCategory = studentObject.categories.find(category =>
                        category.CategoryName.toLowerCase() == (this.loginService.LoginSettings.V10 ? 'student demographics' : 'demographics')
                    );
                    if (demographicsCategory) {

                        // Creating a helper function to add the fields to the correct spots
                        const addStudentField = (field: DataQueryField) => {
                            if (field) {
                                field.SortDirection = 'None';
                                field.ShowOnReport = true;
                                if (query.reportFields.length > 0) {
                                    for (let i = 0; i < query.reportFields.length; i++) {
                                        const studentField = query.reportFields.find(f =>
                                            f.ID == field.ID && f.CategoryID == field.CategoryID
                                        );
                                        if (studentField == null) {
                                            query.reportFields.push(field);
                                        }
                                        if (
                                            !query.reportFields[i].TransformationFields ||
                                            query.reportFields[i].TransformationFields.length == 0
                                        ) {
                                            query.reportFields[i].FunctionText = this.getTransFunctionText(query.reportFields[i]);
                                            query.reportFields[i].TransformationFields = [
                                                {
                                                    fieldId: query.reportFields[i].ID,
                                                    categoryId: query.reportFields[i].CategoryID,
                                                    position: 0,
                                                    sql: ''
                                                }
                                            ];
                                        }
                                    }
                                } else {
                                    query.reportFields.push(field);
                                }
                                query.sortFields.push(field);
                            }
                        };

                        // Performing the additions on each student field
                        const studentFields = [];
                        studentFields.push(
                            demographicsCategory.fields.find(field =>
                                (this.loginService.LoginSettings.V10 && field.FieldName.toLowerCase() == 'studentname')
                                || (!this.loginService.LoginSettings.V10 && field.DisplayName == 'Name - Full')
                            ),
                            demographicsCategory.fields.find(field =>
                                (this.loginService.LoginSettings.V10 && field.FieldName.toLowerCase() == 'student_number')
                                || (!this.loginService.LoginSettings.V10 && field.DisplayName == 'Student #')
                            )
                        );

                        // ONE-5675
                        if ((this.loginService.LoginSettings.V10) || (!this.loginService.LoginSettings.V10 && query.reportFields.length == 0)) {
                            studentFields.forEach(studentField => {
                                if (!query.reportFields.includes(studentField)) {
                                    addStudentField(studentField);
                                }
                            });
                        }
                    }

                    // Finding the filter for roster classes
                    let courseFilterField = query.filterFields.find(field =>
                        field.DisplayName == DataQueryFieldFilter.courseFilterDisplayName && field.CategoryID == demographicsCategory.ID
                    );
                    if (courseFilterField == null) {
                        courseFilterField = this.information.getField(field =>
                            field.DisplayName == DataQueryFieldFilter.courseFilterDisplayName && field.CategoryID == demographicsCategory.ID
                        );
                        if (courseFilterField != null) {
                            courseFilterField.Visible = true;
                            courseFilterField.Filters = [];
                            query.filterFields.push(courseFilterField);
                        }
                    }

                    // If we have a filter, then set it up
                    if (courseFilterField) {
                        query.queryChanged = true;
                        let strGroup;
                        let intAscii;
                        let intTmpAscii = 64;
                        for (let l = 0; l < query.filterFields.length; l++) {
                            for (let j = 0; j < query.filterFields[l].Filters.length; j++) {
                                strGroup = query.filterFields[l].Filters[j].Group;
                                intAscii = strGroup.charCodeAt(0);
                                if (intAscii > intTmpAscii) {
                                    intTmpAscii = intAscii;
                                }
                            }
                        }

                        // Creating the filters for each course
                        for (let i = 0; i < courses.length; i++) {

                            // Setting up the course name
                            let courseName = this.userPreferencesService.generalPreferences.SelectedClassesOptionText;
                            if (courseName == null || courseName == '') {
                                courseName = this.loginService.LoginSettings.V10 == false ? 'Course Name (Course Number-Section) Meets' : 'Course Name (Course Number-Section) M: Meets';
                            }
                            courseName = courseName.replace('Course Name', courses[i].Course);
                            courseName = courseName.replace('Course Number', courses[i].SubjectCode);
                            courseName = courseName.replace('Section', courses[i].Section);
                            if (courses[i].Meets != null) {
                                courseName = courseName.replace('Meets', courses[i].Meets);
                            } else {
                                courseName = courseName.replace('M: Meets', '');
                            }

                            // Setting up the course menu text
                            courses[i].CourseMenuText = courseName;

                            // Creating the filter
                            intTmpAscii += 1;
                            const filter = new DataQueryFieldFilter();
                            filter.FilterID = DataQueryService.filterFieldId;
                            filter.Operator = '=';
                            filter.Value = courseName;
                            filter.Value2 = courses[i].Id;
                            filter.Group = String.fromCharCode(intTmpAscii);
                            filter.DisplayMenu = false;
                            filter.UseCurrentDate1 = false;
                            filter.UseCurrentDate2 = false;
                            filter.FilterOptions = [];
                            filter.UseFilterForStudentSearch = true;
                            filter.IsStudentFilter = false;
                            filter.StudentFilterId = 0;
                            filter.IsCourseFilter = false;
                            filter.CourseFilterId = 0;
                            filter.ApplyToAll = true;
                            filter.field = courseFilterField;

                            // Incrementing the filter Id
                            DataQueryService.filterFieldId = DataQueryService.filterFieldId + 1;

                            // Adding the course name so it registers as a roster query
                            query.siteSectionId = query.siteSectionId + ',' + courses[i].Id;
                            query.courseName = courseName;

                        }
                        this.originalQuery.siteSectionId = query.siteSectionId;
                        // Setting the query as changed to force reloading results and saving
                        query.queryChanged = true;

                        // Setting up the report parameters
                        this.setShowOnReportParameters();
                    }
                }
            }
        };

        // If courses are provided, use those
        if (rosterFilterCourses) {
            handleCourses(rosterFilterCourses);
        }

        // If no courses are provided, fetch them
        else {
            const dialogBox = this.dialog.open(SelectCourseDialogComponent, {
                disableClose: true,
                // hasBackdrop: false,
                panelClass: 'tyl-mat-dialog',
                data: {
                    multiselect: true,
                    headerText: 'Add Roster Filter'
                }
            });
            const subscription = dialogBox.afterClosed().subscribe(courses => {

                // Unsubscribing to prevent memory leaks
                subscription.unsubscribe();

                // Setting the roster filter on the service
                handleCourses(courses);

                // Re-establishing filter fields
                this.currentQuery.filterFields.forEach(field => {
                    field.Filters.forEach(filter => {

                        // Ensuring the filter is associated
                        filter.field = field;

                        // Setting the operator to the first viable one if one doesn't exist
                        if (!filter.Operator) {
                            const operators = filter.getFilterOperators();
                            filter.Operator = operators[0];
                        }
                    });
                });

                // Setting table data
                if (_callback) {
                    _callback(courses);
                }
            });
        }
    }

    getAllFields(query?: DataQuery, forSave = false, forSortOrder = false): DataQueryField[] {
        // PRE: the information and either a query is supplied or the current query is set
        // POST: obtains the fields for results

        // Setting the query to the current query if none is supplied
        if (!query) {
            query = this.currentQuery;
        }

        // Enforcing sort orders by index
        if (forSortOrder == true) {
            for (let i = 0; i < query.sortFields.length; i++) {
                query.sortFields[i].SortOrder = i; // Input -1 to make this identical to JS
            }
        }

        // Duplicating rFields and fFields to prevent changes from propagating outside results
        const rFields: DataQueryField[] = [];
        const fFields: DataQueryField[] = [];
        if (query != null) {

            // Setting report fields to a copy
            query.reportFields.forEach(field => {
                const object = field.object;
                const category = field.category;
                field.object = null;
                field.category = null;
                field.Filters.forEach(filter => {
                    filter.field = null;
                });

                // ONE-8014..
                if (query.sortFields != null && query.sortFields.length > 0) {
                    const sortField = query.sortFields.find(x => x.ID == field.ID);
                    if (sortField != null) {
                        field.SortDirection = sortField.SortDirection;
                        field.SortOrder = sortField.SortOrder;
                        field.Break = sortField.Break;
                    }
                }

                rFields.push(cloneDeep(field));
                field.object = object;
                field.category = category;
                if (this.loginService.LoginSettings.V10) {
                    field.Filters.forEach(filter => {
                        filter.field = field;
                    });
                }
            });

            // Setting the filter fields to a copy
            query.filterFields.forEach(field => {
                const object = field.object;
                const category = field.category;
                field.object = null;
                field.category = null;
                field.Filters.forEach(filter => {
                    filter.field = null;
                });
                fFields.push(cloneDeep(field));
                field.object = object;
                field.category = category;
                if (this.loginService.LoginSettings.V10) {
                    field.Filters.forEach(filter => {
                        filter.field = field;
                    });
                }
            });
        }

        // Getting all filters from student filter or course filter
        for (let f = 0; f < fFields.length; f++) {
            const field = fFields[f];
            const studentFilters = field.Filters.filter(filter => filter.IsStudentFilter);
            const courseFilters = field.Filters.filter(filter => filter.IsCourseFilter);
            for (let sf = 0; sf < studentFilters.length; sf++) {
                const filter = studentFilters[sf];
                if (filter.StudentFilterId != null) {
                    const filterId = parseInt(filter.StudentFilterId.toString(), 10);
                    const studentFilterQuery = this._studentFilters.find(studentFilter => studentFilter.ID == filterId);
                    if (studentFilterQuery != null) {
                        let filterGroup = 'A';
                        for (let i = field.Filters.length - 1; i >= 0; i--) {
                            if (field.Filters[i].FilterID == filter.FilterID) {
                                filterGroup = field.Filters[i].Group;
                                field.Filters.splice(i, 1);
                                fFields.splice(f, 1);
                                break;
                            }
                        }
                        const queryObj = this.getObjectFromXML(studentFilterQuery.QueryXML);
                        for (let qf = 0; qf < queryObj.filterFields.length; qf++) {
                            // NOTE: We unset the references for safety
                            for (let x = 0; x < queryObj.filterFields[qf].Filters.length; x++) {
                                queryObj.filterFields[qf].Filters[x].Group = filterGroup;
                                queryObj.filterFields[qf].Filters[x].field = null;
                            }
                            queryObj.filterFields[qf].ShowOnReport = false;
                            queryObj.filterFields[qf].object = null;
                            queryObj.filterFields[qf].category = null;
                            fFields.push(queryObj.filterFields[qf]);
                        }
                    }
                }
            }


            for (let cf = 0; cf < courseFilters.length; cf++) {
                const filter = courseFilters[cf];
                if (filter.CourseFilterId != null) {
                    const filterId = parseInt(filter.CourseFilterId.toString(), 10);
                    const courseFilterQuery = this._courseFilters.find(courseFilter => courseFilter.ID == filterId);
                    if (courseFilterQuery != null) {
                        let filterGroup = 'A';
                        for (let i = field.Filters.length - 1; i >= 0; i--) {
                            if (field.Filters[i].FilterID == filter.FilterID) {
                                filterGroup = field.Filters[i].Group;
                                field.Filters.splice(i, 1);
                                fFields.splice(cf, 1);
                                break;
                            }
                        }
                        const queryObj = this.getObjectFromXML(courseFilterQuery.QueryXML);
                        for (let qf = 0; qf < queryObj.filterFields.length; qf++) {
                            // NOTE: We unset the references for safety
                            for (let x = 0; x < queryObj.filterFields[qf].Filters.length; x++) {
                                queryObj.filterFields[qf].Filters[x].Group = filterGroup;
                                queryObj.filterFields[qf].Filters[x].field = null;
                            }
                            queryObj.filterFields[qf].ShowOnReport = false;
                            queryObj.filterFields[qf].object = null;
                            queryObj.filterFields[qf].category = null;
                            fFields.push(queryObj.filterFields[qf]);
                        }
                    }
                }
            }
        }

        // Setting the report parameters for the results
        this.setShowOnReportParameters(query);

        // We enforce transformation fields here, but the JS app sets them before this
        rFields.forEach(rField => {
            if (!rField.TransformationFields || rField.TransformationFields.length == 0 && rField.ID > 0 && rField.CategoryID > 0) {
                rField.TransformationFields = [
                    {
                        fieldId: rField.ID,
                        categoryId: rField.CategoryID,
                        position: 0,
                        sql: ''
                    }
                ];
            }
        });

        // Seeing up the report fields based on the transformation fields
        // NOTE: we don't want to do this while we are saving or we'll get some bunk fields
        if (!forSave) {
            for (let n = 0; n < rFields.length; n++) {
                if (rFields[n].TransformationFields != null) {
                    for (let t = 0; t < rFields[n].TransformationFields.length; t++) {
                        const fieldId = rFields[n].TransformationFields[t].fieldId;
                        let field = rFields.find(rField =>
                            rField.ID == rFields[n].TransformationFields[t].fieldId &&
                            rField.CategoryID == rFields[n].TransformationFields[t].categoryId
                        );
                        if (field == null) {
                            field = this.information.getField(f =>
                                f.ID == rFields[n].TransformationFields[t].fieldId &&
                                f.CategoryID == rFields[n].TransformationFields[t].categoryId
                            );
                        }
                        if (field != null &&
                            rFields.find(rField => rField.ID == field.ID && rField.CategoryID == field.CategoryID) == null) {
                            const category = this.information.categories.find(c => c.ID == field.CategoryID);
                            if (category != null && (field.ObjectName == null || field.ObjectName == undefined)) {
                                const object = this.information.objects.find(o => o.ID == category.ObjectID);
                                if (object != null) {
                                    field.ObjectName = object.ObjectName;
                                }
                            }
                            field.IsDuplicate = field.IsDuplicate == undefined ? false : field.IsDuplicate;
                            rFields.push(field);
                        }
                    }
                }
            }
        }

        // Adding filter fields to the returned fields
        rFields.forEach(rField => {
            rField.Filters = [];
        });
        for (let i = 0; i < fFields.length; i++) {
            const field = rFields.find(rField => rField.ID == fFields[i].ID && rField.CategoryID == fFields[i].CategoryID);
            if (field != null) {
                if (this.loginService.LoginSettings.V10 == false) {
                    fFields[i].Filters.forEach(object => {
                        object.field = null;
                    });
                }
                fFields[i].Filters.forEach(filter => {
                    const matchingFieldFilter = field.Filters.find(fieldFilter =>
                        filter.Value == fieldFilter.Value
                        && filter.Value2 == fieldFilter.Value2
                        && filter.Operator == fieldFilter.Operator
                        && filter.Group == fieldFilter.Group
                        && filter.FilterID == fieldFilter.FilterID
                    );
                    if (!matchingFieldFilter) {
                        field.Filters.push(filter);
                    }
                });
            } else {
                rFields.push(fFields[i]);
            }
        }

        return rFields;
    }

    resetResultProperties() {
        // POST: resets all the properties used for results
        this.results = null;
        this.recordCount = null;
        this.pageCount = null;
        this.queryResultTable = [];
        if (this.currentQuery) {
            this.currentQuery.SQLScript = '';
        }
        this.currentPage = 1;
        this.retrievedData = [];
        this.currentQuery.queryChangedSinceLastResult = true;
    }

    setShowOnReportParameters(query?: DataQuery) {
        // POST: sets up the fields for the reports

        // Defaulting to the current query
        if (!query) {
            query = this.currentQuery;
        }

        // Setting rport parameters
        for (let i = 0; i < query.filterFields.length; i++) {
            query.filterFields[i].ShowOnReport = false;
        }
        for (let i = 0; i < query.reportFields.length; i++) {
            const reportField = query.reportFields[i];
            reportField.ShowOnReport = true;
            if (reportField.FunctionText == null || reportField.FunctionText == '') {
                reportField.TransformationFields = [];
                let viewName = '';
                const category = this.information.categories.find(cat => cat.ID == reportField.CategoryID);
                if (category != null) {
                    viewName = category.ViewName;
                } else {
                    console.warn('No category for reportField.TransformationField sql');
                }
                const textToInsert = this.getTransFunctionText(reportField);
                const sqlToInsert = (reportField.FieldName && reportField.FieldName != '') ? ('vw' + viewName + '.[' + reportField.FieldName + ']') : '';
                reportField.FunctionText = textToInsert;
                const trans = {
                    fieldId: reportField.ID,
                    categoryId: reportField.CategoryID,
                    sql: sqlToInsert,
                    position: 0
                };
                reportField.TransformationFields.push(<DataQueryTransformationField>trans);
                this.transFunctionChanged(reportField);
            }
        }
    }

    reorderResults(indexA, indexB) {
        // PRE: two indexes to switch
        // POST: switches the indexes on all results
        this.results.reorder(indexA, indexB);
    }

    exportResults(callback?: Function) {
        // POST: exports the results to the client based on the export settings on the query

        // Validating our syntax
        let validSyntax = true;
        this.currentQuery.reportFields.forEach(rField => {
            // Remove circular structure to JSON
            if (rField.object != undefined) {
                rField.object = null;
            }
            if (rField.category != undefined) {
                rField.category = null;
            }
            const reportField = cloneDeep(rField);
            this.validateSyntax(reportField, reportField.FunctionText);
            if (reportField.FunctionError && reportField.FunctionError.length > 0) {
                validSyntax = false;
            }
        });

        // If the syntax is valid, exporting results
        if (validSyntax) {
            this.getResultsAsString().pipe(take(1)).subscribe(
                fileData => {

                    // Setting the variables required for the file
                    let fileType: string;
                    let fileName = this.currentQuery.QueryName ? this.currentQuery.QueryName : 'Data Query';

                    // Composing excel file
                    if (this.currentQuery.ExportType == 'Excel') {
                        fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset-utf-8;';
                        fileName += '.xlsx';
                        System.saveFile(fileData, fileType, fileName);
                    }

                    // Composing text file
                    else {
                        fileType = this.currentQuery.Delimiter != 'Comma' ? 'text/plain' : 'application/csv' + ';charset-utf-8;';
                        fileName += this.currentQuery.Delimiter != 'Comma' ? '.txt' : '.csv';
                        System.saveFile(btoa(unescape(encodeURIComponent(fileData))), fileType, fileName);
                    }

                    // Calling the callback
                    if (callback) {
                        callback(true);
                    }
                }
            );
        } else {
            callback(false);
        }
    }

    checkDefaultFilterFields(fields?: DataQueryField[], fieldMutator = (field: DataQueryField) => {
    }) {
        // PRE: the fields in the information is set or fields are provided. The information must also be organized.
        // POST: checks the fields and sets the default filter fields

        // Determining which fields to use
        if (!fields) {
            fields = this.information.fields;
        }

        // V10 uses different fields
        if (this.loginService.LoginSettings.V10) {

            // Helper Function to mutate fields
            const mutateField = (columnName: string, category: DataQueryCategory) => {
                const field = category.fields.find(f =>
                    f.FieldName.toLowerCase() == columnName.toLowerCase()
                );
                if (field) {
                    if (columnName != 'PrimaryJob') {
                        field.IsDefaultFilterField = true;
                    }
                    fieldMutator(field);
                }
            };
            const mutateAllFields = (columnNames: string[], category: DataQueryCategory) => {
                for (let i = 0; i < columnNames.length; i++) {
                    mutateField(columnNames[i], category);
                }
            };

            // Student Search
            if (this.isStudentSearch) {

                // Set demographic fields
                let demographicsCategory = null;
                const studentObject = this.information.objects.find(object => object.ObjectName == 'Student');
                if (studentObject) {
                    demographicsCategory = studentObject.categories.find(category =>
                        category.CategoryName == (this.loginService.LoginSettings.V10 ? 'Student Demographics' : 'Demographics')
                    );
                    if (demographicsCategory) {
                        mutateAllFields(
                            [
                                'STUDENT_NUMBER',
                                'student_id',
                                'FIRST_NAME',
                                'LAST_NAME',
                                'DOB'
                            ],
                            demographicsCategory
                        );
                    }


                    // Set enrollment fields
                    const enrollmentObject = this.information.objects.find(object => object.ObjectName == 'Enrollment');
                    if (enrollmentObject) {
                        const enrollmentCategory = enrollmentObject.categories.find(category => category.CategoryName == 'Student Enrollment' && category.ObjectID == enrollmentObject.ID);
                        if (enrollmentCategory) {
                            mutateAllFields(
                                [
                                    'GRADE_LEVEL',
                                    'SCHOOL_NAME',
                                    'Primary',
                                    'Entry_Date',
                                    'ENROLLMENT_STATUS'
                                ],
                                enrollmentCategory
                            );
                        }
                    }
                }
            }

            // Course Search
            else if (this.isCourseSearch) {

                // Set course search fields
                const districtCourseObject = this.information.objects.find(object => object.ObjectName == 'Course - District');
                let districtCourseCategory = null;
                if (districtCourseObject) {
                    districtCourseCategory = districtCourseObject.categories.find(category => category.CategoryName == 'District Course Master' && category.ObjectID == districtCourseObject.ID);
                }
                const schoolCourseObject = this.information.objects.find(object => object.ObjectName == 'Course - School');
                let schoolCourseCategory = null;
                if (schoolCourseObject) {
                    schoolCourseCategory = schoolCourseObject.categories.find(category => category.CategoryName == 'School Course Master');
                }
                if (districtCourseCategory) {
                    mutateAllFields(
                        [
                            'COURSE_NAME',
                            'COURSE_NUMBER',
                            'DEPARTMENT',
                            'SubjectAreas',
                            'CreditCodes',
                            'COURSE_CREDITS',
                            'Max_Seats',
                            'QUALIFYING_GRADE_LEVELS',
                            'Include_In_GPA',
                            'CourseRequirement'
                        ],
                        districtCourseCategory
                    );
                }
                if (schoolCourseCategory) {
                    mutateAllFields(
                        [
                            'GenderRestriction',
                            'Sped',
                            'GAT',
                            'SchoolName'
                        ],
                        schoolCourseCategory
                    );
                }
            }

            // Staff Search
            else if (this.isStaffSearch) {

                // Staff Fields
                const staffObject = this.information.objects.find(object => object.ObjectName == 'Employee');
                if (staffObject) {

                    // Setting demographic fields
                    const staffDemographicsCategory = staffObject.categories.find(category => category.CategoryName == 'Employee Demographics' && category.ObjectID == staffObject.ID);
                    if (staffDemographicsCategory) {
                        mutateAllFields(
                            [
                                'Staff_ID',
                                'Employee_Number',
                                'Employee_First_Name',
                                'Employee_Middle_Name',
                                'Employee_Last_Name',
                            ],
                            staffDemographicsCategory
                        );
                    }

                    // Setting job fields
                    const staffJobsCategory = staffObject.categories.find(category => category.CategoryName == 'Employee Jobs' && category.ObjectID == staffObject.ID);
                    if (staffJobsCategory) {
                        mutateAllFields(
                            [
                                'School_Name',
                                'JobDesc',
                                'PrimaryJob'
                            ],
                            staffJobsCategory
                        );
                    }
                }

                // Scheduling Specific Fields
                const schedulingObject = this.information.objects.find(object => object.ObjectName == 'Scheduling');
                if (schedulingObject) {

                    // Teacher Info Fields
                    const teacherInformationCategory = schedulingObject.categories.find(category => category.CategoryName == 'Teacher Information' && category.ObjectID == schedulingObject.ID);
                    if (teacherInformationCategory) {
                        mutateAllFields(
                            [
                                'UseForScheduling',
                                'Departments',
                                'Certifications',
                                'DefaultBellSchedule',
                                'PreferredRoom',
                                'PreferredSubjectAreas',
                                'SchedulingGroups',
                                'ScheduledInCourse',
                                'SubjectAreas'
                            ],
                            teacherInformationCategory
                        );
                    }
                }
            }

            // V9 uses different fields
        } else {
            if (this.isStudentSearch) {
                // Both enrollment and demographics come from student object
                const studentObject = this.information.objects.find(object => object.ObjectName == 'Student');
                if (studentObject) {

                    // Setting demographic fields
                    const demographicsCategory = studentObject.categories.find(category =>
                        category.CategoryName == (this.loginService.LoginSettings.V10 ? 'Student Demographics' : 'Demographics')
                    );
                    if (demographicsCategory) {
                        let studentField = fields.find(field => field.DisplayName == 'Student #' && field.CategoryID == demographicsCategory.ID);
                        if (studentField) {
                            studentField.IsDefaultFilterField = true;
                            fieldMutator(studentField);
                        }

                        // studentField = fields.find(field => field.FieldName.toLowerCase() == 'lngstudentid' && field.CategoryID == demographicsCategory.ID);
                        // if (studentField) {
                        //     studentField.IsDefaultFilterField = true;
                        //     fieldMutator(studentField);
                        // }
                        studentField = fields.find(field => field.DisplayName == 'lngStudentId' && field.CategoryID == demographicsCategory.ID);
                        if (studentField) {
                            studentField.IsDefaultFilterField = false;
                            fieldMutator(studentField);
                        }

                        studentField = fields.find(field => field.DisplayName == 'Name - First' && field.CategoryID == demographicsCategory.ID);
                        if (studentField) {
                            studentField.IsDefaultFilterField = true;
                            fieldMutator(studentField);
                        }

                        studentField = fields.find(field => field.DisplayName == 'Name - Last' && field.CategoryID == demographicsCategory.ID);
                        if (studentField) {
                            studentField.IsDefaultFilterField = true;
                            fieldMutator(studentField);
                        }

                        studentField = fields.find(field => field.DisplayName == 'Birth Date' && field.CategoryID == demographicsCategory.ID);
                        if (studentField) {
                            studentField.IsDefaultFilterField = true;
                            fieldMutator(studentField);
                        }
                    }

                    // Setting enrollment fields
                    const enrollmentCategory = studentObject.categories.find(category => category.CategoryName == 'Current Enrollment' && category.ObjectID == studentObject.ID);
                    if (enrollmentCategory) {

                        let enrollmentField = fields.find(field => field.DisplayName == 'Grade' && field.CategoryID == enrollmentCategory.ID);
                        if (enrollmentField) {
                            enrollmentField.IsDefaultFilterField = true;
                            fieldMutator(enrollmentField);
                        }

                        enrollmentField = fields.find(field => field.DisplayName == 'Primary Enrollment Site Name' && field.CategoryID == enrollmentCategory.ID);
                        if (enrollmentField) {
                            enrollmentField.IsDefaultFilterField = true;
                            fieldMutator(enrollmentField);
                        }

                        enrollmentField = fields.find(field => field.DisplayName == 'Enrollment Date' && field.CategoryID == enrollmentCategory.ID);
                        if (enrollmentField) {
                            enrollmentField.IsDefaultFilterField = true;
                            fieldMutator(enrollmentField);
                        }

                        enrollmentField = fields.find(field => field.DisplayName == 'Enrollment Status' && field.CategoryID == enrollmentCategory.ID);
                        if (enrollmentField) {
                            enrollmentField.IsDefaultFilterField = true;
                            fieldMutator(enrollmentField);
                        }

                        enrollmentField = fields.find(field => field.DisplayName == 'Primary' && field.CategoryID == enrollmentCategory.ID);
                        if (enrollmentField) {
                            enrollmentField.IsDefaultFilterField = false;
                            fieldMutator(enrollmentField);
                        }
                    }
                }
            }
            // Staff Search
            else if (this.isStaffSearch) {
                // Staff Fields
                const staffObject = this.information.objects.find(object => object.ObjectName == 'Staff');
                if (staffObject) {

                    // Setting demographic fields
                    const staffDemographicsCategory = staffObject.categories.find(category => category.CategoryName == 'Demographics' && category.ObjectID == staffObject.ID);
                    if (staffDemographicsCategory) {
                        let staffField = fields.find(field => field.DisplayName == 'ID' && field.CategoryID == staffDemographicsCategory.ID);
                        if (staffField) {
                            staffField.IsDefaultFilterField = true;
                            fieldMutator(staffField);
                        }

                        staffField = fields.find(field => field.DisplayName == 'lngFacultyStaffId' && field.CategoryID == staffDemographicsCategory.ID);
                        if (staffField) {
                            staffField.IsDefaultFilterField = false;
                            fieldMutator(staffField);
                        }

                        staffField = fields.find(field => field.DisplayName == 'Name - First' && field.CategoryID == staffDemographicsCategory.ID);
                        if (staffField) {
                            staffField.IsDefaultFilterField = true;
                            fieldMutator(staffField);
                        }

                        staffField = fields.find(field => field.DisplayName == 'Name - Last' && field.CategoryID == staffDemographicsCategory.ID);
                        if (staffField) {
                            staffField.IsDefaultFilterField = true;
                            fieldMutator(staffField);
                        }

                        staffField = fields.find(field => field.DisplayName == 'Name - Middle' && field.CategoryID == staffDemographicsCategory.ID);
                        if (staffField) {
                            staffField.IsDefaultFilterField = true;
                            fieldMutator(staffField);
                        }

                        staffField = fields.find(field => field.DisplayName == 'Roles' && field.CategoryID == staffDemographicsCategory.ID);
                        if (staffField) {
                            staffField.IsDefaultFilterField = true;
                            fieldMutator(staffField);
                        }

                        staffField = fields.find(field => field.DisplayName == 'Pri Assign Site Name' && field.CategoryID == staffDemographicsCategory.ID);
                        if (staffField) {
                            staffField.IsDefaultFilterField = true;
                            fieldMutator(staffField);
                        }

                        staffField = fields.find(field => field.DisplayName == 'Username' && field.CategoryID == staffDemographicsCategory.ID);
                        if (staffField) {
                            staffField.IsDefaultFilterField = true;
                            fieldMutator(staffField);
                        }

                        staffField = fields.find(field => field.DisplayName == 'Perm Group' && field.CategoryID == staffDemographicsCategory.ID);
                        if (staffField) {
                            staffField.IsDefaultFilterField = true;
                            fieldMutator(staffField);
                        }

                        staffField = fields.find(field => field.DisplayName == 'Name' && field.CategoryID == staffDemographicsCategory.ID);
                        if (staffField) {
                            staffField.IsDefaultFilterField = true;
                            fieldMutator(staffField);
                        }

                        staffField = fields.find(field => field.DisplayName == 'State ID' && field.CategoryID == staffDemographicsCategory.ID);
                        if (staffField) {
                            staffField.IsDefaultFilterField = true;
                            fieldMutator(staffField);
                        }
                    }
                }
            }
        }
    }

    addFieldToQuery(field: DataQueryField, type: 'shown' | 'filter'): DataQueryField {
        // POST: adds the field to the query as the specified type

        // Determinine what the field list we're dropping into is
        let fieldList: DataQueryField[] = [];
        switch (type) {
            case 'shown':
                fieldList = this.currentQuery.reportFields;
                break;

            case 'filter':
                fieldList = this.currentQuery.filterFields;
                break;
        }

        // We always duplicate the field before adding it
        field = field.duplicate();

        // Updating the transformation field in case we're dragging from available
        if (!field.FunctionText) {
            this.insertTransField(field, field);
        }

        // If the field exists, add a number to it's alias
        const duplicates = fieldList.filter(f => f.DisplayName == field.DisplayName);
        if (duplicates.length > 0) {
            field.Alias = field.DisplayName + '(' + duplicates.length + ')';
        }
        field.ShowOnReport = true;

        // Adding the field to list of fields
        fieldList.push(field);
        field.SortOrder = fieldList.length - 1;
        this.currentQuery.queryChanged = true;

        // If the list is filter fields, add a filter
        if (type == 'filter') {
            if (!field.Filters) {
                field.Filters = [];
            }
            if (field.Filters.length == 0) {
                const filter = new DataQueryFieldFilter(field);
                filter.FilterID = DataQueryService.filterFieldId;
                DataQueryService.filterFieldId += 1;

                // Obtaining the filter options
                if (this.V10 || (field.FilterType == 3 && field.FieldName != 'Pri Enrl Site Name')) {
                    this.getFilterOptions(filter, field.CategoryID).pipe(take(1)).subscribe(options => {
                        filter.FilterOptions = options;
                        if (filter.FilterOptions.length == 0 && !this.V10) {
                            filter.FilterType = 0;
                        }
                    });
                } else {
                    this.yearService.getStudentSearchSchoolByYearId(this.yearService.defaultYear.Id).pipe(take(1)).subscribe(schools => {
                        filter.FilterOptions = [];
                        const allSchoolsOption = new Lookup();
                        allSchoolsOption.Id = '0';
                        allSchoolsOption.Code = '';
                        allSchoolsOption.CodeDescription = 'All';
                        allSchoolsOption.Sequence = 1;
                        filter.FilterOptions.push(allSchoolsOption);
                        for (let i = 0; i < schools.length; i++) {
                            const schoolOption = new Lookup();
                            schoolOption.Id = schools[i].SchoolId.toString();
                            schoolOption.Code = '';
                            schoolOption.CodeDescription = schools[i].SchoolName;
                            schoolOption.Sequence = i + 2;
                            filter.FilterOptions.push(schoolOption);
                        }
                    });

                    // If the field is a course filter, the filters value is the course name
                    if (field.DisplayName == DataQueryFieldFilter.courseFilterDisplayName) {
                        filter.Value = this.currentQuery.courseName;
                    }
                }
            }
        }

        // If the list is shown fields, remove all filters and add it to the sort fields
        if (type == 'shown') {
            field.Filters = [];
            if (!this.currentQuery.sortFields.includes(field)) {
                this.currentQuery.sortFields.push(field);
            }

            let viewName = '';
            const category = this.information.categories.find(cat => cat.ID == field.CategoryID);
            if (category != null) {
                viewName = category.ViewName;
            } else {
                console.warn('No category for reportField.TransformationField sql');
            }
            // const textToInsert = this.getTransFunctionText(reportField);
            const sqlToInsert = (field.FieldName && field.FieldName != '') ? ('vw' + viewName + '.[' + field.FieldName + ']') : '';

            // Setting the transformation field of the report field
            field.TransformationFields = [
                {
                    fieldId: field.ID,
                    categoryId: field.CategoryID,
                    position: 0,
                    sql: sqlToInsert
                }
            ];
        }

        // Returning the duplicated field we added
        return field;
    }

    //////////// Validation Methods //////////
    validateFilters(query?: DataQuery) {
        // POST: determines if the filter fields are set correctly

        // Defaulting to the current query
        if (!query) {
            query = this.currentQuery;
        }

        // Validating
        let isValid = true;
        for (let i = 0; i < query.filterFields.length; i++) {
            for (let n = 0; n < query.filterFields[i].Filters.length; n++) {
                if (query.filterFields[i].DataType != undefined) {
                    query.filterFields[i].Filters[n].showFilterError1 = false;
                    query.filterFields[i].Filters[n].showFilterError2 = false;
                    if (query.filterFields[i].DataType.toLowerCase() == 'int'
                        || query.filterFields[i].DataType.toLowerCase() == 'decimal'
                        || query.filterFields[i].DataType.toLowerCase() == 'float'
                        || query.filterFields[i].DataType.toLowerCase() == 'money'
                        || query.filterFields[i].DataType.toLowerCase() == 'bigint'
                        || query.filterFields[i].DataType.toLowerCase() == 'smallint'
                        || query.filterFields[i].DataType.toLowerCase() == 'tinyint'
                        || query.filterFields[i].DataType.toLowerCase() == 'numeric'
                        || query.filterFields[i].DataType.toLowerCase() == 'smalldatetime') {

                        if (query.filterFields[i].DataType.toLowerCase() == 'smalldatetime') {
                            if (this.isValidDate(query.filterFields[i].Filters[n].Value) == false
                                && query.filterFields[i].DisplayName != DataQueryFieldFilter.courseFilterDisplayName) {
                                query.filterFields[i].Filters[n].showFilterError1 = true;
                                isValid = false;
                            }
                            if (this.isValidDate(query.filterFields[i].Filters[n].Value2) == false
                                && query.filterFields[i].Filters[n].Operator == 'Between') {
                                query.filterFields[i].Filters[n].showFilterError2 = true;
                                isValid = false;
                            }
                        } else {
                            if (isNaN(<number>query.filterFields[i].Filters[n].Value)
                                && query.filterFields[i].DisplayName != DataQueryFieldFilter.courseFilterDisplayName) {
                                query.filterFields[i].Filters[n].showFilterError1 = true;
                                isValid = false;
                            }

                            if (isNaN(<number>query.filterFields[i].Filters[n].Value2)
                                && query.filterFields[i].Filters[n].Operator == 'Between') {
                                query.filterFields[i].Filters[n].showFilterError2 = true;
                                isValid = false;
                            }
                        }
                    }
                }
            }
        }
        return isValid;
    }

    validateFunctions(query?: DataQuery) {
        // POST: validates the report field's functions

        // Defaulting to the current query
        if (!query) {
            query = this.currentQuery;
        }

        // Validating
        let isValid = true;
        for (let i = 0; i < query.reportFields.length; i++) {
            query.reportFields[i].FunctionError = [];
            if (!query.reportFields[i].FunctionError.find(error => error == 'Column Name is required.')
                && query.reportFields[i].IsCalculated
                && ((query.reportFields[i].Alias != null
                    && query.reportFields[i].Alias.trim() == '')
                    || (query.reportFields[i].DisplayName != null
                        && query.reportFields[i].DisplayName.trim() == '')
                    || ((query.reportFields[i].Alias == undefined || query.reportFields[i].Alias == null)
                        && (query.reportFields[i].DisplayName == undefined
                            || query.reportFields[i].DisplayName == null)))) {
                query.reportFields[i].addFunctionError('Column Name is required.');
            }
            const upperAlias = (query.reportFields[i].Alias == null ? (query.reportFields[i].DisplayName != null ? query.reportFields[i].DisplayName : '') : query.reportFields[i].Alias);
            for (let j = i + 1; j < query.reportFields.length; j++) {
                const innerAlias = (query.reportFields[j].Alias == null ? (query.reportFields[j].DisplayName != null ? query.reportFields[j].DisplayName : '') : query.reportFields[j].Alias);
                if (upperAlias != null && innerAlias != null
                    && upperAlias != '' && innerAlias != ''
                    && upperAlias.toLowerCase() == innerAlias.toLowerCase()
                    && !query.reportFields[i].FunctionError.find(error => error == 'Ambiguous Column Name.')) {
                    query.reportFields[i].addFunctionError('Ambiguous Column Name.');
                }
            }
            if (query.reportFields[i].IsCalculated
                && query.reportFields[i].FunctionText.trim() == ''
                && !query.reportFields[i].FunctionError.find(error => error == 'Value is required.')) {
                query.reportFields[i].addFunctionError('Value is required.');
            }
            if (query.reportFields[i].Alias != null
                && (query.reportFields[i].Alias.trim()[0] == '['
                    && query.reportFields[i].Alias.trim()[query.reportFields[i].Alias.length - 1] == ']')) {
                query.reportFields[i].addFunctionError('Character "[" is not allowed at start and end position of Alias name');
            }
            if (query.reportFields[i].FunctionError == null
                || query.reportFields[i].FunctionError.length == 0) {
                this.validateSyntax(query.reportFields[i], query.reportFields[i].FunctionText);
            }
            if (query.reportFields[i].FunctionError != null
                && query.reportFields[i].FunctionError.length > 0) {
                isValid = false;
            }
        }
        return isValid;
    }

    validateSyntax(field: DataQueryField, fnText: string) {
        // POST: validates the function syntax
        const operatorList = this.operators.map(operator => operator.Display.trim().toLowerCase());
        if (field != null) {
            field.FunctionError = [];
            if (!fnText) {
                field.addFunctionError('Column Name is required.');
            } else {
                const fnParts = this.splitFunctionString(fnText);
                for (let i = 0; i < fnParts.length; i++) {
                    const fnPart = fnParts[i];

                    // If this is an operator and either the next or the previous is also an operator, there is an error
                    if (
                        operatorList.includes(fnPart.trim().toLowerCase())
                        && (
                            i == 0
                            || i == fnParts.length - 1
                            || operatorList.includes(fnParts[i - 1].trim().toLowerCase())
                            || operatorList.includes(fnParts[i + 1].trim().toLowerCase())
                        )
                    ) {
                        field.addFunctionError('Missing conditional operand near ' + fnPart + '.');
                    }

                    // If this isn't an operator and the next or the previous is also not, there is an error
                    else if (
                        !operatorList.includes(fnParts[i].trim().toLowerCase())
                        && (
                            (
                                i < fnParts.length - 1
                                && !operatorList.includes(fnParts[i + 1].trim().toLowerCase())
                            )
                            || (
                                i > 0
                                && !operatorList.includes(fnParts[i - 1].trim().toLowerCase())
                            )
                        )
                    ) {
                        field.addFunctionError('Missing conditional operator near ' + fnPart + '.');
                    }

                    // If the string isn't closed, there is an error
                    if (fnPart.substring(0, 1) == '"') {
                        if (fnPart.substring(fnPart.length - 1) != '"') {
                            field.addFunctionError('Missing Closing ".');
                        }
                    }

                    // If the bracket isn't closed, there is an error
                    else if (fnPart.substring(0, 1) == '[') {
                        if (fnPart.substring(fnPart.length - 1) != ']') {
                            field.addFunctionError('Missing Closing ].');
                        }
                    }

                    // Evaluating parentheses
                    else if (fnPart.includes('(')) {
                        const fnName = fnPart.substring(0, fnPart.indexOf('(')).trim();
                        if (fnPart.substring(fnPart.length - 1) != ')') {
                            field.addFunctionError('Missing closing ) on ' + fnName + '.');
                        } else {
                            const paramString = fnPart.substring(fnPart.indexOf('(') + 1, fnPart.lastIndexOf(')')).trim();
                            const parameters: string[] = [];
                            let currentParam = '';
                            let parenCount = 0;
                            let isStringLiteral = false;
                            const fn = this.functions.find(f => f.Name.toLowerCase() == fnName.toLowerCase());
                            if (fn == null) {
                                field.addFunctionError(fnName + ' is not a function.');
                            } else {
                                for (let c = 0; c < paramString.length; c++) {
                                    currentParam += paramString[c];
                                    if (paramString[c] == '"' && parenCount == 0) {
                                        isStringLiteral = !isStringLiteral;
                                    }
                                    if (isStringLiteral == false) {
                                        if (paramString[c] == '(') {
                                            parenCount++;
                                        } else if (parenCount > 0) {
                                            if (paramString[c] == ')') {
                                                parenCount--;
                                            }
                                        } else if (paramString[c] == ',') {
                                            if (currentParam.indexOf('[') > -1
                                                && currentParam.indexOf(']') < 0) {
                                                // do nothing
                                            } else {
                                                if (currentParam.substring(0, currentParam.length - 1).trim() != '') {
                                                    parameters.push(currentParam.substring(0, currentParam.length - 1).trim());
                                                }
                                                currentParam = '';
                                            }
                                        }
                                        if (c + 1 == paramString.length) {
                                            if (currentParam.trim() != '') {
                                                parameters.push(currentParam.trim());
                                            }
                                            currentParam = '';
                                        }
                                    } else {
                                        if (c + 1 == paramString.length) {
                                            if (currentParam.trim() != '') {
                                                parameters.push(currentParam.trim());
                                            }
                                            currentParam = '';
                                        }
                                    }
                                }
                                // check number of params
                                const dqParams = this.parameters.filter(p => p.FunctionId == fn.Id);
                                if (dqParams.length != parameters.length) {
                                    let er = '';
                                    er = 'Incorrect syntax for ' + fnName + '. Format expected is ' + fnName + '(';
                                    for (let n = 0; n < dqParams.length; n++) {
                                        const param = dqParams.find(p => p.Position == n + 1);
                                        if (param != null) {
                                            if (n > 0) {
                                                er += ', ';
                                            }
                                            er += param.Name;
                                        }
                                    }
                                    er += ').';
                                    field.addFunctionError(er);
                                } else {
                                    if (fnName.toLowerCase() == 'formatphone') {
                                        const formatParam = this.parameters.find(p => p.FunctionId == fn.Id && p.Name == 'format');
                                        if (formatParam != null) {
                                            const format = parameters[formatParam.Position - 1];
                                            if (format.split('x').length - 1 != 10) {
                                                field.addFunctionError('Phone number format requires 10 digits (indicated by "x").');
                                            }
                                        }
                                    }
                                    // ONE-7965
                                    if (fnName.toLowerCase() == 'date' && field.DataType != null && field.DataType.toLowerCase() != 'smalldatetime' && field.DataType.toLowerCase() != 'datetime') {
                                        field.addFunctionError('Date formula requires a date field.');
                                    }
                                    // check param data type
                                    for (let x = 0; x < parameters.length; x++) {
                                        const param = dqParams.find(p => p.Position == x + 1);
                                        const paramParts = this.splitFunctionString(parameters[x]);
                                        if (param != null && fnName.toLowerCase() != 'if') {
                                            for (let y = 0; y < paramParts.length; y++) {
                                                if (param.DataType == 'varchar' || param.DataType == 'char') {
                                                    if (paramParts[y][0] != '"' && paramParts[y] != '+' && paramParts[y][0] != '[') {
                                                        if (paramParts[y].indexOf('(') < 0
                                                            || !this.functions.find(f => f.Name.toLowerCase() == paramParts[y].substring(0, paramParts[y].indexOf('(')).toLowerCase())) {
                                                            field.addFunctionError('Incorrect datatype for parameter ' + (x + 1) + ' in ' + fnName + '.');
                                                        } else {
                                                            const funct = this.functions.find(f => f.Name.toLowerCase() == paramParts[y].substring(0, paramParts[y].indexOf('(')).toLowerCase());
                                                            if (funct.ReturnType != 'varchar' && funct.ReturnType != 'char') {
                                                                field.addFunctionError('Incorrect datatype for parameter ' + (x + 1) + ' in ' + fnName + '.');
                                                            }
                                                        }
                                                    }
                                                } else if (param.DataType == 'int') {
                                                    if (paramParts[y][0] != '[' && paramParts[y] != '+' && paramParts[y] != '-' && paramParts[y] != '*' && paramParts[y] != '^' && !System.isNumeric(paramParts[y][0])) {
                                                        if (paramParts[y].indexOf('(') < 0
                                                            || !this.functions.find(f => f.Name.toLowerCase() == paramParts[y].substring(0, paramParts[y].indexOf('(')).toLowerCase())) {
                                                            field.addFunctionError('Incorrect datatype for parameter ' + (x + 1) + ' in ' + fnName + '.');
                                                        } else {
                                                            const funct = this.functions.find(f => f.Name.toLowerCase() == paramParts[y].substring(0, paramParts[y].indexOf('(')).toLowerCase());
                                                            if (funct.ReturnType != 'integer' && funct.ReturnType != 'int') {
                                                                field.addFunctionError('Incorrect datatype for parameter ' + (x + 1) + ' in ' + fnName + '.');
                                                            }
                                                        }
                                                    }
                                                } else if (param.DataType == 'number') {
                                                    if (paramParts[y][0] != '[' && paramParts[y] != '+' && paramParts[y] != '-' && paramParts[y] != '*' && paramParts[y] != '^' && !System.isNumeric(paramParts[y][0])) {
                                                        if (paramParts[y].indexOf('(') < 0
                                                            || !this.functions.find(f => f.Name.toLowerCase() == paramParts[y].substring(0, paramParts[y].indexOf('(')).toLowerCase())) {
                                                            field.addFunctionError('Incorrect datatype for parameter ' + (x + 1) + ' in ' + fnName + '.');
                                                        } else {
                                                            const funct = this.functions.find(f => f.Name.toLowerCase() == paramParts[y].substring(0, paramParts[y].indexOf('(')).toLowerCase());
                                                            if (funct.ReturnType != 'integer' && funct.ReturnType != 'int' && funct.ReturnType != 'number') {
                                                                field.addFunctionError('Incorrect datatype for parameter ' + (x + 1) + ' in ' + fnName + '.');
                                                            }
                                                        }
                                                    }
                                                } else if (param.DataType == 'datetime') {
                                                    if (paramParts[y][0] != '"' && paramParts[y][0] != '[') {
                                                        if (paramParts[y].indexOf('(') < 0
                                                            || !this.functions.find(f => f.Name.toLowerCase() == paramParts[y].substring(0, paramParts[y].indexOf('(')).toLowerCase())) {
                                                            field.addFunctionError('Incorrect datatype for parameter ' + (x + 1) + ' in ' + fnName + '.');
                                                        } else {
                                                            const funct = this.functions.find(f => f.Name.toLowerCase() == paramParts[y].substring(0, paramParts[y].indexOf('(')).toLowerCase());
                                                            if (funct.ReturnType != 'datetime' && funct.ReturnType != 'date') {
                                                                field.addFunctionError('Incorrect datatype for parameter ' + (x + 1) + ' in ' + fnName + '.');
                                                            }
                                                        }
                                                    }
                                                } else if (param.DataType == 'field') {
                                                    if (paramParts[y][0] != '[') {
                                                        if (this.loginService.LoginSettings.V10 == true) {
                                                            field.addFunctionError('Incorrect datatype for parameter ' + (x + 1) + ' in ' + fnName + '.');
                                                        } else {
                                                            if (paramParts[y].indexOf('(') < 0
                                                                || !this.functions.find(f => f.Name.toLowerCase() == paramParts[y].substring(0, paramParts[y].indexOf('(')).toLowerCase())) {
                                                                field.addFunctionError('Incorrect datatype for parameter ' + (x + 1) + ' in ' + fnName + '.');
                                                            } else {
                                                                const funct = this.functions.find(f => f.Name.toLowerCase() == paramParts[y].substring(0, paramParts[y].indexOf('(')).toLowerCase());
                                                                if (funct.ReturnType != 'varchar' && funct.ReturnType != 'varchar') {
                                                                    field.addFunctionError('Incorrect datatype for parameter ' + (x + 1) + ' in ' + fnName + '.');
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                        if (!field.FunctionError || field.FunctionError.length == 0) {
                                            this.validateSyntax(field, parameters[x]);
                                        }
                                    }
                                }
                            }
                        }
                    } else if (fnPart.substring(0, 1) != '['
                        && !operatorList.map(operator => operator.toLowerCase()).includes(fnPart.toLowerCase())
                        && isNaN(fnPart)) {
                        field.addFunctionError(fnPart + ' is not a function.');
                    }
                }
            }
        }
    }

    testFormulas(isExport, _callback, query?: DataQuery) {
        // POST: tests the formulas for the report fields in the current query

        // Initializing
        let formulasValid = true;

        // Defaulting to the current query
        if (!query) {
            query = this.currentQuery;
        }

        // Keeping track of our check relationships
        let calledCallback = false;
        const checkFields = [];
        const checkFieldsXMLFields = [];

        // Validating each report field
        query.reportFields.forEach((field, i) => {
            if (field.FunctionError == null || field.FunctionError.length == 0) {
                if (!field.TransformationFields || field.TransformationFields.length == 0) {
                    this.transFunctionChanged(field);
                }
                const xmlFields = [];
                if (field.FunctionText != null
                    && field.FunctionText != ''
                    && (
                        this.loginService.LoginSettings.V10
                        || field.FunctionText != '[' + field.ObjectName + '.' + field.CategoryName + '.' + field.DisplayName + ']'
                    )
                ) {
                    if (field.Filters == null) {
                        field.Filters = [];
                    }
                    if (field.TransformationFields == null) {
                        field.TransformationFields = [];
                    }
                    xmlFields.push(field);
                    for (let x = 0; x < field.TransformationFields.length; x++) {
                        if (!xmlFields.find(xmlField => xmlField.ID == field.TransformationFields[x].fieldId)) {
                            const tField = this.information.getField(f => f.ID == field.TransformationFields[x].fieldId && f.CategoryID == field.TransformationFields[x].categoryId);
                            // if (!tField.TransformationFields || tField.TransformationFields.length == 0) {
                            // 	this.transFunctionChanged(tField);
                            // }
                            if (tField != null) {
                                // tField.FunctionText = '[' + tField.ObjectName + '.' + tField.CategoryName + '.' + tField.FieldName + ']';
                                // tField.TransformationFunction = 'vw' + tField.category.ViewName + '.[' + tField.FieldName + ']';
                                // tField.TransformationFields = [
                                //     {
                                //         fieldId: tField.ID,
                                //         categoryId: tField.CategoryID,
                                //         position: 0,
                                //         sql: ''
                                //     }
                                // ];
                                xmlFields.push(tField);
                            }
                        }
                    }
                }

                // If there is XML for this, we need to validate it
                if (xmlFields.length > 0) {
                    checkFields.push(field);
                    checkFieldsXMLFields.push(xmlFields);
                }
            }
        });

        // Checking observables
        if (checkFields.length > 0) {
            let finishedChecks = 0;
            for (let i = 0; i < checkFields.length; i++) {
                this.checkRelationships(checkFieldsXMLFields[i], () => {
                    if (checkFields[i].FunctionError == null) {
                        checkFields[i].FunctionError = [];
                    }
                    const xml = this.getQueryXML(checkFieldsXMLFields[i]);
                    this.currentResultSubscriptions.push(
                        this.validateTransformationFunctions(xml, i).subscribe(
                            result => {
                                if (result != null && result != '') {
                                    checkFields[i].addFunctionError(result);
                                    formulasValid = false;
                                }
                                finishedChecks += 1;
                                if (
                                    finishedChecks == checkFields.length
                                    && formulasValid
                                    && _callback != null
                                    && !calledCallback
                                ) {
                                    calledCallback = true;
                                    _callback();
                                }
                                if (
                                    finishedChecks == checkFields.length
                                    && !formulasValid
                                ) {
                                    this.dialog.open(ErrorsExistDialogComponent, {
                                        disableClose: true,
                                        data: {
                                            message: (this.currentTab == 'Settings for Export' ?
                                                'Errors on this tab must be corrected before Results can be exported.' :
                                                'Errors on the Settings for Export tab must be corrected before Results can be viewed.')
                                        }
                                    });
                                    this.currentTab = 'Settings for Export';
                                }
                            }
                        )
                    );
                });
            }
        } else if (_callback != null && !calledCallback) {
            calledCallback = true;
            _callback();
        }
    }

    checkRelationships(fields: DataQueryField[], _callBack?, _errorCallback?) {
        let joinsExistForAllCategories = true;
        const unjoinedCategoryGroups: DataQueryCategory[][] = [];

        // Obtaining distinct category groups of fields
        const shownFields = fields.filter(f => !f.IsConstant && !f.IsCalculated);
        const distinctCategoryGroup: { [key: number]: DataQueryField[] } = {};
        shownFields.forEach(field => {
            if (field.CategoryID > 0) {
                if (!distinctCategoryGroup[field.CategoryID]) {
                    distinctCategoryGroup[field.CategoryID] = [];
                }
                distinctCategoryGroup[field.CategoryID].push(field);
            }
        });
        if (Object.keys(distinctCategoryGroup).length > 1) {
            this.currentResultSubscriptions.push(
                this.getRelationships(fields).pipe(take(1)).subscribe(
                    relationships => {
                        const categoryRelationshipGroups = [];
                        const unconfirmedCategoryRelationships = [];
                        let confirmedCategoryRelationships = [];
                        for (const category in distinctCategoryGroup) {
                            if (distinctCategoryGroup.hasOwnProperty(category)) {
                                unconfirmedCategoryRelationships.push(category);
                            }
                        }

                        while (unconfirmedCategoryRelationships.length > 0) {
                            confirmedCategoryRelationships.push(unconfirmedCategoryRelationships[0]);
                            unconfirmedCategoryRelationships.splice(0, 1);
                            for (let i = 0; i < confirmedCategoryRelationships.length; i++) {
                                let u = unconfirmedCategoryRelationships.length;
                                while (u--) {
                                    let relations = relationships.filter(rel =>
                                        rel.Category1ID == parseInt(unconfirmedCategoryRelationships[u], 10) &&
                                        rel.Category2ID == parseInt(confirmedCategoryRelationships[i], 10)
                                    );
                                    if (relations.length == 0) {
                                        relations = relationships.filter(rel =>
                                            rel.Category1ID == parseInt(confirmedCategoryRelationships[i], 10) &&
                                            rel.Category2ID == parseInt(unconfirmedCategoryRelationships[u], 10)
                                        );
                                    }
                                    if (relations.length > 0) {
                                        confirmedCategoryRelationships.push(unconfirmedCategoryRelationships[u]);
                                        unconfirmedCategoryRelationships.splice(u, 1);
                                    }
                                }
                            }
                            if (unconfirmedCategoryRelationships.length > 0) {
                                joinsExistForAllCategories = false;
                            }
                            categoryRelationshipGroups.push(confirmedCategoryRelationships);
                            confirmedCategoryRelationships = [];
                        }

                        if (categoryRelationshipGroups.length > 0) {
                            for (let g = 0; g < categoryRelationshipGroups.length; g++) {
                                const catGroup = [];
                                for (let c = 0; c < categoryRelationshipGroups[g].length; c++) {
                                    const category = this.information.categories.find(cat =>
                                        cat.ID == parseInt(categoryRelationshipGroups[g][c], 10)
                                    );
                                    if (category != null) {
                                        const catObject = this.information.objects.find(o =>
                                            o.ID == category.ObjectID
                                        );
                                        if (catObject != null) {
                                            category.ObjectName = catObject.ObjectName;
                                        }
                                        catGroup.push(category);
                                        break;
                                    }
                                }
                                unjoinedCategoryGroups.push(catGroup);
                            }
                        }
                    },
                    () => { },
                    () => {
                        if (joinsExistForAllCategories) {
                            if (_callBack != null) {
                                _callBack();
                            }
                        } else {
                            const dialogBox = this.dialog.open(NoRelationshipsWarningDialogComponent, {
                                disableClose: true,
                                data: {
                                    unjoinedCategoryGroups: unjoinedCategoryGroups
                                }
                            });
                            const dialogBoxSubscription = dialogBox.afterClosed().subscribe(() => {
                                dialogBoxSubscription.unsubscribe();
                                if (_errorCallback) {
                                    _errorCallback();
                                }
                            });
                        }
                    }
                )
            );
        } else {
            if (_callBack != null) {
                _callBack();
            }
        }
    }

    validateQuery(success: Function, failure?: Function, forImport = false, query?: DataQuery) {
        // PRE: the current query is set
        // POST: validates the query

        // Defaults to the current query
        if (!query) {
            query = this.currentQuery;
        }

        // Removing subscriptions that we've closed
        this.currentResultSubscriptions = this.currentResultSubscriptions.filter(subscription => !subscription.closed);

        // Running validators
        if (this.validateFilters(query)) {
            if (this.validateFunctions(query)) {
                this.testFormulas(false,
                    () => {
                        this.checkRelationships(
                            this.getAllFields(query),
                            () => {
                                success();
                            },
                            () => {
                                setTimeout(() => {
                                    this.currentTab = this._previousTab;
                                    if (failure) {
                                        failure('Relationships');
                                    }
                                }, 10);
                            }
                        );
                    },
                    query
                );
            } else {
                if (forImport) {
                    this.dialog.open(ImportErrorDialogComponent, {
                        disableClose: true,
                        data: {
                            fields: query.reportFields.filter(field => field.FunctionError.length != 0)
                        }
                    });
                } else {
                    if (!this.dialog.openDialogs.find(dialog => dialog.componentInstance instanceof ErrorsExistDialogComponent)) {
                        this.dialog.open(ErrorsExistDialogComponent, {
                            disableClose: true,
                            data: {
                                message: (this.currentTab == 'Settings for Export' ?
                                    'Errors on this tab must be corrected before Results can be exported.' :
                                    'Errors on the Settings for Export tab must be corrected before Results can be viewed.')
                            }
                        });
                    }
                }
                this.currentTab = 'Settings for Export';
                if (failure) {
                    failure('Settings for Export');
                }
            }
        } else {
            this.currentTab = 'Filters';
            if (failure) {
                failure('Filters');
            }
        }
    }
}
